import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { infoIcon } from '../shared/components/InfoIcon';   // R110 #2: ⓘ on Who pays / Billed yet?
import { woEditFormHtml, collectWoEdit, bindWoEditForm } from '../shared/components/WoEditor';   // R115 maint: the shared WO edit fieldsets + diff
import { enqueuePhotos, pendingItems } from '../lib/photoUploader';   // R116 #5: every WO photo / clip rides the persisted background queue
import { mediaStrip, bindMediaStrips, pendingTilesHtml, patchPendingProgress, mergeLandedMedia, isVideoUrl, EVIDENCE_ACCEPT, type MediaItem } from '../shared/lib/media';
import { evidencePickerHtml, bindEvidencePicker, type EvidencePickerCfg } from '../shared/components/EvidencePicker';
import { openExternal, openApiDoc, fieldViewSwitcherHtml, bindFieldViewSwitcher } from '../shared/lib/nav';
// R109 #d: the work-order smart search — pure ESM in lib/ so `node lib/woSearch.test.mjs` tests the same code
import { parseQuery, buildDoc, searchDocs, highlight as hlText, snippet as hlSnippet, HIDDEN_FIELDS, FIELD_LABEL } from '../../../lib/woSearch.mjs';
import { resolveRosterId, isActiveRow } from '../../../lib/rosterIdentity.mjs';   // R120: the login → roster row rule (C8)
// R116 #2: vendor GROUP texts — the shared Text-vendor form + the vendor-reply renderer (sender names, photo thumbnails)
import { openTextVendorModal } from '../shared/components/TextVendorSheet';
import { vendorRepliesHtml, vendorTextedLabel } from '../shared/lib/vendorReplies';

// Maintenance — preventive maintenance + work orders, one surface (API: /api/assetops).
//
// Three tabs:
//   BOARD          one row per active listing — last PM (date + 'Nd ago', red when
//                  stale vs the PM template cadence), open WOs, in-progress WOs.
//   WORK ORDERS    the WO list (filter by status / listing); each row expands to its
//                  checklist (toggle done, note, attach photo), assignee, status, summary.
//   PM TEMPLATES   edit a template's items + cadence_days; create / delete templates.
//
// PRINCIPLES.md: every count states its basis inline (window, denominator, threshold),
// and a gap that can't be computed honestly says why instead of showing a fake zero.
//
// Owner-facing: a completed WO's summary + photos IS the "maintenance done" record;
// 'Copy owner summary' assembles a plain-text recap of completed WOs for a listing (v1).

const API = '/api/assetops';
const WO_STATUSES: Array<['open' | 'in_progress' | 'done', string]> = [
  ['open', 'Open'],
  ['in_progress', 'In progress'],
  ['done', 'Done'],
];
const WO_PRIORITIES: Array<['emergency' | 'urgent' | 'routine' | 'pm', string]> = [
  ['emergency', 'Emergency'],
  ['urgent', 'Urgent'],
  ['routine', 'Routine'],
  ['pm', 'PM'],
];
const ASSET_CATEGORIES = ['Appliance', 'HVAC', 'Plumbing', 'Pool/Spa', 'Electrical', 'Other'];

const PM_TRIGGER_KINDS: Array<['interval' | 'usage' | 'season', string]> = [
  ['interval', 'Interval (calendar)'],
  ['usage', 'Usage (booked nights)'],
  ['season', 'Seasonal (once / year)'],
];
const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const WO_PAYERS: Array<['wch' | 'owner', string]> = [
  ['wch', 'WCH'],
  ['owner', 'Owner'],
];
// R110 #2: "Payer" and "Billed" read as one idea to the office (Jesse's screenshot of
// the Money & billing fieldset) — they are two different questions. Payer = whose
// money covers the cost (drives the Costs split, owner queue, owner portal, care
// report). Billed = Michelle's tracker of whether the owner has been charged yet, and
// how — it drives no calculation anywhere. "OS" in the stored value = the owner's
// monthly statement (generated externally in VR Platform). Stored values untouched:
// payer 'wch'|'owner'; billing_status ''|'billed_owner'|'billed_os'. One wording
// here feeds the editor, the card chip, the board filter and the Costs tab.
const WO_BILLED_LABEL: Record<string, string> = { billed_owner: 'billed · invoiced', billed_os: 'billed · on statement' };
const WO_BILLED_TITLE: Record<string, string> = {
  billed_owner: 'Owner has been charged — invoiced directly',
  billed_os: 'Owner has been charged — added to their monthly owner statement',
};
// (the Who pays / Billed yet? explainer + options now come from lib/woFormat via the shared WoEditor — R115)
// Which field discipline owns the work — lets a cleaning/inspection job be
// routed off the maintenance queue (Jesse R2 #8). Old WOs default to maintenance.
const WO_ROLES: Array<['maintenance' | 'cleaner' | 'inspector', string]> = [
  ['maintenance', 'Maintenance'],
  ['cleaner', 'Cleaner'],
  ['inspector', 'Inspector'],
];
const WO_ROLE_LABEL: Record<string, string> = { maintenance: 'Maintenance', cleaner: 'Cleaner', inspector: 'Inspector' };

const INC_SEVERITIES: Array<['emergency' | 'urgent' | 'minor', string]> = [
  ['emergency', 'Emergency'],
  ['urgent', 'Urgent'],
  ['minor', 'Minor'],
];
const INC_CATEGORIES: Array<['hvac' | 'plumbing' | 'electrical' | 'lockout' | 'appliance' | 'safety' | 'wifi' | 'damage' | 'other', string]> = [
  ['hvac', 'HVAC'],
  ['plumbing', 'Plumbing'],
  ['electrical', 'Electrical'],
  ['lockout', 'Lockout'],
  ['appliance', 'Appliance'],
  ['safety', 'Safety'],
  ['wifi', 'Wi-Fi'],
  ['damage', 'Damage'],   // R109 #g: a server category the card showed as raw "damage"
  ['other', 'Other'],
];
// R112 board focus filter — the KPI cards and the card-head chips share it.
type BoardFocus = '' | 'open' | 'emg' | 'unsched' | 'unassigned' | 'owner' | 'pm';
// Mirror of the server's severity→WO-priority map (for the dispatch button label).
function severityToPriorityLabel(severity: string): string {
  return ({ emergency: 'emergency', urgent: 'urgent', minor: 'routine' } as Record<string, string>)[severity] || 'routine';
}

export class MaintenancePage {
  private container: HTMLElement;
  private tab: 'board' | 'calendar' | 'work_orders' | 'assets' | 'pm_plans' | 'templates' | 'vendors' | 'costs' | 'incidents' | 'performance' | 'prop_review' | 'recurring' | 'project' = 'board';
  // R90 #2 (Jesse: "pull all the maintenance expenses per property … for our
  // ground team to review, add notes"): per-property expense review state.
  private prLid = '';
  private prData: any = null;            // /work-orders/by-listing payload ({error:true} = failed)
  private prListings: any[] | null = null;
  private prBusy = '';
  private prDrafts = new Map<string, string>();   // review #26: draw() rebuilds the tab — typed ground notes live here, not in the DOM
  private techStats: any = null;            // GET /maintenance/tech-stats (lazy)
  private woAssigneeFilter: string = '';    // '' = all (My Queue)
  private woUrgentOnly = false;             // urgent/today quick filter
  private woReviewOnly = false;             // R101 #5: vendor quotes / invoices awaiting the office

  // data
  private board: any = null;                 // { active_count, rows:[] }
  private workOrders: any[] | null = null;   // [] from /work-orders
  private templates: any[] | null = null;    // [] from /pm-templates
  private assets: any[] | null = null;       // [] from /assets (all non-archived)
  private calendar: any = null;              // { as_of, days, listings:[] } from /maintenance/calendar
  private pmPlans: any[] | null = null;      // [] from /pm-plans
  private pmDue: any = null;                 // { due:[], as_of, days } from /pm/due
  private vendors: any[] | null = null;      // [] from /vendors (with derived jobs/spend)
  private cleaners: any[] | null = null;     // [] from /cleaners (hourly_rate → WO labor cost)
  private people: any[] = [];                // R53: team roster for the assignee picker (was free-text only)
  private costs: any = null;                 // rollup from /maintenance/costs
  private assetIntel: any[] | null = null;   // [] from /maintenance/asset-intel

  // calendar tab state
  private calDays = 30;                      // horizon toggle (7/30/60)
  // R102 (Maintenance Tab Rework): board-as-project, real calendar, recurring, merged costs.
  private recSub: 'plans' | 'templates' = 'plans';      // #8: Recurring maintenance = PM plans + templates
  private costSub: 'costs' | 'performance' = 'costs';   // #7: Costs & performance in one tab
  private projectLid = '';                               // #4: the property whose maintenance project is open
  private calView: 'month' | 'windows' = 'month';        // #2: month grid (default) vs the vacancy-window list
  private calMonth = '';                                 // YYYY-MM ('' = this month)
  private calLid = '';                                   // '' = whole portfolio
  private calGrid: any = null;                           // GET /maintenance/calendar-grid
  private calGridError = false;                          // last grid fetch failed → explicit 'unavailable' state, not endless Loading
  private calGridLoading = false;
  private calSearch = '';
  private calPick: string | null = null;                 // #3: WO id being placed on a day
  private calPickWho: any = null;                        // vendor / tech typed in the rail before the day was clicked
  private calGridSeq = 0;                                // only the latest grid request may write state
  private calDaySel = '';                                // day opened in the rail
  private dayPlanStaff: number | null = null;            // #9: people available that day (null = unconstrained)
  private incListingFilter = '';                         // #1: board → this property's emergencies
  private boardQ = '';
  // R112 (Jesse: "Let's also redesign the maintenance board!"): ONE focus
  // filter shared by the KPI cards and the card-head chips — '' = every
  // property. 'open' is the old "only with open work" toggle (same attribute).
  private boardFocus: BoardFocus = '';
  private boardQueueOpen = false;                        // #1a: owner-portal publishing queue
  private expPopLid = '';                                // board row with the expenses date-range popover open
  // R103 (Loom "Streamlining Work Orders In the Board"): stay in the board / project —
  // emergencies expand under the board row, the project opens work orders inline,
  // and the project's calendar is a panel, not another tab.
  private boardEmgOpen = new Set<string>();              // listing ids whose incidents are expanded on the board
  private projectCal = false;                            // month grid shown inside the project
  // pm plans tab state
  private pmListingFilter = '';              // '' = all
  private showAddPm = false;                 // inline add-plan form toggle
  private editingPm: string | null = null;   // plan id being edited inline, else null
  private autogenFlags: any = null;          // GET /autogen-flags (lazy, pm_plans tab)

  // filters / ui state
  private woStatusFilter: string = '';       // '' = all
  private woListingFilter: string = '';      // '' = all
  private woBilledFilter: string = '';       // R70 (Michelle): '' | 'unbilled' | 'billed' · R110 #2: + 'billed_owner' | 'billed_os'
  private woRoleFilter: string = '';         // '' = all (maintenance/cleaner/inspector)
  // R50 — WO list was storage-order (oldest first); default newest-first + user controls.
  private woSortDir: 'newest' | 'oldest' = 'newest';
  private woGroupByProperty = false;         // group WO cards under property headers
  private woDateTimer: number | null = null;   // R50: debounce for the date-range inputs
  private woDateFrom: string = '';           // lower bound (YYYY-MM-DD), '' = none — on the date woDateMode names
  private woDateTo: string = '';             // upper bound (YYYY-MM-DD), '' = none
  // R109 #c (Jesse: "Also be able to filter by date of completion on previous"):
  // the From/To range reads the CREATED day or the COMPLETED day.
  private woDateMode: 'created' | 'completed' = 'created';
  // R109 #d (Jesse: "Add a smart search feature on the WOs, so you type in things
  // and the system tries to find the WO"): client-side over the loaded WOs.
  private woQ = '';
  private woQTimer: number | null = null;
  private woQShown = 100;                    // render cap while searching ("Show more")
  private woDocCache = new WeakMap<any, { sig: string; doc: any }>();   // WOs mutate in place — the sig catches it
  // R109 #c: the project's "✅ Done" section — 30 / 90 / 365 days (0 = all) or a from–to range
  private projDoneDays = 30;
  private projDoneFrom = '';
  private projDoneTo = '';
  private projDoneTimer: number | null = null;
  private fieldSeeded = false;               // R5 #2: one-time field-role scoping done
  private expandedWo: Set<string> = new Set();
  private editingWoTitle: string | null = null; // wo id whose title is being renamed inline, else null
  private editingWoDesc: string | null = null; // wo id whose instructions are being edited inline, else null
  private expandedTpl: Set<string> = new Set();
  // Field-access panel cache (per listing) for expanded emergency/urgent/incident
  // WOs — fetched once per listing from GET /maintenance/field/:id, never refetched.
  private fieldByListing: Record<string, { listing_name: string; access: any; occupied: boolean; guest_present_note: string }> = {};
  private cadenceDefault = 90;               // fallback cadence for staleness when a WO has no template match

  // assets tab state
  private assetListingFilter: string = '';   // '' = no property selected yet
  private expandedAsset: Set<string> = new Set();
  private assetHistory: Record<string, { history: any[]; open_count: number }> = {};
  private showAddAsset = false;              // inline add-asset form toggle
  private editingAsset: string | null = null; // asset id being edited inline, else null

  // vendors tab state
  private vendorTradeFilter: string = '';    // '' = all trades
  private vendorOnCallOnly = false;          // on-call filter toggle
  private expandedVendor: Set<string> = new Set();
  private vendorWos: Record<string, any[]> = {}; // vendor id -> its work orders (lazy)
  private showAddVendor = false;             // inline add-vendor form toggle
  private editingVendor: string | null = null; // vendor id being edited inline, else null

  // costs tab state
  private costDays = 365;                     // rollup window toggle (90/365)
  private costListingFilter: string = '';     // '' = all properties

  // incidents tab state (the in-stay emergency war room)
  private incidents: any[] | null = null;     // [] from /incidents (emergency-first)
  private incSeverityFilter: string = '';     // '' = all severities
  private incStatusFilter: string = '';       // '' = all statuses
  private expandedInc: string | null = null;  // incident id open in command view, else null
  private incDetail: Record<string, { incident: any; on_call_vendors: any[] }> = {}; // lazy GET /incidents/:id
  private showLogIncident = false;            // log-incident form toggle
  // AI-unblock: true while POST /incidents is in flight — the Log button stays
  // disabled across redraws so a re-tap can't log the same emergency twice.
  private incCreating = false;
  // AI-unblock (review #18): one id per opened log form, sent as
  // client_request_id. When the save + Slack page landed but the answer was
  // lost (spotty signal), the re-tap carries the same id and the server hands
  // back the first incident — no duplicate, no second EMERGENCY page.
  private incRequestId = '';
  private incTriageWatch = new Set<string>();  // incidents whose background AI triage we're waiting on

  constructor(container: HTMLElement) {
    this.container = container;
    this.listenWoMedia();   // R116 #5: a WO photo / clip that lands in the background patches its card in place
    // R112: the shared WO quick view dispatches this when the user is ALREADY on Maintenance
    // (App.navigate no-ops on the same page, so the sessionStorage deep link alone would not fire)
    document.addEventListener('mn:focus-wo', (e) => { const id = String((e as CustomEvent).detail || ''); if (id) { this.drillWo(id); this.openPendingTextVendor(); } });
  }
  /** R115 review: the Calendar's "Text a vendor" parks the WO id in 'mn_text_wo' — open the SAME Text-vendor modal
   *  once that WO is loaded (one-shot: the key is consumed when the modal opens). */
  private openPendingTextVendor(): void {
    let id = '';
    try { id = sessionStorage.getItem('mn_text_wo') || ''; } catch { return; }
    if (!id) return;
    const w = (this.workOrders || []).find((x: any) => x && x.id === id);
    if (!w) return;   // not loaded yet — the load path calls this again
    try { sessionStorage.removeItem('mn_text_wo'); } catch { /* private mode */ }
    setTimeout(() => { void this.showTextVendorModal(w); }, 0);
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">
        <div id="mn-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    await this.loadAll();
    this.draw();
    // R120 fix round 2: an unsettled My Work identity resolves again on each
    // render (draw() seeds it on the first; runRosterIdentity is a no-op while
    // one is in flight or once settled).
    if (this.fieldSeeded && MaintenancePage.fieldRole() === 'maintenance') void this.runRosterIdentity();
  }

  // ── data ─────────────────────────────────────────────────────
  // R53: the assignee field was free-text only — you could pick a vendor but
  // not an in-house tech (Allan etc.). This builds a <datalist> of everyone
  // assignable: team roster (leads + field roles), vendor companies, and any
  // name already in use on a WO — so the field is a real picker while staying
  // typeable for one-off names. `assigned_to` stays a plain string server-side.
  private assignDatalist(id: string): string {
    const seen = new Set<string>();
    const opts: string[] = [];
    const add = (name: string, hint?: string) => {
      const v = String(name || '').trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      opts.push(`<option value="${this.esc(v)}"${hint ? ` label="${this.esc(v)} — ${this.esc(hint)}"` : ''}></option>`);
    };
    // Team first (in-house people), then vendors, then any existing WO names.
    for (const p of (this.people || [])) add(p.full_name || p.email, p.role || 'team');
    for (const v of (this.vendors || [])) add(v.name, 'vendor');
    for (const w of (this.workOrders || [])) add(String(w.assigned_to || ''));
    return `<datalist id="${id}">${opts.join('')}</datalist>`;
  }

  private async loadAll(): Promise<void> {
    const [board, wos, tpls, assets, plans, vendors, incidents, people, cleaners] = await Promise.all([
      fetch(`${API}/maintenance/board`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/work-orders`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/pm-templates`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/assets`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/pm-plans`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/vendors`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/incidents`).then(r => r.ok ? r.json() : null).catch(() => null),
      // R53: the team roster — so a WO can be ASSIGNED to an in-house tech
      // (Allan etc.), not just typed as free text or picked from vendors.
      fetch('/api/tasks/assignees').then(r => r.ok ? r.json() : null).catch(() => null),
      // Hourly rates for in-house techs (ao_cleaners) — WO labor cost is
      // hourly_rate (assignee name match vendors+cleaners) × minutes/60.
      fetch(`${API}/cleaners`).then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.board = board;
    this.workOrders = wos ? (wos.work_orders || []) : null;
    this.openPendingTextVendor();
    this.templates = tpls ? (tpls.templates || []) : null;
    this.assets = assets ? (assets.assets || []) : null;
    this.pmPlans = plans ? (plans.plans || []) : null;
    this.vendors = vendors ? (vendors.vendors || []) : null;
    this.cleaners = cleaners ? (cleaners.cleaners || []) : null;
    this.incidents = incidents ? (incidents.incidents || []) : null;
    if (people && Array.isArray(people.people)) this.people = people.people;
    if (this.templates && this.templates.length) {
      const std = this.templates[0];
      this.cadenceDefault = Number(std.cadence_days) || 90;
    }
  }
  private async reloadCalendar(): Promise<void> {
    const j = await fetch(`${API}/maintenance/calendar?days=${this.calDays}`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.calendar = j;
  }
  private async reloadPmPlans(): Promise<void> {
    const j = await fetch(`${API}/pm-plans`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.pmPlans = j.plans || [];
  }
  private async reloadPmDue(): Promise<void> {
    const j = await fetch(`${API}/pm/due?days=${this.calDays}`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.pmDue = j;
  }
  private async reloadBoard(): Promise<void> {
    const j = await fetch(`${API}/maintenance/board`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.board = j;
  }
  private async reloadWos(): Promise<void> {
    const j = await fetch(`${API}/work-orders`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.workOrders = j.work_orders || [];
    // R102 review: any work-order change can move a calendar item — drop the
    // cached month grid; the calendar refetches the next time it is shown.
    this.calGrid = null;
  }
  private async reloadTemplates(): Promise<void> {
    const j = await fetch(`${API}/pm-templates`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.templates = j.templates || [];
  }
  private async reloadAssets(): Promise<void> {
    const j = await fetch(`${API}/assets`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.assets = j.assets || [];
  }
  private async reloadVendors(): Promise<void> {
    const j = await fetch(`${API}/vendors`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.vendors = j.vendors || [];
  }
  private async reloadCosts(): Promise<void> {
    const qs = `days=${this.costDays}${this.costListingFilter ? `&listing_id=${encodeURIComponent(this.costListingFilter)}` : ''}`;
    const j = await fetch(`${API}/maintenance/costs?${qs}`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.costs = j;
  }
  private async reloadAssetIntel(): Promise<void> {
    const j = await fetch(`${API}/maintenance/asset-intel`).then(r => r.ok ? r.json() : null).catch(() => null);
    this.assetIntel = j ? (j.assets || []) : null;
  }
  private async reloadAutogenFlags(): Promise<void> {
    const j = await fetch(`${API}/autogen-flags`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.autogenFlags = j.flags || {};
  }
  private async reloadIncidents(): Promise<void> {
    const qs: string[] = [];
    if (this.incStatusFilter) qs.push(`status=${encodeURIComponent(this.incStatusFilter)}`);
    if (this.incSeverityFilter) qs.push(`severity=${encodeURIComponent(this.incSeverityFilter)}`);
    const j = await fetch(`${API}/incidents${qs.length ? `?${qs.join('&')}` : ''}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.incidents = j.incidents || [];
  }
  private async reloadIncidentDetail(id: string): Promise<void> {
    const j = await fetch(`${API}/incidents/${id}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j && j.incident) this.incDetail[id] = { incident: j.incident, on_call_vendors: j.on_call_vendors || [] };
  }
  // AI-unblock: a new incident is saved before its AI triage runs — check back
  // every 6 s (up to ~4 min) and refresh the card when severity / category /
  // self-fix steps land. Holds the redraw while someone is typing on the page.
  private watchIncidentTriage(id: string): void {
    if (this.incTriageWatch.has(id)) return;
    this.incTriageWatch.add(id);
    const started = Date.now();
    let landed = false;
    const tick = async () => {
      if (!landed) {
        const j = await fetch(`${API}/incidents/${id}`).then(r => r.ok ? r.json() : null).catch(() => null);
        if (j && j.incident && (!j.incident.triage || j.incident.triage.status !== 'pending')) {
          landed = true;
          this.incDetail[id] = { incident: j.incident, on_call_vendors: j.on_call_vendors || [] };
          await this.reloadIncidents();
        }
      }
      const typing = !!document.activeElement && this.container.contains(document.activeElement) && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName);
      if (landed && !typing) { this.incTriageWatch.delete(id); if (this.tab === 'incidents') this.draw(); return; }
      if (Date.now() - started > 240000) { this.incTriageWatch.delete(id); return; }
      setTimeout(() => { void tick(); }, 6000);
    };
    setTimeout(() => { void tick(); }, 6000);
  }
  // Field-access panel for a listing (lazy, cached). Used by expanded
  // emergency/urgent/incident work-order cards so a tech on the (PWA-installed)
  // phone has lockbox/breaker/water/HVAC/safety + a guest-present courtesy note.
  private async reloadField(listingId: string): Promise<void> {
    if (this.fieldByListing[listingId]) return;
    const j = await fetch(`${API}/maintenance/field/${encodeURIComponent(listingId)}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (j) this.fieldByListing[listingId] = j;
  }
  private async get(path: string): Promise<any> {
    const r = await fetch(`${API}${path}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { alert(j.error || `request failed (${r.status})`); return null; }
    return j;
  }
  private _lastErrCode: string | null = null;   // R70: machine error code from the last failed post (e.g. 'owner_handled')
  private async post(path: string, body: any): Promise<any> {
    const r = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    this._lastErrCode = r.ok ? null : (j.error || null);
    if (!r.ok) alert(j.message || j.error || `request failed (${r.status})`);
    return r.ok ? j : null;
  }
  private async del(path: string): Promise<boolean> {
    const r = await fetch(`${API}${path}`, { method: 'DELETE' });
    if (!r.ok) { const j = await r.json().catch(() => ({} as any)); alert(j.error || `request failed (${r.status})`); }
    return r.ok;
  }

  // ── formatting helpers ───────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private fmtDate(iso: string | null): string {
    if (!iso) return '—';
    // Bare 'YYYY-MM-DD' renders as a calendar date pinned to UTC — parsing it
    // with new Date() alone shows the PREVIOUS day in Pacific.
    const bare = /^\d{4}-\d{2}-\d{2}$/.test(String(iso));
    const d = bare ? new Date(iso + 'T00:00:00Z') : new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', bare
      ? { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', year: 'numeric' });
  }
  // Whole days from today to a YYYY-MM-DD date (negative = past). "Today" is
  // the LOCAL calendar date (en-CA = YYYY-MM-DD) — toISOString() is UTC, which
  // from ~5pm Pacific flags tomorrow-due WOs "past due" a day early.
  private daysFromToday(isoDate: string | null): number | null {
    if (!isoDate) return null;
    const today = new Date(new Intl.DateTimeFormat('en-CA').format(new Date()) + 'T00:00:00Z').getTime();
    const target = new Date(isoDate + 'T00:00:00Z').getTime();
    if (isNaN(target)) return null;
    return Math.round((target - today) / 86400000);
  }
  private fmtDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  // USD money; '—' when there's nothing to show.
  private fmtMoney(n: any): string {
    const v = Number(n);
    if (!Number.isFinite(v) || v === 0) return '—';
    return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  private fmtMins(m: any): string {
    const v = Math.max(0, Math.round(Number(m) || 0));
    if (!v) return '0m';
    const h = Math.floor(v / 60), mm = v % 60;
    return h ? `${h}h ${mm}m` : `${mm}m`;
  }
  // Hourly rate for an assignee, resolved by name match against the vendor
  // roster + in-house cleaner roster (ao_cleaners). null when no rate on file.
  private hourlyRateFor(name: any): number | null {
    const nm = String(name || '').trim().toLowerCase();
    if (!nm) return null;
    const hit = [...(this.vendors || []), ...(this.cleaners || [])]
      .find(p => String(p.name || '').trim().toLowerCase() === nm);
    const r = hit ? Number(hit.hourly_rate) : NaN;
    return Number.isFinite(r) && r > 0 ? r : null;
  }
  // Display-only labor cost of a WO: assignee hourly_rate × total_minutes/60.
  // null (skip silently) when no tracked minutes or no rate on file.
  private laborCost(w: any): number | null {
    const mins = Number(w?.total_minutes) || 0;
    if (mins <= 0) return null;
    const rate = this.hourlyRateFor(w?.assigned_to);
    return rate == null ? null : rate * mins / 60;
  }
  // Labor-time panel on an expanded WO — start/pause timer + manual entry
  // (Jesse R2b #4: internal work is costed by hours). Persists to the WO.
  // R56: internal team notes on a WO — the counterpart to the owner-facing
  // summary. Explicitly labeled: nothing here ever reaches an owner report.
  private intNotesListHtml(w: any): string {
    const notes: any[] = Array.isArray(w.internal_notes) ? [...w.internal_notes].reverse() : [];
    return notes.map(n => `
      <div style="padding:6px 8px; border-left:3px solid var(--warning,#f59e0b); background:var(--warning-soft,#fffbeb); border-radius:4px; margin-top:5px; font-size:12px;">
        <div>${this.esc(n.text)}</div>
        <div style="font-size:10.5px; color:var(--text-muted); margin-top:2px;">${this.esc((n.by || 'unknown').split('@')[0])} · ${this.fmtDateTime(n.at)}</div>
      </div>`).join('') || '<div style="font-size:11.5px; color:var(--text-faint,#9ca3af); margin-top:4px;">No internal notes yet.</div>';
  }

  private renderInternalNotes(w: any): string {
    // R105 #3: merged into 💬 Internal comments (woCommentsList renders
    // internal_notes as older entries); the composer below is retired.
    if (w) return '';
    return `
      <label style="display:block; margin-top:10px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">🔒 Internal notes (team only — never on owner reports)</label>
      <div data-mn-intnotes-list="${this.esc(w.id)}">${this.intNotesListHtml(w)}</div>
      <div style="display:flex; gap:6px; margin-top:6px;">
        <input type="text" data-mn-intnote="${this.esc(w.id)}" placeholder="team-only update — parts ordered, waiting on owner, vendor quirks…" style="flex:1; padding:7px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px;"/>
        <button class="mn-btn sm" data-mn-intnote-add="${this.esc(w.id)}">Add</button>
      </div>`;
  }

  private renderTimePanel(w: any): string {
    const running = !!w.timer_started_at;
    const total = Number(w.total_minutes) || 0;
    const labor = this.laborCost(w);
    return `
      <div class="mn-time">
        <span class="mn-time-h">Labor time</span>
        <span class="mn-time-total mn-mono">${this.fmtMins(total)}</span>
        ${labor != null ? `<span style="font-size:11px; color:var(--text-muted);" title="assignee hourly rate × tracked time — display only">💵 labor ≈ ${this.fmtMoney(labor)}</span>` : ''}
        ${running ? '<span class="mn-time-run">⏱ running…</span>' : ''}
        <button class="mn-btn sm ${running ? 'danger' : 'primary'}" data-mn-timer="${this.esc(w.id)}|${running ? 'pause' : 'start'}">${running ? '⏸ Pause' : '▶ Start'}</button>
        <input type="number" min="1" placeholder="min" data-mn-tmin="${this.esc(w.id)}" style="width:64px; padding:5px 7px; border:1px solid var(--border); border-radius:6px; font-size:12px;">
        <button class="mn-btn sm" data-mn-timer-manual="${this.esc(w.id)}">+ Log time</button>
      </div>`;
  }
  // Tech completion report (Mark's Loom: "he can give his report after —
  // pre-photos, post-photos, what did he do, how long did it take"). Once filed
  // it renders read-only; until then everyone (field tech and office alike)
  // gets the textarea + one button that files the report AND marks the WO done
  // in a single POST — the update route handles both.
  // R100 A6 (Jesse: "send WO and get work done with any vendor… quote… flag
  // bad quotes… complete without being a user… add their invoice… close out
  // parts"): what the vendor link produced — quotes with the AI sanity badge
  // and approve/decline, item-level progress the vendor marked, invoices they
  // sent/built, and their messages. The link itself is minted/texted from the
  // 📲 Text vendor modal.
  private pendingReview(w: any): number {
    return (Array.isArray(w.quotes) ? w.quotes : []).filter((q: any) => q.status === 'pending').length + (Array.isArray(w.invoices) ? w.invoices : []).filter((i: any) => i.status === 'pending').length;
  }

  private renderVendorFlow(w: any): string {
    const quotes: any[] = Array.isArray(w.quotes) ? w.quotes : [];
    const replies: any[] = Array.isArray(w.vendor_replies) ? w.vendor_replies : [];
    const inv: any[] = (Array.isArray(w.invoices) ? w.invoices : []).filter((i: any) => i.source === 'vendor_link');
    const prog = w.vendor_progress;
    const rep = w.vendor_report;
    if (!quotes.length && !replies.length && !inv.length && !prog && !w.vendor_link && !w.vendor_texted && !rep) return '';
    const RESULT_LABEL: Record<string, string> = { fixed: 'Fixed', partial: 'Partially fixed', not_fixed: 'Not fixed', skipped: "Couldn't get to it" };
    const skippedItems = (w.items || []).filter((i: any) => i.vendor_status === 'skipped' && !i.done);
    const repHtml = rep ? `<div style="margin-top:8px; padding:8px 10px; border:1px solid var(--border); border-radius:8px; background:var(--card,#fff); font-size:12px;">
          <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;"><strong>Vendor report</strong>${rep.result ? `<span class="mn-chip" style="${rep.result === 'fixed' ? 'background:var(--success-soft,#dcfce7); color:var(--success-text,#166534);' : rep.result === 'partial' ? 'background:var(--warning-soft,#fffbeb); color:#92400e;' : 'background:var(--danger-soft,#fee2e2); color:var(--danger-text,#b91c1c);'}">${this.esc(RESULT_LABEL[rep.result] || rep.result)}</span>` : ''}<span class="mn-muted" style="color:var(--text-muted); font-size:11.5px;">${this.esc(String(rep.by || '').replace(/^vendor:/, ''))} · ${this.esc(String(rep.at || '').slice(0, 16).replace('T', ' '))}</span></div>
          ${rep.work_done ? `<div style="margin-top:4px; white-space:pre-wrap;">${this.esc(rep.work_done)}</div>` : ''}
          ${skippedItems.length ? `<div style="margin-top:4px; color:#92400e;">⏭ Not reached: ${skippedItems.map((i: any) => this.esc(i.title)).join(' · ')}</div>` : ''}
          ${(rep.before || []).length || (rep.after || []).length ? `<div style="display:flex; gap:14px; margin-top:6px; flex-wrap:wrap;">${(rep.before || []).length ? `<div><div class="mn-muted" style="color:var(--text-muted); font-size:10.5px; text-transform:uppercase; letter-spacing:.04em;">Before</div>${mediaStrip(rep.before, { size: 'sm', alt: 'vendor before photo' })}</div>` : ''}${(rep.after || []).length ? `<div><div class="mn-muted" style="color:var(--text-muted); font-size:10.5px; text-transform:uppercase; letter-spacing:.04em;">After</div>${mediaStrip(rep.after, { size: 'sm', alt: 'vendor after photo' })}</div>` : ''}</div>` : ''}
        </div>` : '';
    // AI-unblock: the price check now runs AFTER the vendor's quote is saved —
    // 'pending' shows as checking (a stale one, e.g. across a restart, as —),
    // and a failed check says why instead of a bare dash.
    const aiFresh = (q: any) => q.ai_status === 'pending' && Date.now() - Date.parse(q.at || '') < 15 * 60000;
    const aiChip = (q: any) => !q.ai_check ? (aiFresh(q) ? '<span class="mn-chip" style="opacity:.75;" title="The AI price check is running — refresh in a minute">AI checking…</span>'
        : `<span class="mn-chip" style="opacity:.6;" title="${this.esc(q.ai_fail && q.ai_fail.label ? `AI check didn't run: ${q.ai_fail.label}` : 'AI check not available')}">AI —</span>`)
      : q.ai_check.flag === 'ok' ? `<span class="mn-chip" style="background:var(--success-soft,#dcfce7); color:var(--success-text,#166534);" title="${this.esc((q.ai_check.reasons || []).join(' · '))}">AI: looks fair</span>`
      : q.ai_check.flag === 'high' ? `<span class="mn-chip" style="background:var(--danger-soft,#fee2e2); color:var(--danger-text,#b91c1c);" title="${this.esc((q.ai_check.reasons || []).join(' · '))}">AI: looks high${q.ai_check.expected_range ? ` (expect ${this.fmtMoney(q.ai_check.expected_range[0])}–${this.fmtMoney(q.ai_check.expected_range[1])})` : ''}</span>`
      : `<span class="mn-chip" style="background:var(--warning-soft,#fffbeb); color:#92400e;" title="${this.esc((q.ai_check.reasons || []).join(' · '))}">AI: review${q.ai_check.expected_range ? ` (expect ${this.fmtMoney(q.ai_check.expected_range[0])}–${this.fmtMoney(q.ai_check.expected_range[1])})` : ''}</span>`;
    return `
      <div style="margin-top:10px; border:1px solid var(--border); border-radius:10px; padding:10px 12px; background:var(--bg-subtle,#f8fafc);">
        <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
          <span style="font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted);">Vendor flow</span>
          ${w.vendor_texted ? `<span class="mn-chip" title="${this.esc(w.vendor_texted.group && Array.isArray(w.vendor_texted.group.names) ? w.vendor_texted.group.names.join(', ') : '')}">${this.esc(vendorTextedLabel(w.vendor_texted))}</span>` : ''}
          ${w.vendor_link ? `<button class="mn-btn sm" data-mn-wolink="${this.esc(w.id)}" title="Copy the vendor's no-login link">🔗 copy link</button><button class="mn-btn sm" data-mn-worevoke="${this.esc(w.id)}" title="Kill the vendor's link now (wrong number, vendor dropped) — a new text mints a fresh one">🚫 revoke link</button>` : ''}
          ${w.vendor_link && w.vendor_link.primary === false ? '<span class="mn-chip" title="Quotes and invoices for this bundle are recorded on the primary work order">bundled</span>' : ''}
          ${prog ? `<span class="mn-chip" title="Items the vendor marked done">${prog.done}/${prog.total} items done${prog.all_done ? ' · vendor says complete' : ''}</span>` : ''}
        </div>
        ${quotes.length ? `<div style="margin-top:8px;">${quotes.map(q => `
          <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:5px 0; border-top:1px solid var(--border-soft,#f1f5f9); font-size:12px;">
            <strong>${this.fmtMoney(q.total)}</strong><span class="mn-muted" style="color:var(--text-muted); font-size:11.5px;">${this.esc(q.vendor_name || 'vendor')} · ${this.esc(String(q.at || '').slice(0, 10))}${q.site_visit ? ' · wants a site visit' : ''}</span>
            ${aiChip(q)}
            <span class="mn-chip" style="${q.status === 'approved' ? 'background:var(--success-soft,#dcfce7); color:var(--success-text,#166534);' : q.status === 'declined' ? 'background:var(--danger-soft,#fee2e2); color:var(--danger-text,#b91c1c);' : ''}">${this.esc(q.status)}</span>
            ${q.doc_url ? `<a href="${this.esc(q.doc_url)}" target="_blank" rel="noopener">📄 quote doc</a>` : ''}
            ${q.status === 'pending' ? `<button class="mn-btn sm" data-mn-quote="${this.esc(w.id)}|${this.esc(q.id)}|approve">✓ Approve</button><button class="mn-btn sm" data-mn-quote="${this.esc(w.id)}|${this.esc(q.id)}|decline">✕ Decline</button>` : ''}
            ${q.note ? `<div class="mn-muted" style="color:var(--text-muted); font-size:11.5px;" style="flex-basis:100%;">${this.esc(q.note)}</div>` : ''}
            ${(q.lines || []).length ? `<div class="mn-muted" style="color:var(--text-muted); font-size:11.5px;" style="flex-basis:100%;">per item: ${q.lines.map((l: any) => `${this.esc(((w.items || []).find((i: any) => i.key === l.item_key) || {}).title || l.item_key)} ${this.fmtMoney(l.amount)}`).join(' · ')}</div>` : ''}
          </div>`).join('')}</div>` : ''}
        ${inv.length ? `<div style="margin-top:6px; font-size:12px;">${inv.map(i => `<div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap;">🧾 ${i.url ? `<a href="${this.esc(i.url)}" target="_blank" rel="noopener">${this.esc(i.name)}</a>` : this.esc(i.name)}${i.total != null ? ` · ${this.fmtMoney(i.total)}` : ''}${i.invoice_no ? ` · #${this.esc(i.invoice_no)}` : ''}${(i.lines || []).length ? ` <span class="mn-muted" style="color:var(--text-muted); font-size:11.5px;">(${i.lines.map((l: any) => `${this.esc(l.label)} ${this.fmtMoney(l.amount)}`).join(', ')})</span>` : ''}
            ${i.status === 'accepted' ? '<span class="mn-chip" style="background:var(--success-soft,#dcfce7); color:var(--success-text,#166534);" title="Accepted — the WO cost is this invoice">accepted → cost</span>' : i.status === 'rejected' ? '<span class="mn-chip" style="background:var(--danger-soft,#fee2e2); color:var(--danger-text,#b91c1c);">rejected</span>' : i.id ? `<span class="mn-chip" style="background:var(--warning-soft,#fffbeb); color:#92400e;" title="The vendor's numbers wait for you — accepting sets the WO cost">awaiting acceptance</span><button class="mn-btn sm" data-mn-invoice="${this.esc(w.id)}|${this.esc(i.id)}|accept">✓ Accept${i.total != null ? ` (${this.fmtMoney(i.total)} → cost)` : ''}</button><button class="mn-btn sm" data-mn-invoice="${this.esc(w.id)}|${this.esc(i.id)}|reject">✕ Reject</button>` : ''}</div>`).join('')}</div>` : ''}
        ${repHtml}
        ${replies.length ? vendorRepliesHtml(replies, { limit: 4 }) : ''}
      </div>`;
  }

  // R116 #2: the Text-vendor modal is the shared TextVendorSheet (the Calendar's WO inspector opens the same form
  // in its sheet) — vendor + team members with a phone on file + typed numbers go out as ONE Quo group text.
  private async showTextVendorModal(w: any): Promise<void> {
    openTextVendorModal({ wo: w, vendors: this.vendors || [], by: MaintenancePage.actorName(),
      onDone: async () => { await this.loadAll(); this.draw(); } });
  }

  private renderCompletion(w: any): string {
    if (w.completion) {
      const c = w.completion;
      const followup = c.needs_followup ? `
          <div style="margin-top:6px; padding:6px 8px; border-left:3px solid var(--warning,#f59e0b); background:var(--warning-soft,#fffbeb); border-radius:4px;">
            <div style="font-size:10px; font-weight:700; color:var(--warning-text,#92400e); text-transform:uppercase; letter-spacing:0.04em;">🔁 Follow-up needed</div>
            ${c.followup_note ? `<div style="white-space:pre-wrap; font-size:12px; margin-top:3px;">${this.esc(c.followup_note)}</div>` : ''}
          </div>` : '';
      return `
        <div style="margin-top:10px; padding:8px 10px; border-left:3px solid var(--success, #16a34a); background:var(--success-soft, #f0fdf4); border-radius:4px;">
          <div style="font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">Tech report — ${this.esc((c.by || 'unknown').split('@')[0])} · ${this.fmtDateTime(c.at)}</div>
          <div style="white-space:pre-wrap; font-size:12.5px; margin-top:4px;">${this.esc(c.what_done)}</div>
          <div style="font-size:11px; color:var(--text-muted); margin-top:4px;">⏱ ${this.fmtMins(w.total_minutes)}</div>
          ${followup}
        </div>`;
    }
    return `<div data-evp-scope>
      <label style="display:block; margin-top:10px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">Tech report (what was done)</label>
      <textarea id="mn-comp-${this.esc(w.id)}" style="width:100%; box-sizing:border-box; padding:7px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px; font-family:inherit; resize:vertical; min-height:50px; margin-top:4px;" placeholder="What did you do? Anything the team should know?"></textarea>
      <label style="display:flex; align-items:center; gap:6px; margin-top:6px; font-size:12px; cursor:pointer;">
        <input type="checkbox" data-mn-comp-fu="${this.esc(w.id)}"> 🔁 More work / inspection needed
      </label>
      <input type="text" data-mn-comp-funote="${this.esc(w.id)}" placeholder="what's still needed — part on order, needs inspection, owner approval…" style="display:none; width:100%; box-sizing:border-box; padding:7px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px; margin-top:5px;"/>
      ${evidencePickerHtml(this.afterPickerCfg(w))}
      <button class="mn-btn sm primary" data-mn-comp-file="${this.esc(w.id)}" style="margin-top:10px;">✅ File report &amp; mark done</button>
    </div>`;
  }
  // <option> list for the vendor selects (shared by WO controls + PM form).
  private vendorOptions(selectedId: string | null): string {
    const opts = (this.vendors || []).map(v =>
      `<option value="${this.esc(v.id)}" ${selectedId === v.id ? 'selected' : ''}>${this.esc(v.name)}</option>`).join('');
    return `<option value="">— none —</option>${opts}`;
  }
  private statusPill(status: string): string {
    const map: Record<string, [string, string]> = {
      open: ['var(--bg-subtle)', 'var(--text-muted)'],
      in_progress: ['var(--warning-soft)', 'var(--warning-text)'],
      done: ['var(--success-soft)', 'var(--success-text, #166534)'],
    };
    const [bg, fg] = map[status] || ['var(--bg-subtle)', 'var(--text-muted)'];
    const label = status === 'in_progress' ? 'In progress' : status.charAt(0).toUpperCase() + status.slice(1);
    return `<span class="mn-pill" style="background:${bg}; color:${fg};">${label}</span>`;
  }
  // priority of a WO, tolerating records persisted before the field existed.
  private woPriority(w: any): 'emergency' | 'urgent' | 'routine' | 'pm' {
    return w.priority ?? (w.type === 'pm' ? 'pm' : 'routine');
  }
  private priorityBadge(priority: string): string {
    const map: Record<string, [string, string, string]> = {
      emergency: ['var(--danger-soft)', 'var(--danger-text, #b91c1c)', 'Emergency'],
      urgent: ['var(--warning-soft)', 'var(--warning-text)', 'Urgent'],
      routine: ['var(--bg-subtle)', 'var(--text-muted)', 'Routine'],
      pm: ['var(--info-soft, #dbeafe)', 'var(--info, #1d4ed8)', 'PM'],
    };
    const [bg, fg, label] = map[priority] || map.routine;
    return `<span class="mn-pill" style="background:${bg}; color:${fg};">${label}</span>`;
  }

  // Acuity-colored severity pill for incidents (ER triage feel).
  private severityPill(severity: string): string {
    const map: Record<string, [string, string, string]> = {
      emergency: ['var(--danger-soft, #fee2e2)', 'var(--danger-text, #b91c1c)', 'Emergency'],
      urgent: ['var(--warning-soft)', 'var(--warning-text)', 'Urgent'],
      minor: ['var(--bg-subtle)', 'var(--text-muted)', 'Minor'],
    };
    const [bg, fg, label] = map[severity] || map.urgent;
    return `<span class="mn-pill" style="background:${bg}; color:${fg};">${label}</span>`;
  }
  private incStatusPill(status: string): string {
    const map: Record<string, [string, string, string]> = {
      open: ['var(--danger-soft, #fee2e2)', 'var(--danger-text, #b91c1c)', 'Open'],
      acknowledged: ['var(--warning-soft)', 'var(--warning-text)', 'Acknowledged'],
      dispatched: ['var(--info-soft, #dbeafe)', 'var(--info, #1d4ed8)', 'Dispatched'],
      resolved: ['var(--success-soft)', 'var(--success-text, #166534)', 'Resolved'],
    };
    const [bg, fg, label] = map[status] || map.open;
    return `<span class="mn-pill" style="background:${bg}; color:${fg};">${label}</span>`;
  }
  // Coarse "Nm ago" / "Nh ago" / "Nd ago" relative time for the SLA clock.
  private fmtAgo(iso: string | null): string {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso).getTime();
    if (!isFinite(ms)) return '—';
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  // ── shell ────────────────────────────────────────────────────
  // R90 review #36: the 15-minute auto-refresh re-renders this page — never
  // while someone is typing (ground notes, WO edits, incident forms) or has
  // an un-saved ground-note draft sitting in a box.
  suspendAutoRefresh(): boolean {
    const ae = document.activeElement;
    if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT') && this.container.contains(ae)) return true;
    return [...this.prDrafts.values()].some(v => v.trim().length > 0);
  }

  private static fieldRole(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }
  // R75: who is acting — rides WO creation as created_by so the team knows
  // who to ask for more info.
  private static actorName(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); }
    catch { return ''; }
  }
  // AI-unblock (review #18): a create's idempotency key (randomUUID needs a secure context — fall back).
  private static newRequestId(): string {
    try { if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID(); } catch { /* insecure context */ }
    return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
  }

  // R70 (Mark) + R72 (Jesse): the day plan — due/doable WOs grouped by
  // property, now with a suggested ROUTE (nearest-neighbor over property
  // coordinates, constraint-critical stops first), job-size estimates, a
  // staffing readout, a must-do-only view, inline WO editing, and send tools.
  private dayPlan: any | null = null;
  private dayPlanOpen = false;
  private dayPlanDate = '';   // '' = server default (tomorrow)
  private dayPlanDateTimer: number | null = null;
  private dayPlanMustDo = false;
  private dayPlanEditWo: string | null = null;
  private dayPlanHtml(): string {
    const head = `<div class="mn-card" style="margin:10px 0; padding:10px 14px;">
      <div data-mn-dayplan-toggle style="cursor:pointer; display:flex; justify-content:space-between; align-items:center; font-weight:700; font-size:13px;">
        🌅 Day plan — what's due &amp; doable ${this.dayPlan ? `on ${this.esc(this.dayPlan.date)}` : 'tomorrow'}
        <span style="font-weight:400; font-size:12px; color:var(--text-muted);">${this.dayPlanOpen ? '▾ hide' : '▸ show'}</span>
      </div>`;
    if (!this.dayPlanOpen) return head + '</div>';
    if (!this.dayPlan) return head + '<div style="padding:10px 0; color:var(--text-muted); font-size:12.5px;">Loading…</div></div>';
    const p = this.dayPlan;
    const occStyle = (o: string) => /same-day/.test(o) ? 'background:#fef3c7;color:#92400e;'
      : /checkout/.test(o) ? 'background:#dbeafe;color:#1d4ed8;'
      : /in-house/.test(o) ? 'background:#fee2e2;color:#b91c1c;'
      : /check-in/.test(o) ? 'background:#fce7f3;color:#be185d;'
      : 'background:#dcfce7;color:#166534;';
    const hm = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`;
    const t = p.totals || {}; const mt = p.must_do_totals || {};
    const totalsStrip = `<div style="display:flex; gap:14px; flex-wrap:wrap; align-items:center; font-size:12.5px; margin:8px 0; padding:8px 12px; background:var(--bg-subtle,#f8fafc); border-radius:10px;">
      <span>🧰 <strong>${p.count}</strong> work order${p.count === 1 ? '' : 's'}</span>
      <span>⏱ ~${hm(t.work_minutes || 0)} work + ${hm(t.travel_minutes || 0)} driving (${t.travel_km || 0}km)</span>
      <span>👷 ≈ <strong>${t.staff_needed || 0}</strong> tech${(t.staff_needed || 0) === 1 ? '' : 's'} for ${p.must_do_only ? 'the must-do set shown' : 'everything'}</span>
      <span style="color:#b45309;">⭐ must-do: ${mt.wo_count || 0} WOs · ~${hm((mt.work_minutes || 0) + (mt.travel_minutes || 0))} ≈ <strong>${mt.staff_needed || 0}</strong> tech${(mt.staff_needed || 0) === 1 ? '' : 's'}</span>
      <label style="display:flex; gap:5px; align-items:center; margin-left:auto;" title="R102: how many people we actually have that day — the plan fills their day in priority order and DEFERS the rest instead of pretending 8 techs exist"><select data-mn-dayplan-staff style="padding:4px 8px; border:1px solid var(--border); border-radius:7px; font-size:12px; font-family:inherit;"><option value="">👥 people today: auto</option>${[1, 2, 3, 4, 5, 6, 8, 10].map(n => `<option value="${n}" ${this.dayPlanStaff === n ? 'selected' : ''}>👥 ${n} ${n === 1 ? 'person' : 'people'}</option>`).join('')}</select></label>
      <label style="display:flex; gap:5px; align-items:center; cursor:pointer;"><input type="checkbox" data-mn-dayplan-mustdo ${this.dayPlanMustDo ? 'checked' : ''}> ⭐ must-do only</label>
    </div>
    ${(() => {
      const st = p.staffing; if (!st) return '';
      return `<div class="mn-dp-staff ${st.must_do_deferred ? 'bad' : (st.deferred_count ? 'warn' : 'ok')}">
        👥 <b>${st.staff}</b> ${st.staff === 1 ? 'person' : 'people'} = ${hm(st.capacity_minutes)} of capacity · planned <b>${st.planned_count}</b> of ${st.planned_count + st.deferred_count} jobs (~${hm(st.planned_minutes)})
        ${st.deferred_count ? `· <b>${st.deferred_count} deferred</b> (~${hm(st.overflow_minutes)} over the day)` : '· everything fits ✓'}
        ${st.must_do_deferred ? `· <b>⚠ ${st.must_do_deferred} must-do job${st.must_do_deferred === 1 ? '' : 's'} won't get done</b> — add a person or move the deadline` : ''}
        ${st.booked_deferred ? `· <b>📆 ${st.booked_deferred} booked job${st.booked_deferred === 1 ? '' : 's'} deferred</b> — rebook or add a person` : ''}
        ${st.vendor_count ? `· 🏷 ${st.vendor_count} vendor-run (not on our clock)` : ''}
        ${st.deferred_count ? `<button class="mn-btn sm" data-mn-dp-deferall title="Move every deferred job's due date to the next day so tomorrow's plan picks them up">⏭ push deferred to the next day</button>` : ''}
      </div>`;
    })()}
    <div style="font-size:10.5px; color:var(--text-faint); margin:-4px 0 6px;">${this.esc(p.basis || '')}</div>`;
    const routeStrip = (p.route || []).length >= 2 ? `<div style="font-size:12px; margin:6px 0 10px; padding:8px 12px; border:1px dashed var(--border); border-radius:10px;">
      🗺 <strong>Suggested route</strong> (constraint-critical stops pulled early):
      ${(p.route || []).map((r: any, i: number) => `<span style="white-space:nowrap;">${i ? ' → ' : ''}<strong>${i + 1}.</strong> ${this.esc(r.listing_name)} <span style="color:var(--text-muted);">(${this.esc(r.eta || '')}${r.leg_minutes ? ` · +${r.leg_minutes}m drive` : ''})</span></span>`).join('')}
      ${p.gmaps_url ? ` · <a data-mn-gmaps href="#" style="font-weight:700;">Open in Google Maps (live traffic${p.gmaps_truncated ? ` — first ${p.gmaps_stops} of ${(p.route || []).length} stops, Maps caps at 10` : ''}) →</a>` : ''}
      ${(p.unroutable || []).length ? `<div style="color:var(--text-faint); margin-top:3px;">no location on file: ${(p.unroutable || []).map((n: string) => this.esc(n)).join(', ')}</div>` : ''}
    </div>` : '';
    const sendStrip = `<div style="display:flex; gap:8px; margin:4px 0 10px;">
      <button class="mn-btn sm" data-mn-dayplan-copy>📋 Copy plan (text)</button>
      <button class="mn-btn sm" data-mn-dayplan-print>🖨 Print</button>
      ${['admin', 'director', 'manager', 'remote_ops'].includes(MaintenancePage.fieldRole()) ? '<button class="mn-btn sm" data-mn-dayplan-slack title="Posts to the ops Slack channel (needs SLACK_WEBHOOK_URL)">📨 Slack</button>' : ''}
    </div>`;
    const woRow = (g: any, w: any) => {
      const editing = this.dayPlanEditWo === w.id;
      const sizeChip = `<span title="~${w.est_minutes}m — keyword/skill estimate" style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:${w.size === 'L' ? '#fee2e2;color:#b91c1c' : w.size === 'M' ? '#fef3c7;color:#92400e' : '#dcfce7;color:#166534'};">${w.size} ~${hm(w.est_minutes || 45)}</span>`;
      const row = `<div data-mn-dp-wo="${this.esc(w.id)}" class="${w.deferred ? 'mn-dp-deferred' : ''}" style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; font-size:12.5px; padding:4px 0; border-top:1px solid var(--border-soft,#f8fafc); cursor:pointer;">
        <span>${w.priority === 'emergency' ? '🚨' : w.priority === 'urgent' ? '⚡' : '·'}</span>
        ${w.must_do ? '<span title="Must-do: urgent/emergency or due by this date">⭐</span>' : ''}
        ${w.booked ? '<span title="Booked for this day (calendar / board / card)" style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:#eaf2ff; color:#3730a3;">📆 booked</span>' : (w.overdue_booking ? `<span title="Was booked for ${this.esc(w.scheduled_date || '')} and is still open" style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:#fee2e2; color:#b91c1c;">📆 was booked ${this.esc(this.fmtDay(w.scheduled_date))}</span>` : '')}
        ${w.deferred ? `<span title="Doesn't fit the people we have that day" style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:#fee2e2; color:#b91c1c;">⏭ deferred</span>${(w.due_source !== 'checkout' || w.booked || w.overdue_booking) ? `<button class="mn-btn sm" data-mn-dp-defer="${this.esc(w.id)}" title="${(w.booked || w.overdue_booking) ? 'Rebook it for the next day' : 'Move its due date to the next day'}">→ next day</button>` : ''}` : ''}
        <span style="font-weight:600;">${this.esc(w.title)}</span>
        ${sizeChip}
        ${w.vendor_id ? `<span style="font-size:11px; color:var(--text-muted);" title="vendor booked on this work order">🏷 ${this.esc(this.vendorNameOf(w.vendor_id) || 'vendor')}</span>` : ''}
        ${w.assigned_to ? `<span style="font-size:11px; color:var(--text-muted);">👤 ${this.esc(w.assigned_to)}</span>` : (w.vendor_id ? '' : '<span style="font-size:11px; color:#b45309;">nobody on it</span>')}
        ${w.created_by ? `<span style="font-size:11px; color:var(--text-faint);" title="Submitted by — ask them for details">✍️ ${this.esc(w.created_by)}</span>` : ''}
        ${w.skill ? `<span style="font-size:10px; font-weight:700; color:var(--text-muted);">${w.skill === 'high' ? 'HIGH $65' : 'LOW $45'}</span>` : ''}
        ${w.due_source === 'checkout' ? '<span style="font-size:10.5px; color:#1d4ed8;">🔁 due at checkout</span>' : (w.due_date ? `<span class="mono" style="font-size:11px; color:var(--text-muted);">due ${this.esc(w.due_date)}</span>` : '')}
        ${w.guest_permission && w.guest_permission.granted ? `<span style="font-size:10.5px; font-weight:700; color:#166534; background:#dcfce7; border-radius:999px; padding:1px 8px;">🚪 ${w.guest_permission.mode === 'own_code' ? 'enter w/ own code' : 'guest will be present'}</span>` : ''}
        <span style="margin-left:auto; font-size:11px; color:var(--text-faint);">${editing ? '▾ close' : '✎ edit'}</span>
        ${w.description ? `<div style="flex:1 1 100%; color:var(--text-muted); font-size:11.5px; white-space:pre-wrap;">${this.esc(String(w.description).slice(0, 240))}</div>` : `<div style="flex:1 1 100%; color:var(--danger-text,#b91c1c); font-size:11px;">no instructions on this WO — add them below (✎) so the tech isn't walking in blind</div>`}
      </div>`;
      if (!editing) return row;
      return row + `<div style="padding:8px 10px 10px 22px; background:var(--bg-subtle,#f8fafc); border-radius:8px; margin:2px 0 6px;" data-mn-dp-editor="${this.esc(w.id)}">
        <label style="display:block; font-size:10px; font-weight:600; text-transform:uppercase; color:var(--text-muted);">Instructions — the problem + what needs to be done</label>
        <textarea data-dpe-desc style="width:100%; box-sizing:border-box; min-height:56px; padding:6px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px; font-family:inherit;">${this.esc(w.description || '')}</textarea>
        <div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:6px; align-items:flex-end;">
          <label style="font-size:10.5px; color:var(--text-muted);">Due<br><input type="date" data-dpe-due value="${this.esc(w.due_date || '')}" ${w.due_source === 'checkout' ? 'disabled title="follows checkout"' : ''} style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px;"></label>
          <label style="font-size:10.5px; color:var(--text-muted);">Assigned<br><input type="text" data-dpe-assign list="mn-assign-roster" value="${this.esc(w.assigned_to || '')}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; width:150px;"></label>
          <label style="font-size:10.5px; color:var(--text-muted);">Priority<br><select data-dpe-prio style="padding:5px 6px; border:1px solid var(--border); border-radius:6px; font-size:12px;">
            ${['emergency', 'urgent', 'routine', 'pm'].map(k => `<option value="${k}" ${(w.priority || 'routine') === k ? 'selected' : ''}>${k}</option>`).join('')}
          </select></label>
          <label style="font-size:10.5px; color:var(--text-muted);">Skill<br><select data-dpe-skill style="padding:5px 6px; border:1px solid var(--border); border-radius:6px; font-size:12px;">
            <option value="" ${!w.skill ? 'selected' : ''}>—</option><option value="low" ${w.skill === 'low' ? 'selected' : ''}>low $45</option><option value="high" ${w.skill === 'high' ? 'selected' : ''}>high $65</option>
          </select></label>
          <label style="font-size:10.5px; color:var(--text-muted);">Status<br><select data-dpe-status style="padding:5px 6px; border:1px solid var(--border); border-radius:6px; font-size:12px;">
            ${['open', 'in_progress', 'done'].map(k => `<option value="${k}" ${w.status === k ? 'selected' : ''}>${k.replace('_', ' ')}</option>`).join('')}
          </select></label>
          <button class="mn-btn sm primary" data-dpe-save="${this.esc(w.id)}">Save — plan refigures</button>
        </div>
        <div style="font-size:10.5px; color:var(--text-faint); margin-top:4px;">Move the due date off ${this.esc(p.date)} and this WO leaves the plan; the route and staffing recompute on save.</div>
      </div>`;
    };
    return head + totalsStrip + routeStrip + sendStrip + `
      <div style="display:flex; gap:8px; align-items:center; margin:8px 0;">
        <input type="date" data-mn-dayplan-date value="${this.esc(p.date)}" style="padding:6px 9px; border:1px solid var(--border); border-radius:8px; font-size:12.5px;">
        <span style="font-size:12px; color:var(--text-muted);">grouped in route order — tap a work order to edit it right here</span>
      </div>
      ${(p.groups || []).length ? (p.groups || []).map((g: any) => `
        <div style="border:1px solid var(--border-soft,#f1f5f9); border-radius:10px; padding:8px 12px; margin-bottom:8px;">
          <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; font-weight:600; font-size:13px;">
            🏠 ${this.esc(g.listing_name)}
            <span style="font-size:10.5px; font-weight:700; padding:2px 8px; border-radius:999px; ${occStyle(String(g.occupancy))}">${this.esc(g.occupancy)}</span>
            <span style="font-size:11px; color:var(--text-muted);">~${hm(g.work_minutes || 0)} here</span>
            ${g.address ? `<span style="font-size:10.5px; color:var(--text-faint);">📍 ${this.esc(g.address)}</span>` : ''}
          </div>
          ${(g.wos || []).map((w: any) => woRow(g, w)).join('')}
        </div>`).join('') : `<div style="color:var(--text-muted); font-size:12.5px; padding:6px 0;">Nothing ${this.dayPlanMustDo ? 'must-do ' : ''}due or urgent for this day.</div>`}
    </div>`;
  }
  private dayPlanAsText(): string {
    const p = this.dayPlan; if (!p) return '';
    const hm = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? `${m % 60}m` : ''}` : `${m}m`;
    const scope = (MaintenancePage.fieldRole() === 'maintenance' && this.woAssigneeFilter) ? ` — ${this.woAssigneeFilter}'s jobs only` : '';
    const lines = [`DAY PLAN — ${p.date}${p.must_do_only ? ' (MUST-DO ONLY)' : ''}${scope}`];
    if ((p.route || []).length >= 2) lines.push('Route: ' + p.route.map((r: any, i: number) => `${i + 1}.${r.listing_name} ${r.eta}`).join(' → '));
    for (const g of (p.groups || [])) {
      lines.push('', `${g.listing_name} [${g.occupancy}]${g.address ? ` — ${g.address}` : ''}`);
      for (const w of g.wos) lines.push(`  ${w.deferred ? '~' : (w.must_do ? '*' : '-')} ${w.title}${w.deferred ? ' (DEFERRED — not today)' : ''} (~${hm(w.est_minutes || 45)}${w.vendor_id ? `, vendor: ${this.vendorNameOf(w.vendor_id) || 'vendor'}` : ''}${w.assigned_to ? `, ${w.assigned_to}` : ''})${w.guest_permission?.granted ? ` [guest permission: ${w.guest_permission.mode === 'own_code' ? 'own code' : 'present'}]` : ''}${w.description ? `\n    ${String(w.description).slice(0, 160)}` : ''}`);
    }
    const t = p.totals || {}, mt = p.must_do_totals || {};
    lines.push('', `Totals: ${p.count} WOs · ~${hm(t.work_minutes || 0)} work + ${hm(t.travel_minutes || 0)} drive · ≈${t.staff_needed} techs (must-do: ${mt.wo_count} WOs ≈ ${mt.staff_needed} techs)`);
    if (p.staffing) lines.push(`Staffing: ${p.staffing.staff} people · ${p.staffing.planned_count} planned · ${p.staffing.deferred_count} deferred${p.staffing.must_do_deferred ? ` (${p.staffing.must_do_deferred} MUST-DO deferred!)` : ''}`);
    if (p.gmaps_url) lines.push(`Maps: ${p.gmaps_url}`);
    return lines.join('\n');
  }
  private async loadDayPlan(): Promise<void> {
    const qp = new URLSearchParams();
    if (this.dayPlanDate) qp.set('date', this.dayPlanDate);
    if (this.dayPlanMustDo) qp.set('must_do', '1');
    if (this.dayPlanStaff) qp.set('staff', String(this.dayPlanStaff));
    if (MaintenancePage.fieldRole() === 'maintenance' && this.woAssigneeFilter) qp.set('assignee', this.woAssigneeFilter);
    const qs = qp.toString();
    const j = await this.get(`/maintenance/day-plan${qs ? `?${qs}` : ''}`);
    if (j) { this.dayPlan = j; this.draw(); }
  }
  private static profileName(): string { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.name || '').trim(); } catch { return ''; } }

  // R6 #1 — a vendor/tech login may be linked (Users → roster identity) to an
  // ao_vendors entry whose NAME is what work orders are assigned to. Prefer that
  // roster name for the "My work" scope; fall back to the login-name seed above.
  // R120: the same rule as the cleaner portal (lib/rosterIdentity
  // resolveRosterId) — a link counts only when its row is still active; a link
  // to a removed vendor falls back to the active vendor with the login's email
  // (an unlinked login is matched by email too); nothing matched → the seed.
  // R120 fix round 2 (the #11 class on My Work): this used to run ONCE, on
  // the first field draw — a failed /api/me or /vendors read (or an inspector
  // roster the server couldn't read) kept the login-name seed all session. An
  // answer made without both the login's link and its roster rows is now
  // UNSETTLED and render() (navigation / the 15-min refresh on this cached
  // page) resolves again until it settles. A failed read never downgrades:
  // the link and inspector row the last good /api/me returned are kept. The
  // answer only replaces a scope this page set itself (assigneeAuto) — a
  // person's own Assignee pick (or a deep link's cleared filters) stands.
  private rosterSettled = false;
  private rosterResolving = false;
  private assigneeAuto = false;
  private meLinkRead: { link: any } | null = null;   // the last link a good /api/me returned (link null = not linked)
  private meInspRoster: any = null;                  // the last inspector_roster a good /api/me returned
  private async runRosterIdentity(): Promise<void> {
    if (this.rosterResolving || this.rosterSettled) return;
    this.rosterResolving = true;
    try { await this.resolveRosterIdentity(); } finally { this.rosterResolving = false; }
  }
  private async resolveRosterIdentity(): Promise<void> {
    try {
      const me = await fetch('/api/me').then(r => r.ok ? r.json() : null).catch(() => null);
      if (me && !me.links_unavailable) {
        this.meLinkRead = { link: me.tech_link && me.tech_link.roster_id ? me.tech_link : null };
        if (me.inspector_roster && typeof me.inspector_roster === 'object') this.meInspRoster = me.inspector_roster;
      }
      const linkKnown = !!this.meLinkRead;
      const link = this.meLinkRead ? this.meLinkRead.link : null;
      // Vendor links point into ao_vendors; inspector links into ao_managers.
      const kind: 'vendor' | 'inspector' = link && link.kind === 'inspector' ? 'inspector' : 'vendor';
      let rosterName = '';
      let settled = false;
      if (kind === 'inspector') {
        // R120 review fix (#10): the inspector-roster match is made on the
        // SERVER (/api/me inspector_roster, full rows). GET /inspections/managers
        // strips emails for a field login, so the email fallback done here only
        // ever worked for an admin under View As; view-context carries the same
        // answer, so the real login and View As now agree. null = unknown → the seed.
        const ir = this.meInspRoster;
        rosterName = ir && ir.id ? String(ir.name || '').trim() : '';
        settled = linkKnown && !!ir;
      } else {
        const j = await fetch(`${API}/vendors`).then(r => r.ok ? r.json() : null).catch(() => null);
        // R120 review fix (#8): a vendor list the server couldn't read (roster_unavailable) didn't load
        const list = j && !j.roster_unavailable && Array.isArray(j.vendors) ? j.vendors : null;
        const rows: any[] = (list || []).filter((x: any) => isActiveRow(x));
        let email = '';
        try { email = String(JSON.parse(sessionStorage.getItem('userProfile') || '{}').email || ''); } catch { email = ''; }
        const res = resolveRosterId({ link, kind, rows, email: (me && me.profile && me.profile.email) || email, rowsLoaded: !!list });
        const row = res.id ? rows.find((x: any) => String(x.id) === res.id) : null;
        rosterName = row ? String(row.name || '').trim() : '';
        settled = linkKnown && !!list;
      }
      if (settled) this.rosterSettled = true;
      // Always scope to the roster name — "My work" defaults to MY jobs even
      // when I have none right now; the Assignee select's "Anyone" is the
      // explicit escape hatch to see everything at a property.
      if (rosterName && this.assigneeAuto && rosterName !== this.woAssigneeFilter) {
        this.woAssigneeFilter = rosterName;
        this.draw();
      }
    } catch (_) { /* keep the name-based seed */ }
  }

  private draw(): void {
    const body = document.getElementById('mn-body');
    if (!body) return;
    // R5 #2 — a MAINTENANCE tech gets a field view: just "My work" (their open
    // work orders, auto-scoped) + "Emergencies", not the 10-tab manager cockpit.
    const field = MaintenancePage.fieldRole() === 'maintenance';
    if (field && !this.fieldSeeded) {
      this.fieldSeeded = true;
      this.tab = 'work_orders';
      this.woStatusFilter = '';            // all statuses; done rows still show (R50: list sorts newest-first, nothing is hidden)
      const name = MaintenancePage.profileName();
      // Always scope to my jobs — even when I have none yet the default view is
      // MINE; "Anyone" in the Assignee select shows everything at a property.
      if (name) this.woAssigneeFilter = name;
      this.assigneeAuto = true;
      // A vendor/tech login may be dispatched under their ROSTER name (admin's
      // tech-link), not their login name — resolve it and prefer that scope so
      // "My work" is never empty for a linked vendor. (Async; redraw on arrival;
      // render() resolves again until it settles.)
      void this.runRosterIdentity();
    }
    if (field && this.tab !== 'work_orders' && this.tab !== 'incidents' && this.tab !== 'prop_review') this.tab = 'work_orders';
    // R102 #7/#8: the old PM plans / PM templates / Performance tabs live on as
    // sub-views — anything that still sets them lands in the right place.
    if (this.tab === 'pm_plans' || this.tab === 'templates') { this.recSub = this.tab === 'templates' ? 'templates' : 'plans'; this.tab = 'recurring'; }
    if (this.tab === 'performance') { this.costSub = 'performance'; this.tab = 'costs'; }
    if (this.tab === 'project' && !this.projectLid) this.tab = 'board';
    // Cross-page deep link — another page parked a wo_id in sessionStorage
    // (then dispatched app:navigate → 'maintenance'): land on Work orders with
    // that card expanded, filters cleared so it's guaranteed visible.
    let focusWo = '';
    try {
      focusWo = sessionStorage.getItem('mn_focus_wo') || '';
      if (focusWo) sessionStorage.removeItem('mn_focus_wo');
    } catch (_) { /* sessionStorage unavailable — no deep link */ }
    if (focusWo) {
      this.tab = 'work_orders';
      // reset EVERY list filter — this page is a session-long singleton, so a
      // leftover role/urgent/date filter from an earlier visit would hide the
      // focused WO with the one-shot key already consumed (review fix). R109:
      // one reset (it also clears the search box and the completed-date mode).
      this.resetWoFilters();
      this.expandedWo.add(focusWo);
    }
    const tabs: Array<[string, string]> = field
      ? [['work_orders', 'My work'], ['incidents', 'Emergencies'], ['prop_review', 'Property expenses']]
      : [['board', 'Board'], ['incidents', 'Emergencies'], ['calendar', 'Calendar'], ['work_orders', 'Work orders'], ['assets', 'Assets'], ['recurring', 'Recurring maintenance'], ['vendors', 'Vendors'], ['costs', 'Costs & performance'], ['prop_review', 'Accounting']];
    if (!field && this.tab === 'project' && this.projectLid) {
      const pr = (this.board?.rows || []).find((r: any) => String(r.listing_id) === this.projectLid);
      tabs.splice(1, 0, ['project', `📋 ${pr ? pr.listing_name : 'Project'}`]);
    }
    const activeCount = this.board?.active_count ?? '?';
    body.innerHTML = `
      <style>
        .mn-head { display:flex; align-items:baseline; gap:12px; margin-bottom:6px; flex-wrap:wrap; }
        .mn-head h2 { margin:0; font-size:18px; font-weight:600; letter-spacing:-0.015em; color:var(--text); }
        .mn-head .sub { font-size:12px; color:var(--text-muted); }
        /* R102 #10: segmented tab rail — the board should look as capable as it is */
        .mn-tabs { display:flex; gap:2px; flex-wrap:wrap; margin:14px 0 16px; padding:4px; background:var(--bg-subtle, #f8fafc); border:1px solid var(--border); border-radius:12px; }
        .mn-tab { padding:7px 13px; font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer; border:none; background:none; border-radius:8px; transition:color .15s ease, background .15s ease, box-shadow .15s ease; }
        .mn-tab:hover { color:var(--text); background:rgba(255,255,255,.7); }
        .mn-tab.on { color:var(--accent-hover); background:var(--accent-soft); box-shadow:0 0 0 1px var(--accent-border); }   /* R112 #3: the soft-blue pill */
        .mn-basis { font-size:11px; color:var(--text-faint, #9ca3af); line-height:1.5; margin:0 0 14px; }
        .mn-card { background:var(--card, #fff); border:1px solid var(--border); border-radius:14px; box-shadow:0 1px 2px rgba(15,23,42,.04); }
        table.mn-tbl { width:100%; border-collapse:collapse; table-layout:fixed; }
        table.mn-tbl th { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); text-align:left; padding:11px 14px; border-bottom:1px solid var(--border); white-space:nowrap; background:var(--bg-subtle, #f8fafc); }
        table.mn-tbl td { font-size:12.5px; padding:12px 14px; border-bottom:1px solid var(--border-soft, #f1f5f9); vertical-align:middle; color:var(--text); }
        table.mn-tbl tbody tr { transition:background .12s ease; }
        table.mn-tbl tbody tr:hover { background:var(--bg-subtle); }
        table.mn-tbl tr:last-child td { border-bottom:none; }
        .mn-num { font-variant-numeric:tabular-nums; font-weight:600; }
        .mn-mono { font-family:ui-monospace, monospace; }
        .mn-prop { font-weight:600; color:var(--text); }
        .mn-pill { display:inline-block; font-size:10px; font-weight:600; letter-spacing:0.02em; padding:3px 9px; border-radius:var(--radius-pill, 999px); white-space:nowrap; }
        .mn-role-cleaner { background:#ecfeff; color:#0e7490; }
        .mn-role-inspector { background:#fdf2f8; color:#9d174d; }
        .mn-time { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-top:10px; padding:9px 12px; background:var(--bg-subtle); border:1px solid var(--border); border-radius:8px; }
        .mn-time-h { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-muted); }
        .mn-time-total { font-size:14px; font-weight:700; }
        .mn-time-run { font-size:11px; font-weight:700; color:#b45309; }
        .mn-drill:hover { text-decoration:underline; color:var(--info, #1d4ed8); }
        .mn-stale { color:var(--danger-text, #b91c1c); font-weight:600; }
        .mn-btn { padding:6px 12px; border:1px solid var(--border); border-radius:8px; font-size:11px; font-weight:600; background:#fff; cursor:pointer; color:var(--text); box-shadow:0 1px 1px rgba(15,23,42,.04); transition:background .15s ease, border-color .15s ease, transform .1s ease; }
        .mn-btn:hover { border-color:#cbd5e1; background:var(--bg-subtle); }
        .mn-btn:active { transform:translateY(1px); }
        .mn-btn.primary { background:var(--accent); color:#fff; border-color:var(--accent); }   /* R112 #3: the one action color */
        .mn-btn.primary:hover { background:var(--accent-hover); border-color:var(--accent-hover); }
        .mn-btn.danger { color:var(--danger); border-color:var(--danger-soft, #fecaca); background:#fff; }
        .mn-btn.danger:hover { background:var(--danger-soft, #fee2e2); }
        .mn-btn.sm { padding:4px 10px; font-size:10px; }
        .mn-filters { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:14px; }
        .mn-filters select, .mn-filters input { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:#fff; color:var(--text); transition:border-color .15s ease; }
        .mn-filters select:hover, .mn-filters input:hover { border-color:#cbd5e1; }
        .mn-filters select:focus, .mn-filters input:focus { outline:none; border-color:var(--accent); }
        .mn-filters label { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); }
        .mn-wo { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); overflow:hidden; box-shadow:var(--shadow-card, none); }
        .mn-wo-row { display:grid; grid-template-columns:1.6fr 1.2fr 0.9fr 0.9fr 0.5fr; gap:10px; align-items:center; padding:13px 16px; cursor:pointer; transition:background .12s ease; }
        .mn-wo-row:hover { background:var(--bg-subtle); }
        .mn-wo-title { font-weight:600; font-size:13px; color:var(--text); }
        .mn-wo-meta { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .mn-wo-caret { text-align:right; color:var(--text-muted); font-size:12px; }
        .mn-wo-body { border-top:1px solid var(--border); padding:16px; background:var(--bg-subtle); }
        .mn-item { background:var(--card, #fff); border:1px solid var(--border); border-radius:10px; padding:11px 13px; margin-bottom:8px; }
        .mn-item-head { display:flex; align-items:flex-start; gap:8px; }
        .mn-check { width:20px; height:20px; flex:none; border-radius:6px; border:1px solid var(--border); font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; cursor:pointer; color:var(--text-muted); background:#fff; transition:border-color .12s ease, background .12s ease; }
        .mn-check.done { background:var(--success-soft); border-color:var(--success); color:var(--success-text, #166534); }
        .mn-item-title { font-weight:600; font-size:12.5px; color:var(--text); }
        .mn-item-instr { font-size:11px; color:var(--text-muted); margin-top:1px; }
        .mn-item-note { font-size:11px; color:var(--text); margin-top:4px; font-style:italic; }
        .mn-item-actions { display:flex; gap:10px; margin-top:6px; font-size:10px; }
        .mn-item-actions a { cursor:pointer; color:var(--info); }
        .mn-photos { display:flex; gap:6px; flex-wrap:wrap; margin-top:6px; }
        .mn-photos a img { width:46px; height:46px; object-fit:cover; border-radius:6px; border:1px solid var(--border); }
        .mn-wo-controls { display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; margin-top:12px; padding-top:12px; border-top:1px dashed var(--border); }
        /* R101 #8 (Jesse: "so many drop downs, selections, buttons… make it look/feel more organized"): the controls sit in four labeled groups, the buttons in one action bar */
        .mn-wo-groups { display:grid; grid-template-columns:repeat(auto-fit, minmax(270px, 1fr)); gap:10px; margin-top:12px; }
        .mn-wo-grp { border:1px solid var(--border); border-radius:10px; padding:6px 10px 10px; margin:0; background:var(--card,#fff); display:flex; flex-wrap:wrap; gap:8px 10px; align-items:flex-end; min-width:0; }
        .mn-wo-grp legend { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); padding:0 4px; }
        .mn-wo-grp label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; min-width:0; }
        .mn-wo-grp input, .mn-wo-grp select { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; background:#fff; max-width:100%; }
        .mn-wo-grp input:focus, .mn-wo-grp select:focus { outline:none; border-color:var(--accent); }
        .mn-wo-grp label.mn-wo-check { flex-direction:row; align-items:center; gap:6px; text-transform:none; font-size:12px; letter-spacing:0; cursor:pointer; padding-bottom:6px; }
        /* R110 #2: full-width explainer under Who pays / Billed yet?; amber clash note when WCH pays but it's marked billed */
        .mn-wo-billhint { flex-basis:100%; font-size:11px; color:var(--text-muted); line-height:1.45; margin-top:-2px; }
        .mn-wo-billclash { display:inline-block; color:var(--warning-text, #92400e); background:var(--warning-soft, #fffbeb); border-radius:6px; padding:1px 7px; font-weight:600; }
        .mn-wo-actions { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-top:10px; padding-top:10px; border-top:1px dashed var(--border); }
        .mn-dd { position:relative; display:inline-block; }
        .mn-dd summary { list-style:none; cursor:pointer; }
        .mn-dd summary::-webkit-details-marker { display:none; }
        .mn-dd-menu { position:absolute; top:calc(100% + 4px); left:0; z-index:20; background:#fff; border:1px solid var(--border); border-radius:10px; padding:6px; display:flex; flex-direction:column; gap:4px; box-shadow:0 8px 24px rgba(15,23,42,.12); min-width:180px; }
        .mn-dd-menu .mn-btn { text-align:left; }
        .mn-wo-controls label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-wo-controls input, .mn-wo-controls select, .mn-wo-controls textarea { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; background:#fff; transition:border-color .15s ease; }
        .mn-wo-controls input:focus, .mn-wo-controls select:focus, .mn-wo-controls textarea:focus { outline:none; border-color:var(--accent); }
        .mn-wo-controls textarea { resize:vertical; min-height:36px; width:100%; }
        .mn-tpl { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); overflow:hidden; box-shadow:var(--shadow-card, none); }
        .mn-tpl-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:13px 16px; cursor:pointer; transition:background .12s ease; }
        .mn-tpl-head:hover { background:var(--bg-subtle); }
        .mn-tpl-name { font-weight:600; font-size:13px; color:var(--text); }
        .mn-tpl-meta { font-size:11px; color:var(--text-muted); }
        .mn-tpl-body { border-top:1px solid var(--border); padding:16px; }
        .mn-tpl-item { display:grid; grid-template-columns:1fr 2fr auto; gap:8px; align-items:start; margin-bottom:8px; }
        .mn-tpl-item input, .mn-tpl-item textarea { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; width:100%; box-sizing:border-box; transition:border-color .15s ease; }
        .mn-tpl-item input:focus, .mn-tpl-item textarea:focus { outline:none; border-color:var(--accent); }
        .mn-tpl-item textarea { resize:vertical; min-height:34px; }
        .mn-modal-bg { position:fixed; inset:0; background:rgba(15,23,42,0.45); z-index:200; display:flex; align-items:center; justify-content:center; }
        .mn-modal { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); width:min(620px, 92vw); max-height:86vh; overflow:auto; display:flex; flex-direction:column; padding:22px; gap:14px; box-shadow:var(--shadow-pop, var(--shadow-card, none)); }
        .mn-modal h3 { margin:0; font-size:15px; font-weight:600; color:var(--text); }
        .mn-modal label { display:flex; flex-direction:column; gap:4px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-modal select, .mn-modal input, .mn-modal textarea { padding:8px 11px; border:1px solid var(--border); border-radius:8px; font-size:13px; font-family:inherit; transition:border-color .15s ease; }
        .mn-modal select:focus, .mn-modal input:focus, .mn-modal textarea:focus { outline:none; border-color:var(--accent); }
        .mn-modal textarea { font-family:ui-monospace, monospace; font-size:11.5px; min-height:240px; resize:vertical; }
        .mn-modal-actions { display:flex; gap:8px; justify-content:flex-end; }
        .mn-ast { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); overflow:hidden; box-shadow:var(--shadow-card, none); }
        .mn-ast-row { display:grid; grid-template-columns:1.4fr 0.8fr 1.4fr 1fr 0.4fr; gap:10px; align-items:center; padding:13px 16px; cursor:pointer; transition:background .12s ease; }
        .mn-ast-row:hover { background:var(--bg-subtle); }
        .mn-ast-name { font-weight:600; font-size:13px; color:var(--text); }
        .mn-ast-sub { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .mn-ast-cat { display:inline-block; font-size:10px; font-weight:600; letter-spacing:0.02em; padding:3px 9px; border-radius:var(--radius-pill, 999px); background:var(--bg-subtle); color:var(--text-muted); white-space:nowrap; }
        .mn-ast-caret { text-align:right; color:var(--text-muted); font-size:12px; }
        .mn-ast-body { border-top:1px solid var(--border); padding:16px; background:var(--bg-subtle); }
        .mn-ast-hist { background:var(--card, #fff); border:1px solid var(--border); border-radius:10px; padding:10px 12px; margin-bottom:7px; display:flex; align-items:center; gap:8px; flex-wrap:wrap; font-size:12px; }
        .mn-ast-form { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); padding:16px; margin-bottom:14px; display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; box-shadow:var(--shadow-card, none); }
        .mn-ast-form label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-ast-form input, .mn-ast-form select { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; transition:border-color .15s ease; }
        .mn-ast-form input:focus, .mn-ast-form select:focus { outline:none; border-color:var(--accent); }
        .mn-ast-form .full { grid-column:1 / -1; }
        .mn-ast-form-actions { grid-column:1 / -1; display:flex; gap:8px; }
        .mn-seg { display:inline-flex; border:1px solid var(--border); border-radius:8px; overflow:hidden; }
        .mn-seg button { padding:6px 13px; font-size:11px; font-weight:600; background:#fff; border:none; border-right:1px solid var(--border); cursor:pointer; color:var(--text-muted); transition:background .12s ease, color .12s ease; }
        .mn-seg button:last-child { border-right:none; }
        .mn-seg button:hover { background:var(--bg-subtle); color:var(--text); }
        .mn-seg button.on { background:var(--accent-soft); color:var(--accent-hover); }   /* R112 #3 */
        .mn-seg button.on:hover { background:var(--accent-soft); color:var(--accent-hover); }
        .mn-cal { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); padding:14px 16px; display:grid; grid-template-columns:1.4fr 1.1fr 2fr; gap:14px; align-items:start; box-shadow:var(--shadow-card, none); }
        .mn-cal-prop { font-weight:600; font-size:13px; color:var(--text); }
        .mn-cal-occ { font-size:11px; margin-top:3px; }
        .mn-dot { display:inline-block; width:8px; height:8px; border-radius:999px; margin-right:5px; vertical-align:middle; }
        .mn-dot.occ { background:var(--danger, #dc2626); }
        .mn-dot.vac { background:var(--success, #16a34a); }
        .mn-cal-sub { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .mn-chip { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:600; letter-spacing:0.02em; padding:3px 9px; border-radius:var(--radius-pill, 999px); background:var(--bg-subtle); color:var(--text-muted); margin:2px 4px 2px 0; white-space:nowrap; }
        .mn-chip.risk { background:var(--danger-soft, #fee2e2); color:var(--danger-text, #b91c1c); }
        .mn-chip.due { background:var(--warning-soft); color:var(--warning-text); }
        .mn-cal-label { font-size:9.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-faint, #9ca3af); margin-bottom:4px; }
        .mn-pm { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); padding:13px 16px; box-shadow:var(--shadow-card, none); }
        .mn-field { margin-top:12px; border:1px solid var(--border); border-radius:10px; background:var(--bg-subtle); padding:12px 14px; }
        .mn-field-h { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); margin-bottom:7px; }
        .mn-field-loading { font-size:12px; color:var(--text-muted); }
        .mn-field-guest { font-size:12px; font-weight:600; color:var(--warning-text); background:var(--warning-soft); border-radius:8px; padding:7px 10px; margin-bottom:9px; }
        .mn-field-line { display:flex; gap:10px; font-size:12px; padding:4px 0; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .mn-field-line:last-child { border-bottom:none; }
        .mn-field-k { flex:0 0 96px; font-weight:600; color:var(--text-muted); }
        .mn-field-v { flex:1; min-width:0; word-break:break-word; font-family:ui-monospace, monospace; }
        .mn-pm-head { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .mn-pm-name { font-weight:600; font-size:13px; color:var(--text); }
        .mn-pm-meta { font-size:11px; color:var(--text-muted); }
        .mn-pm-form { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); padding:16px; margin-bottom:14px; display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; box-shadow:var(--shadow-card, none); }
        .mn-pm-form label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-pm-form input, .mn-pm-form select { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; transition:border-color .15s ease; }
        .mn-pm-form input:focus, .mn-pm-form select:focus { outline:none; border-color:var(--accent); }
        .mn-pm-form .full { grid-column:1 / -1; }
        .mn-pm-form-actions { grid-column:1 / -1; display:flex; gap:8px; }
        .mn-honest { font-size:11px; color:var(--text-faint, #9ca3af); font-style:italic; line-height:1.5; margin:0 0 14px; padding:9px 12px; background:var(--bg-subtle); border-left:3px solid var(--border); border-radius:0 8px 8px 0; }
        .mn-ven { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); overflow:hidden; box-shadow:var(--shadow-card, none); }
        .mn-ven-row { display:grid; grid-template-columns:1.4fr 1.4fr 1fr 0.6fr 0.5fr 0.7fr 0.4fr; gap:10px; align-items:center; padding:13px 16px; cursor:pointer; transition:background .12s ease; }
        .mn-ven-row:hover { background:var(--bg-subtle); }
        .mn-ven-name { font-weight:600; font-size:13px; color:var(--text); }
        .mn-ven-caret { text-align:right; color:var(--text-muted); font-size:12px; }
        .mn-ven-body { border-top:1px solid var(--border); padding:16px; background:var(--bg-subtle); }
        .mn-ven-form { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); padding:16px; margin-bottom:14px; display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; box-shadow:var(--shadow-card, none); }
        .mn-ven-form label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-ven-form input, .mn-ven-form select { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; transition:border-color .15s ease; }
        .mn-ven-form input:focus, .mn-ven-form select:focus { outline:none; border-color:var(--accent); }
        .mn-ven-form .full { grid-column:1 / -1; }
        .mn-ven-form .chk { flex-direction:row; align-items:center; gap:6px; text-transform:none; }
        .mn-ven-form .chk input { width:auto; }
        .mn-ven-form-actions { grid-column:1 / -1; display:flex; gap:8px; }
        .mn-cost-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; margin-bottom:16px; }
        .mn-cost-card { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); padding:16px 18px; box-shadow:var(--shadow-card, none); }
        .mn-cost-label { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .mn-cost-val { font-size:22px; font-weight:700; font-variant-numeric:tabular-nums; letter-spacing:-0.01em; color:var(--text); margin-top:4px; }
        .mn-cost-sub { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .mn-cost-sec { margin-bottom:18px; }
        .mn-cost-sec h4 { margin:0 0 8px; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); }
        .mn-inc { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:10px; background:var(--card, #fff); overflow:hidden; border-left:4px solid var(--border); box-shadow:var(--shadow-card, none); }
        .mn-inc.sev-emergency { border-left-color:var(--danger, #dc2626); }
        .mn-inc.sev-urgent { border-left-color:var(--warning-text, #b45309); }
        .mn-inc.sev-minor { border-left-color:var(--text-muted, #9ca3af); }
        .mn-inc-row { display:grid; grid-template-columns:minmax(0, 3fr) 1fr 0.8fr 0.8fr 0.3fr; gap:10px; align-items:center; padding:13px 16px; cursor:pointer; transition:background .12s ease; }   /* R109 #g: the report text gets the room */
        .mn-inc-row:hover { background:var(--bg-subtle); }
        .mn-inc-title { font-weight:600; font-size:13px; color:var(--text); }
        .mn-inc-meta { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .mn-inc-clock { font-size:11px; color:var(--text-muted); font-variant-numeric:tabular-nums; }
        .mn-inc-caret { text-align:right; color:var(--text-muted); font-size:12px; }
        .mn-inc-body { border-top:1px solid var(--border); padding:16px; background:var(--bg-subtle); }
        .mn-inc-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .mn-inc-grid .full { grid-column:1 / -1; }
        .mn-inc-panel { background:var(--card, #fff); border:1px solid var(--border); border-radius:10px; padding:12px 14px; }
        .mn-inc-panel h5 { margin:0 0 8px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .mn-inc-tl { list-style:none; margin:0; padding:0; }
        .mn-inc-tl li { display:flex; gap:8px; align-items:baseline; font-size:12px; padding:3px 0; }
        .mn-inc-tl li .dot { width:8px; height:8px; border-radius:999px; flex:none; }
        .mn-inc-tl li.on .dot { background:var(--success, #16a34a); }
        .mn-inc-tl li.off .dot { background:var(--border, #e5e7eb); }
        .mn-inc-tl li .lab { font-weight:600; min-width:96px; }
        .mn-inc-tl li .ts { color:var(--text-muted); font-variant-numeric:tabular-nums; }
        .mn-inc-steps { margin:0; padding-left:18px; font-size:12px; }
        .mn-inc-steps li { margin-bottom:4px; }
        .mn-inc-kv { font-size:12px; line-height:1.55; }
        .mn-inc-kv b { font-weight:600; }
        .mn-inc-kv .k { color:var(--text-muted); display:inline-block; min-width:104px; }
        .mn-inc-safety { background:var(--danger-soft, #fee2e2); color:var(--danger-text, #b91c1c); border:1px solid var(--danger-soft, #fecaca); border-radius:10px; padding:10px 12px; font-size:12px; font-weight:600; margin-bottom:11px; }
        .mn-inc-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:flex-end; margin-top:4px; }
        .mn-inc-actions label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-inc-actions input, .mn-inc-actions select { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; transition:border-color .15s ease; }
        .mn-inc-actions input:focus, .mn-inc-actions select:focus { outline:none; border-color:var(--accent); }
        .mn-inc-form { background:var(--card, #fff); border:1px solid var(--border); border-radius:var(--radius); padding:16px; margin-bottom:14px; display:grid; grid-template-columns:repeat(3, 1fr); gap:12px; box-shadow:var(--shadow-card, none); }
        .mn-inc-form label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .mn-inc-form input, .mn-inc-form select, .mn-inc-form textarea { padding:6px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; font-family:inherit; transition:border-color .15s ease; }
        .mn-inc-form input:focus, .mn-inc-form select:focus, .mn-inc-form textarea:focus { outline:none; border-color:var(--accent); }
        .mn-inc-form textarea { resize:vertical; min-height:40px; }
        .mn-inc-form .full { grid-column:1 / -1; }
        .mn-inc-form-actions { grid-column:1 / -1; display:flex; gap:8px; }
        /* ── R102 board / project / calendar ─────────────────────────── */
        /* R112: the board in the HostBuddy idiom — shared .kpi-card / .card / .chip / .pill / .btn, token-driven */
        .mn-board-kpis { display:grid; grid-template-columns:repeat(6, minmax(0, 1fr)); gap:12px; margin-bottom:14px; }
        .mn-board-kpis .kpi-card { padding:14px 16px; min-width:0; position:relative; }
        .mn-board-kpis .kpi-card-value { font-size:26px; margin-top:10px; }
        .mn-board-kpis .kpi-card-cap { margin-top:4px; line-height:1.35; }
        .mn-board-kpis .kpi-card-cap .mn-link { display:inline-block; margin-top:3px; font-size:11.5px; text-decoration:none; }
        .mn-board-kpis .kpi-card-cap .mn-link:hover { text-decoration:underline; }
        .mn-board-kpis .kpi-card.on { border-color:var(--accent); box-shadow:0 0 0 2px var(--accent-border), var(--shadow-card); }
        .mn-board-kpis .kpi-card:focus-visible { outline:none; box-shadow:var(--focus-ring); }
        .mn-card-open { overflow:visible; }                                   /* popovers (⋯ menu, expenses range, WO card menus) escape the card */
        .mn-card-open > .card-head { border-radius:var(--radius-lg) var(--radius-lg) 0 0; align-items:center; }
        .mn-board-filters { display:flex; gap:6px; align-items:center; flex-wrap:wrap; justify-content:flex-end; margin-left:auto; }
        .mn-board-filters .chip { padding:5px 11px; font-size:11.5px; }
        .mn-board-filters .mn-search { min-width:190px; border-radius:var(--radius-pill); padding:6px 12px; }
        table.mn-board thead th { position:sticky; top:var(--mn-sticky-top, 0px); z-index:3; }
        table.mn-board th:first-child { position:sticky; left:0; z-index:4; }
        table.mn-board tbody tr:not(.mn-board-x) td:first-child { position:sticky; left:0; z-index:2; background:var(--card); }
        table.mn-board tbody tr:not(.mn-board-x):hover td:first-child { background:var(--bg-subtle); }
        table.mn-board td { padding:10px 12px; }
        table.mn-board td.mn-acts-cell { padding-right:14px; }                /* the cell stays a table-cell (borders line up); the flex row is inside */
        table.mn-board .mn-acts { gap:5px; align-items:center; }
        table.mn-board .mn-acts .btn-primary { white-space:nowrap; }
        .mn-ibtn { width:28px; height:28px; padding:0; border-radius:var(--radius-sm); border:1px solid var(--border); background:var(--card); color:var(--text-muted); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:14px; font-weight:600; line-height:1; font-family:inherit; transition:border-color .12s ease, color .12s ease, background .12s ease; }
        .mn-ibtn:hover { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }
        .mn-ibtn svg { width:15px; height:15px; }
        .mn-dd[open] > summary.mn-ibtn { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }
        .mn-pm-when { white-space:nowrap; }
        .mn-pm-when .pill { margin-left:4px; }
        .mn-cell-pills { display:flex; flex-wrap:wrap; gap:4px; }
        .mn-board-x .btn { white-space:nowrap; }
        @media (max-width: 1180px) { .mn-board-kpis { grid-template-columns:repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 900px) {
          /* field phones: the same rows, stacked as property cards — one render path, no second template */
          .mn-board-kpis { grid-template-columns:repeat(2, minmax(0, 1fr)); gap:10px; }
          .mn-board-kpis .kpi-card { padding:12px 14px; }
          .mn-board-kpis .kpi-card-value { font-size:22px; }
          .mn-board-filters { justify-content:flex-start; margin-left:0; width:100%; }
          .mn-board-filters .mn-search { width:100%; min-width:0; }
          table.mn-board, table.mn-board tbody, table.mn-board tr, table.mn-board td { display:block; width:100%; box-sizing:border-box; }
          table.mn-board thead { display:none; }
          table.mn-board tbody tr { border-bottom:1px solid var(--border); padding:10px 16px 12px; }
          table.mn-board tbody tr:last-child { border-bottom:none; }
          table.mn-board tbody tr:hover, table.mn-board tbody tr:not(.mn-board-x):hover td:first-child { background:transparent; }
          table.mn-board td, table.mn-board tbody tr:not(.mn-board-x) td:first-child { display:flex; align-items:center; gap:10px; padding:4px 0; border:none; position:static; background:transparent; box-shadow:none; }
          table.mn-board td[data-label]::before { content:attr(data-label); flex:0 0 88px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); }
          table.mn-board td.mn-prop, table.mn-board tbody tr:not(.mn-board-x) td.mn-prop { display:block; font-size:14px; padding:0 0 6px; }
          table.mn-board td.mn-prop .mn-ellip { white-space:normal; overflow:visible; }
          table.mn-board td.mn-acts-cell { padding-top:8px; }
          table.mn-board .mn-acts { justify-content:flex-start; flex-wrap:wrap; }
          table.mn-board tbody tr.mn-row-emg { box-shadow:inset 3px 0 0 var(--danger,#dc2626); }
          table.mn-board tbody tr.mn-row-emg.urgent { box-shadow:inset 3px 0 0 #f59e0b; }
          table.mn-board tbody tr.mn-row-emg.minor { box-shadow:inset 3px 0 0 #94a3b8; }
          table.mn-board tr.mn-board-x td { display:block; padding:8px 0 0 !important; border-bottom:none; background:transparent; }
          .mn-exp-pop { right:auto; left:0; min-width:0; width:100%; }
        }
        .mn-search { padding:7px 11px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:#fff; min-width:220px; font-family:inherit; }
        .mn-search:focus { outline:none; border-color:var(--accent); }
        .mn-sel { padding:7px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:#fff; font-family:inherit; }
        .mn-board-card { margin-bottom:12px; }
        .mn-ellip { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .mn-muted { color:var(--text-faint,#9ca3af); }
        .mn-sub { font-size:11px; color:var(--text-muted); font-weight:400; }
        .mn-sub.warn, .mn-pj-meta .warn { color:#b45309; font-weight:600; }
        .mn-row-emg td:first-child { box-shadow:inset 3px 0 0 var(--danger,#dc2626); }
        .mn-row-emg.urgent td:first-child { box-shadow:inset 3px 0 0 #f59e0b; }
        .mn-row-emg.minor td:first-child { box-shadow:inset 3px 0 0 #94a3b8; }
        /* R105 #2 (Jesse: "the formatting on the maintenance board is not looking right"): the chip and its title teaser stay INSIDE the emergencies cell — the teaser used to run under the count column */
        /* R112: the chip is a shared .pill (severity tone) with the newest title under it, still inside the cell */
        .mn-emg-wrap { min-width:0; max-width:100%; }
        .mn-emg { cursor:pointer; border:none; font-family:inherit; max-width:100%; overflow:hidden; text-overflow:ellipsis; }
        .mn-emg:hover { filter:brightness(.96); }
        .mn-emg-t { font-size:10.5px; color:var(--text-muted); margin-top:3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:100%; }
        /* R105 #3: internal WO comments */
        .mn-cmts { margin:10px 0 12px; padding:10px 12px; border:1px solid var(--border); border-radius:10px; background:var(--card,#fff); }
        .mn-cmts-h { font-size:11px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:.04em; display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:6px; }
        .mn-cmts-h .mn-sub { text-transform:none; letter-spacing:0; font-weight:400; }
        .mn-cmt { border-left:2px solid var(--border); padding:3px 9px; margin:4px 0; }
        .mn-cmt-h { font-size:11px; color:var(--text-muted); }
        .mn-cmt-t { font-size:12.5px; line-height:1.45; white-space:pre-wrap; }
        .mn-cmt-compose { display:flex; gap:6px; align-items:flex-start; margin-top:6px; }
        .mn-cmt-compose textarea { flex:1; min-height:32px; padding:6px 9px; border:1px solid var(--border); border-radius:8px; font-size:12.5px; font-family:inherit; resize:vertical; }
        .mn-metric { display:inline-block; font-size:10.5px; font-weight:600; padding:2px 7px; border-radius:999px; background:var(--bg-subtle); color:var(--text-muted); margin:1px 4px 1px 0; white-space:nowrap; }
        .mn-metric.ok { background:#eaf2ff; color:#3730a3; }
        .mn-metric.warn { background:#fffbeb; color:#92400e; }
        .mn-metric.danger { background:#fee2e2; color:#b91c1c; }
        .mn-link { background:none; border:none; padding:0; font:inherit; font-weight:600; color:var(--info,#1d4ed8); cursor:pointer; text-decoration:underline dotted; }
        .mn-acts { display:flex; flex-wrap:wrap; gap:4px; justify-content:flex-end; position:relative; }
        .mn-dd-menu.right { left:auto; right:0; }
        .mn-exp-pop { position:absolute; right:0; top:calc(100% + 4px); z-index:30; background:#fff; border:1px solid var(--border); border-radius:10px; padding:10px; display:flex; gap:8px; align-items:flex-end; flex-wrap:wrap; box-shadow:0 8px 24px rgba(15,23,42,.12); min-width:340px; text-align:left; }
        .mn-exp-pop label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .mn-exp-pop label.chk { flex-direction:row; align-items:center; text-transform:none; font-size:11px; padding-bottom:6px; }
        .mn-exp-pop input[type=date] { padding:5px 8px; border:1px solid var(--border); border-radius:7px; font-size:12px; }
        /* owner-portal queue */
        /* R112 #5: a .card with a .card-head; each section is a subtle strip */
        .mn-queue { margin-bottom:12px; }
        .mn-q-body { padding:4px var(--space-5) 14px; }
        .mn-q-sec { margin-top:10px; }
        .mn-q-h { display:flex; gap:8px; align-items:baseline; flex-wrap:wrap; font-size:12px; font-weight:700; color:var(--text); margin:0 calc(-1 * var(--space-5)); padding:7px var(--space-5); background:var(--bg-subtle); border-top:1px solid var(--border-soft,#f1f5f9); border-bottom:1px solid var(--border-soft,#f1f5f9); }
        .mn-q-row { display:flex; gap:10px; align-items:center; justify-content:space-between; padding:6px 0; border-top:1px solid var(--border-soft,#f1f5f9); flex-wrap:wrap; }
        .mn-q-main { display:flex; flex-direction:column; gap:1px; min-width:0; flex:1 1 300px; }
        .mn-q-title { font-size:12.5px; font-weight:600; cursor:pointer; }
        .mn-q-title:hover { text-decoration:underline; }
        .mn-q-acts { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
        /* project (punch list) */
        .mn-pj-head { display:flex; gap:12px; align-items:center; flex-wrap:wrap; padding:14px 18px; margin-bottom:12px; }
        .mn-pj-head h3 { margin:0; font-size:17px; font-weight:700; letter-spacing:-0.01em; }
        .mn-pj-chips { display:flex; gap:6px; flex-wrap:wrap; }
        .mn-pj-acts { display:flex; gap:6px; flex-wrap:wrap; margin-left:auto; }
        .mn-pj-sec { margin-bottom:12px; overflow:visible; }
        .mn-pj-sech { display:flex; gap:8px; align-items:baseline; padding:10px 16px; border-bottom:1px solid var(--border); font-size:12.5px; font-weight:700; border-radius:14px 14px 0 0; }
        .mn-pj-sec.emg .mn-pj-sech { background:#fef2f2; color:#b91c1c; }
        .mn-pj-sec.wait .mn-pj-sech { background:#fffbeb; color:#92400e; }
        .mn-pj-sec.sched .mn-pj-sech { background:#eaf2ff; color:#3730a3; }
        .mn-pj-sec.back .mn-pj-sech { background:var(--bg-subtle); color:var(--text); }
        .mn-pj-sec.done .mn-pj-sech { background:#ecfdf5; color:#065f46; }
        .mn-pj-sec.rec .mn-pj-sech { background:var(--bg-subtle); color:var(--text); }
        .mn-pj-row { display:flex; gap:12px; align-items:center; justify-content:space-between; padding:9px 16px; border-bottom:1px solid var(--border-soft,#f1f5f9); flex-wrap:wrap; }
        .mn-pj-row:last-child { border-bottom:none; }
        .mn-pj-row.overdue { background:#fff7f7; }
        .mn-pj-main { min-width:0; flex:1 1 320px; }
        .mn-pj-title { font-size:13px; font-weight:600; cursor:pointer; }
        .mn-pj-title:hover { text-decoration:underline; }
        .mn-pj-meta { font-size:11px; color:var(--text-muted); margin-top:2px; }
        .mn-pj-book { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
        .mn-pj-book input, .mn-pj-book select { padding:5px 8px; border:1px solid var(--border); border-radius:7px; font-size:11.5px; font-family:inherit; background:#fff; }
        .mn-pj-book input[type=text] { width:120px; }
        .mn-pj-empty { padding:12px 16px; font-size:12px; color:var(--text-faint,#9ca3af); }
        .mn-pj-caret { color:var(--text-muted); font-size:11px; width:10px; display:inline-block; }
        .mn-pj-row.open { background:var(--bg-subtle,#f8fafc); border-bottom:none; }
        .mn-pj-card { padding:0 12px 12px; background:var(--bg-subtle,#f8fafc); border-bottom:1px solid var(--border-soft,#f1f5f9); }
        .mn-pj-card .mn-wo { margin:0; }
        .mn-pj-card > .mn-wo > .mn-wo-row { background:var(--bg-subtle,#f8fafc); padding:8px 16px; }   /* header stays: rename ✏️, status, chips (review R103 #24) */
        .mn-queue .mn-pj-card { margin:0 calc(-1 * var(--space-5)); padding:0 var(--space-5) 12px; }
        .mn-board-x td { background:var(--bg-subtle,#f8fafc); padding:10px 14px 12px !important; border-bottom:2px solid var(--border); }
        .mn-board-xh { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:8px; font-size:12.5px; }
        .mn-row-open td { border-bottom:none; }
        /* month calendar */
        .mn-cal-head { display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
        .mn-cal-nav { display:flex; gap:6px; align-items:center; }
        .mn-cal-nav b { min-width:140px; text-align:center; font-size:14px; }
        .mn-cal-wrap { display:grid; grid-template-columns:minmax(0, 1fr) 320px; gap:14px; align-items:start; }
        @media (max-width: 1100px) { .mn-cal-wrap { grid-template-columns:1fr; } }
        .mn-cal-grid { display:grid; grid-template-columns:repeat(7, minmax(0, 1fr)); gap:4px; }
        .mn-cal-dow { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); text-align:center; padding:4px 0; }
        .mn-cal-day { min-height:104px; background:#fff; border:1px solid var(--border); border-radius:10px; padding:6px; display:flex; flex-direction:column; gap:3px; cursor:pointer; transition:box-shadow .12s ease, border-color .12s ease; min-width:0; }
        .mn-cal-day:hover { border-color:#cbd5e1; box-shadow:0 1px 3px rgba(15,23,42,.06); }
        .mn-cal-day.pad { background:transparent; border-color:transparent; cursor:default; }
        .mn-cal-day.past { background:var(--bg-subtle,#f8fafc); }
        .mn-cal-day.today { border-color:var(--accent); box-shadow:inset 0 0 0 1px var(--accent); }   /* R112 #3 */
        .mn-cal-day.sel { border-color:var(--info,#1d4ed8); box-shadow:inset 0 0 0 1px var(--info,#1d4ed8); }
        .mn-cal-day.droppable { border-style:dashed; border-color:#93c5fd; }
        .mn-cal-day.over { background:#eaf2ff; border-color:var(--info,#1d4ed8); }
        .mn-cal-dn { display:flex; gap:4px; align-items:center; font-size:11.5px; font-weight:700; color:var(--text-muted); }
        .mn-cal-day.today .mn-cal-dn > span:first-child { background:var(--accent); color:#fff; border-radius:999px; padding:0 6px; }
        .mn-cal-turn { font-size:9.5px; font-weight:600; color:#1d4ed8; background:#dbeafe; border-radius:999px; padding:0 5px; }
        .mn-cal-turn.in { color:#be185d; background:#fce7f3; }
        .mn-cal-it { font-size:10.5px; line-height:1.25; padding:2px 6px; border-radius:6px; background:var(--bg-subtle); color:var(--text); border-left:3px solid #94a3b8; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:pointer; }
        .mn-cal-it i { color:var(--text-muted); font-style:normal; }
        .mn-cal-it.k-scheduled { background:var(--accent-soft); border-left-color:var(--accent); }
        .mn-cal-it.k-due { background:#fffbeb; border-left-color:#f59e0b; }
        .mn-cal-it.k-pm { background:#ecfeff; border-left-color:#0e7490; }
        .mn-cal-it.k-done { background:#ecfdf5; border-left-color:#16a34a; color:var(--text-muted); text-decoration:line-through; }
        .mn-cal-it.p-emergency { border-left-color:var(--danger,#dc2626); background:#fee2e2; }
        .mn-cal-it.p-urgent:not(.k-done) { border-left-color:#f59e0b; }
        .mn-cal-it:hover { filter:brightness(.96); }
        .mn-cal-more { font-size:10px; color:var(--info,#1d4ed8); font-weight:600; cursor:pointer; }
        .mn-cal-rail { background:var(--card,#fff); border:1px solid var(--border); border-radius:14px; padding:12px 14px; position:sticky; top:12px; max-height:calc(100vh - 40px); overflow:auto; }
        .mn-cal-picking { background:#eaf2ff; color:#3730a3; border-radius:10px; padding:8px 10px; font-size:12px; margin-bottom:10px; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .mn-cal-dayd { border-bottom:1px dashed var(--border); padding-bottom:10px; margin-bottom:10px; }
        .mn-cal-rrow { display:flex; flex-direction:column; gap:4px; padding:5px 0; border-top:1px solid var(--border-soft,#f1f5f9); }
        .mn-cal-rrow .mn-cal-it { white-space:normal; }
        .mn-cal-un { display:flex; flex-direction:column; margin-top:8px; }
        .mn-cal-urow { padding:7px 0; border-top:1px solid var(--border-soft,#f1f5f9); display:flex; flex-direction:column; gap:5px; }
        .mn-cal-urow.on { background:#eaf2ff; margin:0 -8px; padding:7px 8px; border-radius:8px; }
        .mn-cal-umain { cursor:grab; font-size:12px; }
        .mn-cal-umain:active { cursor:grabbing; }
        .mn-cal-rail input[type=date] { padding:4px 6px; border:1px solid var(--border); border-radius:7px; font-size:11px; }
        .mn-cal-who select { max-width:130px; font-size:11px; padding:3px 6px; border:1px solid var(--border); border-radius:7px; background:#fff; font-family:inherit; }
        .mn-cal-who input[type=text] { width:96px; font-size:11px; padding:3px 6px; border:1px solid var(--border); border-radius:7px; font-family:inherit; }
        .mn-cal-rail .mn-search { width:100%; box-sizing:border-box; min-width:0; margin-top:4px; }
        /* R115 maint: the phone agenda (≤640px) replaces the 7-column grid */
        .mn-cal-main { min-width:0; }
        .mn-cal-agenda { display:none; flex-direction:column; gap:6px; }
        .mn-cal-ag-day { background:var(--card,#fff); border:1px solid var(--border); border-radius:10px; padding:8px 10px; display:flex; flex-direction:column; gap:4px; cursor:pointer; min-height:40px; box-sizing:border-box; }
        .mn-cal-ag-day.past { background:var(--bg-subtle,#f8fafc); }
        .mn-cal-ag-day.today { border-color:var(--accent); box-shadow:inset 0 0 0 1px var(--accent); }
        .mn-cal-ag-day.sel { border-color:var(--info,#1d4ed8); box-shadow:inset 0 0 0 1px var(--info,#1d4ed8); }
        .mn-cal-ag-day.droppable { border-style:dashed; border-color:var(--accent-border); }
        .mn-cal-ag-h { display:flex; gap:6px; align-items:center; font-size:12.5px; min-height:24px; }
        .mn-cal-ag-today { font-size:10px; font-weight:700; color:#fff; background:var(--accent); border-radius:999px; padding:0 7px; }
        .mn-cal-ag-day .mn-cal-it { white-space:normal; font-size:12px; padding:6px 8px; }
        .mn-cal-ag-pick { position:sticky; top:0; z-index:2; margin-bottom:4px; }
        @media (max-width: 640px) {
          .mn-cal-grid { display:none; }
          .mn-cal-agenda { display:flex; }
          .mn-cal-nav b { min-width:0; }
          .mn-cal-head .mn-sel { max-width:100%; }
          .mn-cal-rail { position:static; max-height:none; }
          .mn-cal-rail input[type=date], .mn-cal-who select, .mn-cal-who input[type=text], .mn-cal-rail .mn-search { font-size:16px; }
          .mn-cal-rail .mn-btn.sm, .mn-cal-agenda .mn-btn.sm { min-height:36px; }
        }
        /* day plan staffing */
        .mn-dp-staff { font-size:12.5px; margin:6px 0 8px; padding:8px 12px; border-radius:10px; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .mn-dp-staff.ok { background:#ecfdf5; color:#065f46; }
        .mn-dp-staff.warn { background:#fffbeb; color:#92400e; }
        .mn-dp-staff.bad { background:#fee2e2; color:#b91c1c; }
        .mn-dp-deferred { opacity:.55; }
        .mn-sub-seg { display:flex; gap:10px; align-items:center; margin-bottom:12px; }
        /* R109 #g: the whole incident report wraps on the card (a very long one clamps until the card is opened) */
        .mn-inc-title { overflow-wrap:anywhere; line-height:1.4; }
        .mn-inc-text { white-space:pre-wrap; }
        .mn-inc-desc { font-size:12px; color:var(--text); margin-top:3px; white-space:pre-wrap; overflow-wrap:anywhere; line-height:1.4; }
        .mn-inc-title.clamp, .mn-inc-desc.clamp { display:-webkit-box; -webkit-box-orient:vertical; overflow:hidden; }
        .mn-inc-title.clamp { -webkit-line-clamp:6; }
        .mn-inc-desc.clamp { -webkit-line-clamp:4; }
        /* R109 #d: work-order search */
        .mn-wo-qrow { display:flex; gap:8px; align-items:center; flex:1 1 100%; }
        .mn-filters input.mn-wo-q { flex:1; min-width:0; font-size:13px; padding:9px 12px; }
        .mn-wo-qhelp { flex:none; width:22px; height:22px; border-radius:999px; border:1px solid var(--border); display:inline-flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; color:var(--text-muted); cursor:help; background:#fff; }
        .mn-wo-qinfo { font-size:12px; color:var(--text-muted); margin:-6px 0 12px; display:flex; gap:6px 10px; align-items:center; flex-wrap:wrap; }
        .mn-wo-qinfo b { color:var(--text); }
        .mn-wo-qinfo .loose { color:#92400e; font-weight:600; }
        mark.mn-hl { background:#fef08a; color:#111827; border-radius:2px; padding:0 1px; }
        .mn-wo-found { font-size:11px; color:var(--text-muted); margin-top:3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        /* R109 #c: the project's "Done" range */
        .mn-pj-donef { display:flex; gap:8px; align-items:center; flex-wrap:wrap; padding:8px 16px; border-bottom:1px solid var(--border-soft,#f1f5f9); }
        .mn-pj-donef input[type=date] { padding:4px 7px; border:1px solid var(--border); border-radius:7px; font-size:11.5px; font-family:inherit; background:#fff; }
        .mn-seg.sm button { padding:4px 10px; font-size:10.5px; }
      </style>
      <div class="dash-head">
        <h1>${field ? 'My work' : 'Maintenance'}</h1>
        <p>${field ? `${this.esc(MaintenancePage.profileName() || 'Maintenance')} · your open work orders — start a job, log time, attach photos, mark it done` : `preventive maintenance &amp; work orders — ${this.esc(activeCount)} active listings (status='active')`}</p>
      </div>
      ${field ? fieldViewSwitcherHtml() : ''}
      <div class="mn-tabs">
        ${tabs.map(([k, label]) => `<button class="mn-tab ${this.tab === k ? 'on' : ''}" data-mn-tab="${k}">${label}</button>`).join('')}
      </div>
      <div id="mn-tab-body">
        ${this.tab === 'board' ? this.renderBoard()
          : this.tab === 'incidents' ? this.renderIncidents()
          : this.tab === 'prop_review' ? this.renderPropReview()
          : this.tab === 'calendar' ? this.renderCalendar()
          : this.tab === 'project' ? this.renderProject()
          : this.tab === 'work_orders' ? this.renderWorkOrders()
          : this.tab === 'assets' ? this.renderAssets()
          : this.tab === 'recurring' ? this.renderRecurring()
          : this.tab === 'vendors' ? this.renderVendors()
          : this.tab === 'costs' ? this.renderCostsPerf()
          : this.renderBoard()}
      </div>
      <input type="file" id="mn-photo-file" accept="${EVIDENCE_ACCEPT}" multiple style="display:none;" />`;
    // R112: the board's sticky table head sits just under the app's sticky top nav — measured, not guessed (0 when there is none).
    try { const nav = document.querySelector<HTMLElement>('.topnav'); body.style.setProperty('--mn-sticky-top', `${nav ? Math.round(nav.getBoundingClientRect().height) : 0}px`); } catch { /* no nav in this shell */ }
    this.bind();
    if (focusWo) {
      // Scroll the focused card into view, and (matching the expand handler)
      // lazily load the field-access panel for emergency/urgent/incident WOs.
      const card = body.querySelector(`[data-mn-wotoggle="${CSS.escape(focusWo)}"]`);
      if (card) card.scrollIntoView({ block: 'center' });
      const wo = (this.workOrders || []).find(w => w.id === focusWo);
      const prio = wo ? this.woPriority(wo) : 'routine';
      if (wo && (prio === 'emergency' || prio === 'urgent' || wo.source === 'incident') && !this.fieldByListing[String(wo.listing_id)]) {
        void this.reloadField(String(wo.listing_id)).then(() => { if (this.expandedWo.has(focusWo)) this.draw(); });
      }
    }
  }

  // ── tab 1: board ─────────────────────────────────────────────
  // ── R90 #2: per-property maintenance expense review (ground team) ────────
  private renderPropReview(): string {
    const esc = (x: any) => this.esc(x);
    const money = (n: any) => n == null ? '<span style="color:var(--text-faint);">—</span>' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const picker = `
      <div style="display:flex; gap:8px; align-items:flex-end; flex-wrap:wrap; margin-bottom:12px;">
        <label style="display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Property
          <input type="text" id="mn-pr-prop" list="mn-pr-props" autocomplete="off" placeholder="type to search properties…" style="padding:7px 10px; border:1px solid var(--border); border-radius:8px; font-size:12.5px; min-width:260px;">
          <datalist id="mn-pr-props">${(this.prListings || []).map((l: any) => `<option value="${esc(String(l.short_name || l.name || l.id))}"></option>`).join('')}</datalist>
        </label>
        ${this.prLid ? `<span class="mn-basis" style="margin:0 0 6px;">every work order at this property in the last 12 months, newest first — costs are Asset Ops records, not statement lines</span>` : ''}
      </div>`;
    if (!this.prLid) {
      return `${picker}<div class="mn-card" style="padding:22px; font-size:12.5px; color:var(--text-muted);">Pick a property to review its maintenance expenses — every WO with its cost, invoices and description, ready for ground-team notes before the owner statement conversation.</div>`;
    }
    if (!this.prData) return `${picker}<div class="mn-card" style="padding:22px; font-size:12.5px; color:var(--text-muted);">Loading…</div>`;
    if (this.prData.error) return `${picker}<div class="mn-card" style="padding:22px; font-size:12.5px; color:var(--danger-text,#b91c1c);">⚠ Couldn't load this property's work orders — pick it again to retry.</div>`;
    const rows: any[] = this.prData.rows || [];
    const total = rows.reduce((n, w) => n + (Number(w.cost) || 0), 0);
    const months = [...new Set(rows.map(w => String(w.created_at || '').slice(0, 7)))];
    const monthBlock = (m: string) => {
      const mine = rows.filter(w => String(w.created_at || '').slice(0, 7) === m);
      const mTotal = mine.reduce((n, w) => n + (Number(w.cost) || 0), 0);
      return `
        <div style="padding:8px 14px 2px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); display:flex; gap:10px;">${esc(m)} <span class="num" style="margin-left:auto;">${mine.length} WO${mine.length === 1 ? '' : 's'} · ${money(mTotal)}</span></div>
        ${mine.map(w => `
          <div style="padding:10px 14px; border-bottom:1px solid var(--border-soft,#f1f5f9); font-size:12.5px;">
            <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
              <strong>${esc(w.title)}</strong>
              <span style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:var(--bg-subtle,#f1f5f9); color:var(--text-muted);">${esc(w.status)}</span>
              ${w.payer ? `<span style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; ${w.payer === 'owner' ? 'background:#eaf2ff; color:#1d4ed8;' : 'background:var(--bg-subtle,#f1f5f9); color:var(--text-muted);'}">${w.payer === 'owner' ? 'owner pays' : 'WCH pays'}</span>` : ''}
              ${w.billing_status ? `<span style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:var(--success-soft,#ecfdf5); color:var(--success-text,#065f46);" title="${esc(WO_BILLED_TITLE[w.billing_status] || 'Owner has been charged')}">${esc(WO_BILLED_LABEL[w.billing_status] || 'billed')}</span>` : (w.payer === 'owner' ? `<span style="font-size:10px; font-weight:700; padding:1px 7px; border-radius:999px; background:var(--warning-soft,#fffbeb); color:var(--warning-text,#92400e);" title="Owner pays, but hasn't been charged yet (Billed yet? is empty)">unbilled</span>` : '')}
              ${w.skill ? `<span style="font-size:10px; color:var(--text-muted); font-weight:700;">${w.skill === 'high' ? 'HIGH $65' : 'LOW $45'}</span>` : ''}
              <span class="num" style="margin-left:auto; font-weight:700;">${money(w.cost)}</span>
            </div>
            ${w.description ? `<div style="color:var(--text-muted); margin-top:3px;">${esc(String(w.description).slice(0, 240))}</div>` : ''}
            <div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-top:5px; font-size:11.5px;">
              <span style="color:var(--text-faint);">${esc(String(w.created_at || '').slice(0, 10))}${this.woDoneAt(w) ? ` → done ${esc(this.localDay(this.woDoneAt(w)))}` : ''}${w.created_by ? ` · by ${esc(w.created_by)}` : ''}</span>
              ${(w.invoices || []).map((inv: any) => `<a href="${esc(inv.url)}" target="_blank" rel="noopener" style="text-decoration:none;">🧾 ${esc(inv.name || 'invoice')}</a>`).join('')}
              <button class="mn-btn sm" data-mn-pr-reviewed="${esc(w.id)}" ${this.prBusy === w.id ? 'disabled' : ''} style="margin-left:auto; ${w.ground_review ? 'background:var(--success-soft,#ecfdf5); border-color:var(--success,#16a34a); color:var(--success-text,#065f46);' : ''}">${w.ground_review ? `✓ reviewed · ${esc(w.ground_review.by || '')}` : 'mark reviewed'}</button>
            </div>
            ${(w.review_notes || []).length ? `<div style="margin-top:5px; display:flex; flex-direction:column; gap:2px;">${(w.review_notes || []).map((n: any) => `<div style="font-size:11.5px;"><strong>${esc(n.by || '—')}</strong> <span style="color:var(--text-faint);">${esc(String(n.at || '').slice(0, 10))}</span> — ${esc(n.text)}</div>`).join('')}</div>` : ''}
            <div style="display:flex; gap:6px; margin-top:5px;">
              <input type="text" data-mn-pr-noteinp="${esc(w.id)}" value="${esc(this.prDrafts.get(w.id) || '')}" placeholder="ground note — what you saw, why the cost makes sense (or doesn't)…" maxlength="500" style="flex:1; padding:5px 9px; border:1px solid var(--border); border-radius:7px; font-size:11.5px;">
              <button class="mn-btn sm" data-mn-pr-note="${esc(w.id)}" ${this.prBusy === w.id ? 'disabled' : ''}>📝 Add note</button>
            </div>
          </div>`).join('')}`;
    };
    return `${picker}
      <div class="mn-card" style="overflow:hidden;">
        <div style="display:flex; gap:10px; padding:11px 14px; border-bottom:1px solid var(--border); align-items:center; flex-wrap:wrap;">
          <strong style="font-size:13px;">${esc(this.prData.listing_name || this.prLid)}</strong>
          <span class="mn-basis" style="margin:0;">${rows.length} work order${rows.length === 1 ? '' : 's'} · 12 months</span>
          <span class="num" style="margin-left:auto; font-weight:700;">${money(total)} total recorded cost</span>
        </div>
        ${rows.length ? months.map(monthBlock).join('') : '<div style="padding:22px; font-size:12.5px; color:var(--text-muted);">No work orders at this property in the last 12 months.</div>'}
      </div>`;
  }

  // ── R102 #1/#5/#6/#10: the BOARD is the maintenance project view per
  // property — emergencies first, scheduling + vendor metrics, assets, owner
  // portal queue, one-click Property Expenses PDF, and a per-property Project.
  private schedOf(w: any): string | null {
    if (!w) return null;
    if (w.scheduled_date && /^\d{4}-\d{2}-\d{2}/.test(String(w.scheduled_date))) return String(w.scheduled_date).slice(0, 10);
    const st = w.scheduled_window && w.scheduled_window.start;
    return st && /^\d{4}-\d{2}-\d{2}/.test(String(st)) ? String(st).slice(0, 10) : null;
  }
  private vendorNameOf(id: any): string { return id ? ((this.vendors || []).find(v => v.id === id)?.name || '') : ''; }
  /** R115 review: the grid's own business day (what drew the 'past' cells) — a device ahead of business time must not refuse business-today. */
  private calToday(): string { return (this.calGrid && this.calGrid.today) || this.todayLocal(); }
  private todayLocal(): string { return new Date().toLocaleDateString('en-CA'); }
  // The DEVICE's calendar day of a timestamp (R50 rule — an evening completion
  // belongs to the day the team saw it, not the next UTC day); a bare
  // YYYY-MM-DD is already a day.
  private localDay(iso: any): string {
    if (!iso) return '';
    const s = String(iso);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
  }
  // R109 #c: when a work order was finished — only while it IS done. A reopened
  // WO keeps its old completed_at server-side (tech stats / care reports read
  // it), so every completion filter, sort and label goes through here. Legacy
  // rows without completed_at fall back to the last 'done' log entry.
  private woDoneAt(w: any): string | null {
    if (!w || w.status !== 'done') return null;
    if (w.completed_at) return String(w.completed_at);
    const e = (Array.isArray(w.log) ? w.log : []).filter((x: any) => x && x.status === 'done').pop();
    return e && e.at ? String(e.at) : null;
  }
  // R109 #d: what the search reads off a work order (names, dates, done-ness).
  private woSearchCtx(): any {
    return {
      today: this.todayLocal(),
      localDay: (x: any) => this.localDay(x),
      doneAt: (w: any) => this.woDoneAt(w),
      schedOf: (w: any) => this.schedOf(w),
      vendorName: (id: any) => this.vendorNameOf(id),
      assetName: (id: any) => (id ? ((this.assets || []).find(a => a.id === id)?.name || '') : ''),
    };
  }
  // One cached search doc per WO object. Handlers mutate WOs in place
  // (Object.assign, wo.comments = …), so a cheap signature decides a rebuild;
  // reloadWos() swaps the objects and the WeakMap lets the old docs go.
  private woDoc(w: any, ctx: any): any {
    const c = w.completion || {};
    const sig = [ctx.today, w.title, w.status, w.priority, w.type, w.role, w.assigned_to, w.vendor_id, ctx.vendorName(w.vendor_id), w.asset_id, ctx.assetName(w.asset_id),
      w.listing_name, w.created_by, w.billing_status, w.payer, w.due_date, w.effective_due, this.schedOf(w), w.completed_at, w.claim_tracked ? 1 : 0,
      String(w.description || '').length, String(w.summary || '').length, `${String(c.what_done || '').length}/${String(c.followup_note || '').length}/${c.needs_followup ? 1 : 0}`,
      (w.items || []).map((i: any) => `${String((i && i.title) || '').length}.${String((i && i.note) || '').length}`).join(','),
      (w.comments || []).length, (w.internal_notes || []).length, (w.log || []).length, (w.invoices || []).length, w.vendor_report ? String(w.vendor_report.work_done || '').length : -1].join('|');
    const hit = this.woDocCache.get(w);
    if (hit && hit.sig === sig) return hit.doc;
    const doc = buildDoc(w, ctx);
    this.woDocCache.set(w, { sig, doc });
    return doc;
  }
  private addDaysStr(d: string, n: number): string { const x = new Date(String(d).slice(0, 10) + 'T00:00:00Z'); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); }
  private fmtDay(d: string | null): string {
    if (!d) return '';
    const x = new Date(String(d).slice(0, 10) + 'T00:00:00Z');
    return isNaN(x.getTime()) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  private shortListing(n: any): string { return String(n || '').replace(/^[A-Za-z ]{1,7}-\s*/, ''); }
  private opChip(w: any): string {
    const op = w && w.owner_portal; if (!op || !op.state) return '';
    const map: Record<string, [string, string, string]> = { published: ['🏦 on portal', '#eaf2ff', '#3730a3'], needs_approval: ['🏦 awaiting owner', '#fffbeb', '#92400e'], approved: ['🏦 owner approved', '#ecfdf5', '#065f46'], declined: ['🏦 owner declined', '#fee2e2', '#b91c1c'] };
    const [l, bg, fg] = map[op.state] || [op.state, 'var(--bg-subtle)', 'var(--text-muted)'];
    return `<span class="mn-pill" style="background:${bg}; color:${fg};" title="${this.esc(op.note || '')}${op.amount != null ? ` · $${this.esc(op.amount)}` : ''}${op.decision_note ? ` · owner: ${this.esc(op.decision_note)}` : ''}">${l}</span>`;
  }
  // `cls` = the button class (R112: the owner queue passes the shared .btn-secondary; every other caller keeps .mn-btn).
  private opButtons(w: any, cls = 'mn-btn sm'): string {
    if (MaintenancePage.fieldRole() === 'maintenance') return '';
    const st = w.owner_portal && w.owner_portal.state;
    const b: string[] = [];
    if (w.status === 'done' && st !== 'published') b.push(`<button class="${cls}" data-mn-op="${this.esc(w.id)}|publish" title="Show this completed work (and its cost) in the owner's portal">🏦 Publish to owner</button>`);
    if (w.status !== 'done' && st !== 'needs_approval' && st !== 'approved') b.push(`<button class="${cls}" data-mn-op="${this.esc(w.id)}|request" title="Ask the owner to approve the amount from their portal">🏦 Ask owner to approve</button>`);
    if (st) b.push(`<button class="${cls}" data-mn-op="${this.esc(w.id)}|unpublish" title="Remove from the owner portal (the expense stays in Accounting)">↩ Unpublish</button>`);
    return b.join(' ');
  }
  private async opAction(id: string, action: string): Promise<void> {
    const w = (this.workOrders || []).find(x => x.id === id);
    const body: any = { by: MaintenancePage.actorName() };
    if (action === 'publish') {
      body.action = 'publish';
      if (w && w.payer === 'wch' && !window.confirm('WCH covers this one — it will show in the owner portal as "covered by WCH" (not billed). Publish?')) return;
      if (w && !w.payer && !window.confirm('No payer is set on this work order — the owner will see it as billed to them. Publish anyway? (Cancel to set the payer first.)')) return;
      const note = window.prompt('Owner-facing note (optional) — what was done, in plain language:', (w && (w.summary || (w.completion && w.completion.what_done))) || '');
      if (note === null) return;
      body.note = note;
    } else if (action === 'request') {
      body.action = 'request_approval';
      const amt = window.prompt('Amount for the owner to approve ($):', w && w.cost != null ? String(w.cost) : '');
      if (amt === null) return;
      if (amt.trim() === '' || !Number.isFinite(Number(amt)) || Number(amt) < 0) { alert('Enter the amount to approve.'); return; }
      body.amount = Number(amt);
      const note = window.prompt('What are we asking them to approve? (owner-facing, optional)', (w && w.description) || '');
      if (note === null) return;
      body.note = note;
    } else {
      body.action = 'unpublish';
    }
    const j = await this.post(`/work-orders/${encodeURIComponent(id)}/owner-portal`, body);
    if (j && j.ok) { await Promise.all([this.reloadWos(), this.reloadBoard()]); this.draw(); }
  }
  private async scheduleWo(id: string, body: any): Promise<boolean> {
    const j = await this.post(`/work-orders/${encodeURIComponent(id)}/schedule`, { ...body, by: MaintenancePage.actorName() });
    if (!j || !j.ok) return false;
    this.calendar = null;
    await Promise.all([this.reloadWos(), this.reloadBoard()]);   // reloadWos drops the cached grid…
    if (this.tab === 'calendar' && this.calView === 'month') await this.reloadCalGrid();   // …so refetch before the redraw when the calendar is on screen
    if (this.dayPlanOpen) await this.loadDayPlan(); else this.dayPlan = null;   // review-2: the day plan follows every booking change
    return true;
  }
  // Expand / collapse a work-order card wherever it renders (Work orders tab,
  // project rows, owner queue). On expand, lazily load the field-access panel
  // for emergency/urgent/incident WOs (review R103 #21: the inline card too).
  private async toggleWo(id: string): Promise<void> {
    const opening = !this.expandedWo.has(id);
    if (opening) this.expandedWo.add(id); else this.expandedWo.delete(id);
    this.draw();
    if (opening) {
      const wo = (this.workOrders || []).find(w => w.id === id);
      const prio = wo ? this.woPriority(wo) : 'routine';
      if (wo && (prio === 'emergency' || prio === 'urgent' || wo.source === 'incident') && !this.fieldByListing[String(wo.listing_id)]) {
        await this.reloadField(String(wo.listing_id));
        if (this.expandedWo.has(id)) this.draw();
      }
    }
  }
  private resetWoFilters(): void {
    this.assigneeAuto = false;   // cleared on purpose — a late roster answer never re-scopes it
    this.woStatusFilter = ''; this.woListingFilter = ''; this.woAssigneeFilter = ''; this.woRoleFilter = ''; this.woUrgentOnly = false; this.woReviewOnly = false; this.woBilledFilter = ''; this.woDateFrom = ''; this.woDateTo = '';
    this.woDateMode = 'created'; this.woQ = ''; this.woQShown = 100;   // R109 #c/#d: a leftover search or completed-date range would hide the WO being opened
  }
  // R109 #c/#d: after creating a WO the list must show it — a leftover search or
  // a completed-date range (a new WO has no completion) would hide it.
  private clearWoSearchForNew(): void {
    this.woQ = ''; this.woQShown = 100;
    if (this.woDateMode === 'completed') { this.woDateMode = 'created'; this.woDateFrom = ''; this.woDateTo = ''; }
  }
  private drillWo(id: string): void {
    this.tab = 'work_orders';
    this.resetWoFilters();
    this.expandedWo.add(id);
    this.draw();
    this.container.querySelector(`[data-mn-wotoggle="${CSS.escape(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  private openExpensesPdf(lid: string, from?: string, to?: string, photos?: boolean): void {
    const qp = new URLSearchParams();
    if (from) qp.set('from', from);
    if (to) qp.set('to', to);
    if (photos) qp.set('photos', '1');
    const qs = qp.toString();
    void openApiDoc(`${API}/maintenance/expenses-report/${encodeURIComponent(lid)}${qs ? `?${qs}` : ''}`, 'Property expenses');
  }

  // R112 (Jesse: "Let's also redesign the maintenance board!") — the Board in
  // the HostBuddy idiom: the shared .kpi-card strip (icon squares, tones, click
  // = focus filter), a boxed Calendar-style table (card-head with chip toggles
  // + counts, sticky head, .pill tones in the metric cells, ONE blue primary per
  // row beside icon buttons and the existing ⋯ menu), and the same rows stacked
  // as property cards under 900px. Same payload, same data-mn-* contracts, same
  // handlers — only the chrome changed.
  private renderBoard(): string {
    if (!this.board) return emptyState('Board unavailable', `GET ${API}/maintenance/board failed — check the server log.`, '⚠️');
    const all: any[] = this.board.rows || [];
    if (!all.length) return emptyState('No active listings', "No listings with status='active' were found.", '🏠');
    const cadence = this.cadenceDefault;
    const t = this.board.totals || {};
    const q = this.boardQ.trim().toLowerCase();
    // R112 #4: "today" is the server's business day (America/Los_Angeles — the
    // day its overdue counts already use); the device day drifts after 5pm.
    const today = /^\d{4}-\d{2}-\d{2}$/.test(String(this.board.as_of || '')) ? String(this.board.as_of) : this.todayLocal();
    const stale = (r: any) => r.days_since_pm == null || r.days_since_pm > cadence;
    const hasOpen = (r: any) => ((r.open_wo_count || 0) + (r.in_progress_wo_count || 0) + ((r.emergencies && r.emergencies.open) || 0)) > 0;
    const focusTest: Record<string, (r: any) => boolean> = {
      '': () => true,
      open: hasOpen,
      emg: r => !!(r.emergencies && (r.emergencies.open || r.emergencies.emergency_wos)),
      unsched: r => (r.unscheduled_count || 0) > 0,
      unassigned: r => (r.unassigned_count || 0) > 0,
      owner: r => ((r.awaiting_owner_count || 0) + (r.publish_ready_count || 0)) > 0,
      pm: stale,
    };
    const focusLabel: Record<string, string> = { open: 'with open work', emg: 'emergencies', unsched: 'unscheduled work', unassigned: 'unassigned work', owner: 'owner-portal work', pm: 'PM due' };
    const focusN = (k: string) => all.filter(focusTest[k] || focusTest['']).length;   // properties, over the whole portfolio (not the typed filter)
    // emergencies first (server rank), then alphabetical — the payload itself stays alphabetical for every property <select>
    let rows = [...all].sort((a, b) => ((a.emergency_rank ?? 3) - (b.emergency_rank ?? 3)) || ((b.emergencies?.emergency_wos || 0) - (a.emergencies?.emergency_wos || 0)) || String(a.listing_name).localeCompare(String(b.listing_name)));
    if (q) rows = rows.filter(r => String(r.listing_name).toLowerCase().includes(q));
    if (this.boardFocus && focusTest[this.boardFocus]) rows = rows.filter(focusTest[this.boardFocus]);
    const staleCount = focusN('pm');
    // R112 review: the Emergencies card's caption, its click and the "Emergencies" chip
    // must agree. The server's properties_with_emergencies counts open incidents only,
    // while the focus (focusTest.emg) also takes emergency-priority WOs — so the caption
    // counts properties with the focus's own measure and names the WO share explicitly.
    const emgN = Number(t.emergencies || 0);
    const emgWos = all.reduce((n, r) => n + Number((r.emergencies && r.emergencies.emergency_wos) || 0), 0);
    const emgP = focusN('emg');
    const opN = Number(t.publish_ready || 0) + Number(t.awaiting_owner || 0);
    const on = (k: BoardFocus) => this.boardFocus === k;
    const kpi = (key: BoardFocus, label: string, ico: string, v: any, cap: string, tone = '') => `
      <div class="kpi-card clickable ${tone} ${on(key) ? 'on' : ''}" data-mn-board-focus="${key}" role="button" tabindex="0" aria-pressed="${on(key) ? 'true' : 'false'}" title="${on(key) ? 'Show every property' : `Only the properties with ${label.toLowerCase()}`}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${v}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    const kpis = `<div class="mn-board-kpis">
      ${kpi('emg', 'Emergencies', 'flag', emgN, `open incidents${emgWos ? ` + ${emgWos} emergency WO${emgWos === 1 ? '' : 's'}` : ''} · ${emgP} propert${emgP === 1 ? 'y' : 'ies'}`, emgP ? 'tone-bad' : '')}
      ${kpi('open', 'Open work', 'wrench', t.open_wos || 0, `open + in progress · ${focusN('open')} propert${focusN('open') === 1 ? 'y' : 'ies'}`)}
      ${kpi('unsched', 'Unscheduled', 'calendar', t.unscheduled || 0, 'no booked day yet', (t.unscheduled || 0) ? 'tone-warn' : '')}
      ${kpi('unassigned', 'Unassigned', 'users', t.unassigned || 0, 'no vendor or tech on it', (t.unassigned || 0) ? 'tone-warn' : '')}
      ${kpi('owner', 'Owner portal', 'wallet', opN, `${t.publish_ready || 0} to publish · ${t.awaiting_owner || 0} awaiting<br><button type="button" class="mn-link" data-mn-board-queue title="Completed owner-billed work not yet on the portal + requests awaiting the owner">${this.boardQueueOpen ? 'hide the queue' : 'open the queue →'}</button>`)}
      ${kpi('pm', 'PM due', 'clock', staleCount, `older than ${cadence}d or never`, staleCount ? 'tone-warn' : '')}
    </div>`;
    const chip = (key: BoardFocus, label: string, attr = `data-mn-board-focus="${key}"`) => `<button type="button" class="chip ${on(key) ? 'active' : ''}" ${attr} aria-pressed="${on(key) ? 'true' : 'false'}">${label}<span class="chip-count">${key ? focusN(key) : all.length}</span></button>`;
    const chips = chip('', 'All') + chip('open', 'With open work', 'data-mn-board-openonly') + chip('emg', 'Emergencies') + chip('unsched', 'Unscheduled') + chip('unassigned', 'Unassigned') + chip('owner', 'Owner portal') + chip('pm', 'PM due');
    const pills = (parts: string[]) => { const p = parts.filter(Boolean); return p.length ? `<div class="mn-cell-pills">${p.join('')}</div>` : '<span class="mn-muted">—</span>'; };
    const tr = rows.map(r => {
      const e = r.emergencies || { open: 0, latest: [] };
      const days = r.days_since_pm;
      const pmPill = days == null
        ? `<span class="pill pill-red" title="No completed PM work order on record">never</span>`
        : `<span class="pill ${stale(r) ? 'pill-red' : 'pill-green'}" title="${days} days since the last completed PM${stale(r) ? ` — older than the ${cadence}-day cadence` : ''}">${days}d ago</span>`;
      const drill = (status: string) => `data-mn-board-drill="${this.esc(r.listing_id)}|${this.esc(r.listing_name)}|${status}"`;
      const live = (r.open_wo_count || 0) + (r.in_progress_wo_count || 0);
      const worst = String(e.worst || 'minor');
      const emgTone = worst === 'emergency' ? 'pill-red' : worst === 'urgent' ? 'pill-amber' : 'pill-gray';   // severity tones — semantic, unchanged
      const emgCell = e.open
        ? `<div class="mn-emg-wrap"><button type="button" class="pill ${emgTone} mn-emg" data-mn-board-emg="${this.esc(r.listing_id)}" title="${this.esc((e.latest || []).map((i: any) => `${i.severity}: ${i.title}`).join('\n'))} — click to work them right here">${worst === 'emergency' ? '🚨' : worst === 'urgent' ? '⚡' : '·'} ${e.open} ${this.esc(e.worst || '')}</button><div class="mn-emg-t">${this.esc((e.latest && e.latest[0] && e.latest[0].title) || '')}</div></div>`
        : (e.emergency_wos ? `<button type="button" class="pill pill-amber mn-emg" data-mn-board-project="${this.esc(r.listing_id)}" title="emergency-priority work orders — open this property's project">⚡ ${e.emergency_wos} emergency WO${e.emergency_wos === 1 ? '' : 's'}</button>` : '<span class="mn-muted">—</span>');
      const schedCell = live ? pills([
        r.next_scheduled ? `<span class="pill pill-blue" title="next booked day${r.next_scheduled === today ? ' — today' : ''}">${r.next_scheduled === today ? 'today' : this.fmtDay(r.next_scheduled)}</span>` : '',
        r.unscheduled_count ? `<span class="pill pill-amber" title="open work with no booked day">${r.unscheduled_count} unscheduled</span>` : '',
        r.overdue_scheduled_count ? `<span class="pill pill-red" title="booked day already passed and still open">${r.overdue_scheduled_count} overdue</span>` : '',
      ]) : '<span class="mn-muted">—</span>';
      const whoCell = live ? pills([
        r.vendor_assigned_count ? `<span class="pill pill-gray" title="with a vendor">${r.vendor_assigned_count} vendor</span>` : '',
        r.tech_assigned_count ? `<span class="pill pill-gray" title="with an in-house tech">${r.tech_assigned_count} in-house</span>` : '',
        r.unassigned_count ? `<span class="pill pill-amber" title="nobody assigned">${r.unassigned_count} unassigned</span>` : '',
      ]) : '<span class="mn-muted">—</span>';
      const opLine = (r.awaiting_owner_count || r.publish_ready_count) ? `<div class="mn-sub">🏦 ${r.awaiting_owner_count ? `${r.awaiting_owner_count} awaiting owner` : ''}${r.awaiting_owner_count && r.publish_ready_count ? ' · ' : ''}${r.publish_ready_count ? `${r.publish_ready_count} to publish` : ''}</div>` : '';
      const expPop = this.expPopLid === String(r.listing_id) ? `<div class="mn-exp-pop" data-mn-exp-pop="${this.esc(r.listing_id)}">
            <label>From <input type="date" data-mn-exp-from="${this.esc(r.listing_id)}" value="${this.esc(today.slice(0, 7))}-01"></label>
            <label>To <input type="date" data-mn-exp-to="${this.esc(r.listing_id)}" value="${this.esc(today)}"></label>
            <label class="chk"><input type="checkbox" data-mn-exp-photos="${this.esc(r.listing_id)}"> photos</label>
            <button type="button" class="btn btn-primary btn-sm" data-mn-exp-go="${this.esc(r.listing_id)}">Generate</button>
            <button type="button" class="mn-ibtn" data-mn-exp-close="${this.esc(r.listing_id)}" title="close" aria-label="Close">✕</button>
          </div>` : '';
      const emgOpen = this.boardEmgOpen.has(String(r.listing_id));
      const emgRow = emgOpen ? (() => {
        const incs = (this.incidents || []).filter((i: any) => String(i.listing_id) === String(r.listing_id) && i.status !== 'resolved');
        return `<tr class="mn-board-x"><td colspan="8">
          <div class="mn-board-xh"><b>🚨 ${this.esc(r.listing_name)} — ${incs.length} open incident${incs.length === 1 ? '' : 's'}</b><span class="mn-sub">work them here: acknowledge, deflect, dispatch (creates the repair work order), resolve</span><span style="margin-left:auto; display:flex; gap:6px; flex-wrap:wrap;"><button type="button" class="btn btn-secondary btn-sm" data-mn-newwo="${this.esc(r.listing_id)}|${this.esc(r.listing_name)}">＋ Work order</button><button type="button" class="btn btn-secondary btn-sm" data-mn-board-project="${this.esc(r.listing_id)}">Project</button><button type="button" class="btn btn-secondary btn-sm" data-mn-board-emgtab="${this.esc(r.listing_id)}" title="Open the Emergencies tab filtered to this property">Emergencies tab →</button><button type="button" class="mn-ibtn" data-mn-board-emg="${this.esc(r.listing_id)}" title="collapse" aria-label="Collapse">✕</button></span></div>
          ${this.incidents == null ? `<div class="mn-muted" style="font-size:12px; padding:6px 0;">Incidents didn't load — <button class="mn-link" data-mn-board-emg-retry="${this.esc(r.listing_id)}">retry</button></div>` : (incs.length ? incs.map((i: any) => this.renderIncidentCard(i)).join('') : '<div class="mn-muted" style="font-size:12px; padding:6px 0;">No open incidents.</div>')}
        </td></tr>`;
      })() : '';
      return `
        <tr class="${e.open ? `mn-row-emg ${this.esc(e.worst || '')}` : ''} ${emgOpen ? 'mn-row-open' : ''}">
          <td class="mn-prop mn-drill" ${drill('')} title="Open this property's work orders" style="cursor:pointer;"><div class="mn-ellip">${this.esc(r.listing_name)}</div>${opLine}</td>
          <td data-label="Emergencies">${emgCell}</td>
          <td class="mn-drill" data-label="Open" ${drill('open')} title="See & action the open work orders" style="cursor:pointer;">${r.open_wo_count ? `<span class="pill pill-blue mn-num">${r.open_wo_count}</span>` : '<span class="mn-muted mn-num">0</span>'}${r.in_progress_wo_count ? `<div class="mn-sub">+${r.in_progress_wo_count} in progress</div>` : ''}</td>
          <td data-label="Scheduling">${schedCell}</td>
          <td data-label="Who's on it">${whoCell}</td>
          <td class="mn-num" data-label="Assets">${r.assets_count ? `<button class="mn-link" data-mn-board-assets="${this.esc(r.listing_id)}" title="Open the Assets tab for this property">${r.assets_count}</button>` : '<span class="mn-muted">0</span>'}</td>
          <td class="mn-pm-when" data-label="Last PM">${r.last_pm_at ? `${this.fmtDate(r.last_pm_at)}${pmPill}` : pmPill}</td>
          <td class="mn-acts-cell"><div class="mn-acts">
            <button type="button" class="btn btn-primary btn-sm" data-mn-board-project="${this.esc(r.listing_id)}" title="This property's maintenance project — the organized punch list of everything open">Project</button>
            <button type="button" class="mn-ibtn" data-mn-board-cal="${this.esc(r.listing_id)}" title="View this property's calendar" aria-label="Calendar">${icon('calendar', 15)}</button>
            <button type="button" class="mn-ibtn" data-mn-newwo="${this.esc(r.listing_id)}|${this.esc(r.listing_name)}" title="New work order" aria-label="New work order">＋</button>
            <details class="mn-dd"><summary class="mn-ibtn" title="More" aria-label="More">⋯</summary><div class="mn-dd-menu right">
              <button class="mn-btn sm" data-mn-board-expenses="${this.esc(r.listing_id)}" title="Property Expenses PDF for this month — one click, Print / Save as PDF from the page">📄 Expenses — this month</button>
              <button class="mn-btn sm" data-mn-exp-open="${this.esc(r.listing_id)}">📄 Expenses PDF — pick a range</button>
              <button class="mn-btn sm" data-mn-care-report="${this.esc(r.listing_id)}" title="Photo-forward owner care report — Print / Save as PDF from there">📄 Care report</button>
              <button class="mn-btn sm" data-mn-startpm="${this.esc(r.listing_id)}|${this.esc(r.listing_name)}">🛠 Start PM</button>
            </div></details>
            ${expPop}
          </div></td>
        </tr>${emgRow}`;
    }).join('');

    return `
      ${kpis}
      ${this.boardQueueOpen ? this.renderOwnerQueue() : ''}
      <div class="card mn-card-open mn-board-card">
        <div class="card-head">
          <div><div class="card-title">Properties</div><div class="card-sub">${rows.length} of ${all.length} · emergencies first${this.boardFocus ? ` · only <b>${this.esc(focusLabel[this.boardFocus] || this.boardFocus)}</b>` : ''}${q ? ` · matching “${this.esc(this.boardQ.trim())}”` : ''}</div></div>
          <div class="mn-board-filters">
            <input type="search" class="mn-search" data-mn-board-q value="${this.esc(this.boardQ)}" placeholder="Filter properties… (Enter)" aria-label="Filter properties">
            ${chips}
          </div>
        </div>
        <table class="mn-tbl mn-board">
          <colgroup><col style="width:20%"><col style="width:15%"><col style="width:7%"><col style="width:14%"><col style="width:12%"><col style="width:5%"><col style="width:11%"><col style="width:16%"></colgroup>
          <thead><tr>
            <th>Property</th>
            <th title="Unresolved in-stay incidents — click to open them">Emergencies</th>
            <th title="Work orders with status 'open' (+ in progress)">Open</th>
            <th title="Next booked day · open work with no day yet · booked days already passed">Scheduling</th>
            <th title="Vendor / in-house tech on the open work · nobody yet">Who's on it</th>
            <th title="Assets on file">Assets</th>
            <th title="Last completed PM — red when there is none, or it is older than the ${cadence}-day template cadence">Last PM</th>
            <th></th>
          </tr></thead>
          <tbody>${tr || `<tr><td colspan="8" class="mn-muted" style="text-align:center; padding:22px;">No properties match.</td></tr>`}</tbody>
        </table>
      </div>
      <p class="mn-basis">${this.esc(this.board.basis || 'one row per active listing')} · "last PM" red = no completed PM, or older than the ${cadence}-day template cadence · as of ${this.esc(today)}.</p>`;
  }

  // R102 #1a — what the owner should SEE (the Accounting tab keeps every expense).
  // R112 #5: a .card with a .card-head, section strips, .btn-secondary actions —
  // the inline WO card it opens (data-mn-pj-toggle → renderWoCard) is unchanged.
  private renderOwnerQueue(): string {
    const wos = this.workOrders || [];
    const QB = 'btn btn-secondary btn-sm';
    const st = (w: any) => (w.owner_portal && w.owner_portal.state) || '';
    const byDone = (a: any, b: any) => String(this.woDoneAt(b) || '').localeCompare(String(this.woDoneAt(a) || ''));   // R109 #c: completion via woDoneAt
    const ready = wos.filter(w => w.status === 'done' && w.payer === 'owner' && st(w) !== 'published' && st(w) !== 'needs_approval').sort(byDone);   // review: an owner-approved job that's now done is ready too; a done job still awaiting the owner stays in "Awaiting" only (R103 review #23)
    const awaiting = wos.filter(w => st(w) === 'needs_approval');
    const decided = wos.filter(w => (st(w) === 'approved' || st(w) === 'declined') && w.status !== 'done');
    const cutoff30 = this.addDaysStr(this.todayLocal(), -30);
    const noPayer = wos.filter(w => w.status === 'done' && !w.payer && !st(w) && this.localDay(this.woDoneAt(w)) >= cutoff30).sort(byDone);   // R109 #c: local day, not the UTC slice
    // open work the owner may need to OK first: owner-billed, costed, or quoted — nothing sent yet
    const prioR = (w: any) => ({ emergency: 0, urgent: 1, routine: 2, pm: 3 } as any)[this.woPriority(w)] ?? 2;
    const needsOk = wos.filter(w => w.status !== 'done' && !st(w) && w.payer !== 'wch' && (w.payer === 'owner' || w.cost != null || (Array.isArray(w.quotes) && w.quotes.length)))
      .sort((a, b) => prioR(a) - prioR(b) || String(b.created_at || '').localeCompare(String(a.created_at || '')));
    const line = (w: any, extra: string) => `<div class="mn-q-row"><div class="mn-q-main"><span class="mn-q-title" data-mn-pj-toggle="${this.esc(w.id)}" title="${this.expandedWo.has(w.id) ? 'Collapse' : 'Open the work order right here'}">${this.expandedWo.has(w.id) ? '▾' : '▸'} ${this.esc(w.title)}</span><span class="mn-sub">${this.esc(w.listing_name)}${w.cost != null ? ` · ${this.fmtMoney(w.cost)}` : ''}${this.woDoneAt(w) ? ` · done ${this.fmtDate(this.woDoneAt(w))}` : ''} ${this.opChip(w)}</span></div><div class="mn-q-acts">${extra}</div></div>${this.expandedWo.has(w.id) ? `<div class="mn-pj-card">${this.renderWoCard(w)}</div>` : ''}`;
    const sec = (title: string, sub: string, list: any[], f: (w: any) => string) => `<div class="mn-q-sec"><div class="mn-q-h">${title} <span class="mn-sub">${sub}</span></div>${list.length ? list.slice(0, 30).map(w => line(w, f(w))).join('') : '<div class="mn-muted" style="font-size:12px; padding:6px 0;">nothing here</div>'}${list.length > 30 ? `<div class="mn-sub">+${list.length - 30} more — use the Accounting tab</div>` : ''}</div>`;
    return `<div class="card mn-card-open mn-queue">
      ${this.assignDatalist('mn-assign-roster')}
      <div class="card-head">
        <div><div class="card-title">🏦 Owner portal queue</div><div class="card-sub">the Accounting tab keeps every expense — this is only what the owner should SEE in their portal. Publishing never changes payer or billing.</div></div>
        <button type="button" class="${QB}" data-mn-board-queue title="Hide the queue">✕ Hide</button>
      </div>
      <div class="card-body mn-q-body">
      ${sec('Ready to publish', `${ready.length} completed, owner-billed, not on the portal yet`, ready, w => this.opButtons(w, QB))}
      ${sec('Awaiting the owner', `${awaiting.length} approval request${awaiting.length === 1 ? '' : 's'} open`, awaiting, w => `<span class="mn-sub">asked ${this.fmtDate(w.owner_portal.published_at)}${w.owner_portal.amount != null ? ` · ${this.fmtMoney(w.owner_portal.amount)}` : ''}</span> ${this.opButtons(w, QB)}`)}
      ${sec('Open work the owner may need to OK', `${needsOk.length} owner-billed, costed or quoted — not sent yet`, needsOk, w => `<span class="mn-sub">${this.priorityBadge(this.woPriority(w))}${w.cost != null ? ` ${this.fmtMoney(w.cost)}` : ''}${Array.isArray(w.quotes) && w.quotes.length ? ` · ${w.quotes.length} quote${w.quotes.length === 1 ? '' : 's'}` : ''}</span> ${this.opButtons(w, QB)}`)}
      ${decided.length ? sec('Owner decided', `${decided.length} answered — still open work`, decided, w => `<span class="mn-sub">${w.owner_portal.decision_note ? `“${this.esc(w.owner_portal.decision_note)}”` : ''}</span> ${this.opButtons(w, QB)}`) : ''}
      ${noPayer.length ? sec('Completed — who pays?', `${noPayer.length} finished in the last 30 days with no payer set — decide, then publish`, noPayer, w => `<button type="button" class="${QB}" data-mn-payer="${this.esc(w.id)}|owner" title="Owner pays — sets payer and publishes it to the portal">Owner pays → publish</button> <button type="button" class="${QB}" data-mn-payer="${this.esc(w.id)}|wch" title="WCH covers it">WCH pays</button>`) : ''}
      </div>
    </div>`;
  }

  // R102 #4 — the PROJECT: one property's organized punch list. New work is
  // a task added to the property's ongoing project (inbox-zero), not a thing
  // that must all happen today: emergencies → waiting → booked → backlog → done.
  private renderProject(): string {
    const lid = this.projectLid;
    // review R103 #20: the inline calendar must be THIS property's grid, never a cached one from elsewhere
    if (this.projectCal && this.calLid !== lid) { this.calLid = lid; this.calGrid = null; this.calGridError = false; this.calDaySel = ''; this.calPick = null; this.calPickWho = null; this.calGridSeq++; this.calGridLoading = false; }   // review R103-2 #8: an in-flight grid for another property is dropped
    const row = (this.board?.rows || []).find((r: any) => String(r.listing_id) === lid);
    const wos = (this.workOrders || []).filter(w => String(w.listing_id) === lid);
    const name = row ? row.listing_name : (wos[0]?.listing_name || lid);
    const today = this.todayLocal();
    const prioRank = (w: any) => ({ emergency: 0, urgent: 1, routine: 2, pm: 3 } as any)[this.woPriority(w)] ?? 2;
    const live = wos.filter(w => w.status !== 'done');
    const incs = (this.incidents || []).filter(i => String(i.listing_id) === lid && i.status !== 'resolved');
    const emgWos = live.filter(w => this.woPriority(w) === 'emergency');
    const isWaiting = (w: any) => (w.owner_portal && w.owner_portal.state === 'needs_approval') || this.pendingReview(w) > 0 || !!(w.completion && w.completion.needs_followup);
    const waiting = live.filter(w => !emgWos.includes(w) && isWaiting(w));
    const rest = live.filter(w => !emgWos.includes(w) && !waiting.includes(w));
    const scheduled = rest.filter(w => this.schedOf(w)).sort((a, b) => String(this.schedOf(a)).localeCompare(String(this.schedOf(b))));
    const backlog = rest.filter(w => !this.schedOf(w)).sort((a, b) => prioRank(a) - prioRank(b) || String(a.due_date || '9999').localeCompare(String(b.due_date || '9999')) || String(b.created_at || '').localeCompare(String(a.created_at || '')));
    // R109 #c: "Done" filters on the completion day — 30 / 90 / 365 days, all, or a typed range (device-local day, not the UTC slice)
    const dr = this.projDoneRange(today);
    const done = wos.filter(w => { const d = this.localDay(this.woDoneAt(w)); return !!d && (!dr.from || d >= dr.from) && (!dr.to || d <= dr.to); })
      .sort((a, b) => String(this.woDoneAt(b) || '').localeCompare(String(this.woDoneAt(a) || '')));
    const pmDue = (this.pmDue && Array.isArray(this.pmDue.due)) ? this.pmDue.due.filter((d: any) => String((d.plan && d.plan.listing_id) || d.listing_id) === lid) : null;
    const assets = (this.assets || []).filter(a => String(a.listing_id) === lid);
    const overdue = scheduled.filter(w => String(this.schedOf(w)) < today).length;
    const book = (w: any) => `<div class="mn-pj-book">
        <input type="date" data-mn-proj-date="${this.esc(w.id)}" value="${this.esc(this.schedOf(w) || '')}" title="booked day">
        <select data-mn-proj-vendor="${this.esc(w.id)}" title="vendor"><option value="">— vendor —</option>${(this.vendors || []).map(v => `<option value="${this.esc(v.id)}" ${w.vendor_id === v.id ? 'selected' : ''}>${this.esc(v.name)}</option>`).join('')}</select>
        <input type="text" list="mn-pj-roster" data-mn-proj-assign="${this.esc(w.id)}" value="${this.esc(w.assigned_to || '')}" placeholder="tech / person" title="in-house tech or person">
        <button class="mn-btn sm primary" data-mn-proj-book="${this.esc(w.id)}">${this.schedOf(w) ? 'Update' : 'Book'}</button>
        ${this.schedOf(w) ? `<button class="mn-btn sm" data-mn-proj-unbook="${this.esc(w.id)}" title="Take it off the calendar (back to the backlog)">✕</button>` : ''}
        ${this.opButtons(w)}
      </div>`;
    const pjRow = (w: any, withBook: boolean) => {
      const sd = this.schedOf(w); const late = !!sd && sd < today && w.status !== 'done';
      const vend = this.vendorNameOf(w.vendor_id);
      const rv = this.pendingReview(w);
      const openHere = this.expandedWo.has(w.id);
      return `<div class="mn-pj-row ${late ? 'overdue' : ''} ${openHere ? 'open' : ''}">
        <div class="mn-pj-main">
          <div class="mn-pj-title" data-mn-pj-toggle="${this.esc(w.id)}" title="${openHere ? 'Collapse' : 'Open the work order right here'}"><span class="mn-pj-caret">${openHere ? '▾' : '▸'}</span> ${this.priorityBadge(this.woPriority(w))} ${this.esc(w.title)}${w.status === 'in_progress' ? ' <span class="mn-pill" style="background:var(--warning-soft); color:var(--warning-text);">in progress</span>' : ''} ${this.opChip(w)} <button class="mn-link" data-mn-proj-open="${this.esc(w.id)}" title="Open in the Work orders list" style="font-size:10px; margin-left:4px;">↗</button></div>
          <div class="mn-pj-meta">${vend ? `🏷 ${this.esc(vend)}` : (w.assigned_to ? `👤 ${this.esc(w.assigned_to)}` : '<span class="warn">nobody on it</span>')}${sd ? ` · 📆 ${late ? `<span class="warn">overdue — was ${this.fmtDay(sd)}</span>` : `booked ${sd === today ? 'today' : this.fmtDay(sd)}`}` : ''}${w.due_source === 'checkout' ? ' · 🔁 due at checkout' : (w.due_date ? ` · due ${this.fmtDay(w.due_date)}` : '')}${w.cost != null ? ` · ${this.fmtMoney(w.cost)}${w.payer ? ` (${w.payer === 'wch' ? 'WCH' : 'owner'})` : ''}` : ''}${w.completion && w.completion.needs_followup && w.status !== 'done' ? ` · <span class="warn">🔁 follow-up: ${this.esc(String(w.completion.followup_note || 'more work needed').slice(0, 90))}</span>` : ''}${rv ? ` · <span class="warn">🔔 ${rv} vendor item${rv === 1 ? '' : 's'} to review</span>` : ''}${this.allItemsDoneTag(w)}${w.description ? `<div class="mn-ellip" style="max-width:640px;">${this.esc(String(w.description).slice(0, 160))}</div>` : ''}</div>
        </div>
        ${withBook ? book(w) : `<div class="mn-pj-book">${this.woDoneAt(w) ? `<span class="mn-sub">done ${this.fmtDate(this.woDoneAt(w))}</span>` : ''} ${this.opButtons(w)}</div>`}
      </div>${openHere ? `<div class="mn-pj-card">${this.renderWoCard(w)}</div>` : ''}`;
    };
    // R103: incidents are the full command cards right here (acknowledge / deflect / dispatch → WO / resolve) — no tab hop.
    const incRow = (i: any) => `<div style="padding:8px 12px 0;">${this.renderIncidentCard(i)}</div>`;
    const sec = (cls: string, title: string, sub: string, body: string, empty: string) => `<div class="mn-card mn-pj-sec ${cls}"><div class="mn-pj-sech">${title} <span class="mn-sub">${sub}</span></div>${body || `<div class="mn-pj-empty">${empty}</div>`}</div>`;
    const chips = `<div class="mn-pj-chips">
      ${incs.length || emgWos.length ? `<span class="mn-metric danger">🚨 ${incs.length + emgWos.length} emergency</span>` : ''}
      <span class="mn-metric">${live.length} open</span>
      ${scheduled.length ? `<span class="mn-metric ok">📆 ${scheduled.length} booked${overdue ? ` · ${overdue} overdue` : ''}</span>` : ''}
      ${backlog.length ? `<span class="mn-metric warn">📥 ${backlog.length} unscheduled</span>` : ''}
      ${waiting.length ? `<span class="mn-metric warn">⏳ ${waiting.length} waiting</span>` : ''}
      <span class="mn-metric" title="assets on file">🔧 ${assets.length} asset${assets.length === 1 ? '' : 's'}</span>
      ${row ? `<span class="mn-metric" title="last completed PM">🛠 PM ${row.days_since_pm == null ? 'never' : `${row.days_since_pm}d ago`}</span>` : ''}
    </div>`;
    return `
      <div class="mn-card mn-pj-head">
        <button class="mn-btn sm" data-mn-proj-back title="Back to the board">← Board</button>
        <h3>${this.esc(name)}</h3>
        ${chips}
        <div class="mn-pj-acts">
          <button class="mn-btn sm primary" data-mn-newwo="${this.esc(lid)}|${this.esc(name)}">＋ Add to project</button>
          <button class="mn-btn sm ${this.projectCal ? 'primary' : ''}" data-mn-pj-cal title="${this.projectCal ? 'Hide the calendar' : 'Show this property\'s calendar right here'}">📅 Calendar</button>
          <button class="mn-btn sm" data-mn-board-expenses="${this.esc(lid)}" title="Property Expenses PDF — this month">📄 Expenses</button>
          <button class="mn-btn sm" data-mn-care-report="${this.esc(lid)}">📄 Care report</button>
          <button class="mn-btn sm" data-mn-startpm="${this.esc(lid)}|${this.esc(name)}">🛠 Start PM</button>
          <button class="mn-btn sm" data-mn-board-drill="${this.esc(lid)}|${this.esc(name)}|" title="Every work order at this property, as cards">All WOs →</button>
        </div>
      </div>
      <p class="mn-basis">The project is this property's living punch list: every new work order is a task added here. Give each one a day and a person — the calendar and day plan take it from there. Nothing has to happen today; nothing gets lost. Click a row to open the full work order right here.</p>
      ${this.assignDatalist('mn-pj-roster')}${this.assignDatalist('mn-assign-roster')}
      ${this.projectCal ? `<div class="mn-card" style="padding:12px 14px; margin-bottom:12px;">${this.renderCalMonth('', name)}</div>` : ''}
      ${sec('emg', '🚨 Emergencies', `${incs.length} incident${incs.length === 1 ? '' : 's'} · ${emgWos.length} emergency work order${emgWos.length === 1 ? '' : 's'}`, incs.map(incRow).join('') + emgWos.map(w => pjRow(w, true)).join(''), 'No open emergencies. 🎉')}
      ${sec('wait', '⏳ Waiting on someone', 'owner approval · vendor quote / invoice to review · follow-up flagged', waiting.map(w => pjRow(w, true)).join(''), 'Nothing blocked.')}
      ${sec('sched', '📆 Booked', `${scheduled.length} on the calendar${overdue ? ` · <span class="warn">${overdue} overdue — rebook or close</span>` : ''}`, scheduled.map(w => pjRow(w, true)).join(''), 'Nothing booked yet — pick days for the backlog below.')}
      ${sec('back', '📥 Backlog — needs a day & a person', `${backlog.length} open · highest priority first`, backlog.map(w => pjRow(w, true)).join(''), 'Backlog is empty. Inbox zero.')}
      ${sec('done', `✅ Done — ${this.esc(dr.label)}`, `${done.length} completed · publish the owner-billed ones to the portal`,
        this.projDoneControls(dr.custom) + (done.length ? done.slice(0, 40).map(w => pjRow(w, false)).join('') + (done.length > 40 ? `<div class="mn-pj-empty">+${done.length - 40} more — narrow the dates, or use Work orders → Completed</div>` : '') : `<div class="mn-pj-empty">Nothing completed in this range (${this.esc(dr.label)}).</div>`), '')}
      ${sec('rec', '🛠 Recurring maintenance', pmDue === null ? 'open the Recurring maintenance tab to load what is due' : `${pmDue.filter((d: any) => d.due_now).length} due now · ${pmDue.length} plan${pmDue.length === 1 ? '' : 's'} here`,
        pmDue === null ? '' : pmDue.map((d: any) => `<div class="mn-pj-row"><div class="mn-pj-main"><div class="mn-pj-title">🛠 ${this.esc((d.plan && d.plan.name) || d.name || 'PM plan')}</div><div class="mn-pj-meta">${d.due_now ? '<span class="warn">due now</span>' : (d.next_due ? `next ${this.fmtDay(d.next_due)}` : 'no date yet')}${d.reason ? ` · ${this.esc(d.reason)}` : ''}</div></div><div class="mn-pj-book">${d.due_now && d.plan ? `<button class="mn-btn sm primary" data-mn-pm-gen="${this.esc(d.plan.id)}">Generate WO</button>` : ''}<button class="mn-btn sm" data-mn-tab="recurring">Recurring →</button></div></div>`).join(''),
        pmDue === null ? '' : 'No recurring plans at this property. <button class="mn-link" data-mn-tab="recurring">Set one up →</button>')}`;
  }

  // R109 #c: the project's completion window. A typed from/to wins over the chips.
  private projDoneRange(today: string): { from: string; to: string; label: string; custom: boolean } {
    const from = this.projDoneFrom, to = this.projDoneTo;
    if (from || to) {
      const label = from && to ? `${this.fmtDate(from)} – ${this.fmtDate(to)}` : from ? `since ${this.fmtDate(from)}` : `up to ${this.fmtDate(to)}`;
      return { from, to, label, custom: true };
    }
    const days = this.projDoneDays;
    if (!days) return { from: '', to: '', label: 'all time', custom: false };
    return { from: this.addDaysStr(today, -days), to: '', label: days === 365 ? 'last 12 months' : `last ${days} days`, custom: false };
  }
  private projDoneControls(custom: boolean): string {
    const chip = (d: number, l: string) => `<button data-mn-pj-donedays="${d}" class="${!custom && this.projDoneDays === d ? 'on' : ''}">${l}</button>`;
    return `<div class="mn-pj-donef">
      <span class="mn-sub">Completed</span>
      <div class="mn-seg sm">${chip(30, '30 days')}${chip(90, '90 days')}${chip(365, '12 months')}${chip(0, 'All')}</div>
      <span class="mn-sub">or from</span>
      <input type="date" data-mn-pj-donedate="from" value="${this.esc(this.projDoneFrom)}" title="Completed on/after">
      <span class="mn-sub">to</span>
      <input type="date" data-mn-pj-donedate="to" value="${this.esc(this.projDoneTo)}" title="Completed on/before">
      ${custom ? '<button class="mn-btn sm" data-mn-pj-doneclear title="Clear the dates">✕</button>' : ''}
    </div>`;
  }

  // R102 #8 — Recurring maintenance: PM plans and their templates, one tab.
  private renderRecurring(): string {
    const seg = `<div class="mn-sub-seg"><div class="mn-seg"><button data-mn-recsub="plans" class="${this.recSub === 'plans' ? 'on' : ''}">🛠 Plans</button><button data-mn-recsub="templates" class="${this.recSub === 'templates' ? 'on' : ''}">📋 Templates</button></div><span class="mn-sub">a plan schedules a template at a property on a cadence — templates define the checklist and the PM cadence</span></div>`;
    return seg + (this.recSub === 'templates' ? this.renderTemplates() : this.renderPmPlans());
  }
  // R102 #7 — Costs & performance together.
  private renderCostsPerf(): string {
    const seg = `<div class="mn-sub-seg"><div class="mn-seg"><button data-mn-costsub="costs" class="${this.costSub === 'costs' ? 'on' : ''}">💵 Costs</button><button data-mn-costsub="performance" class="${this.costSub === 'performance' ? 'on' : ''}">📈 Performance</button></div><span class="mn-sub">${this.costSub === 'costs' ? 'spend by property, vendor and type' : 'per-tech / vendor throughput, speed, on-time rate and cost (90d)'}</span></div>`;
    return seg + (this.costSub === 'performance' ? this.renderPerformance() : this.renderCosts());
  }

  // R102 #2/#3 — the CALENDAR: a real month grid that doubles as the scheduler.
  private async reloadCalGrid(): Promise<void> {
    const qp = new URLSearchParams();
    if (this.calMonth) qp.set('month', this.calMonth);
    if (this.calLid) qp.set('listing_id', this.calLid);
    const seq = ++this.calGridSeq;
    const j = await fetch(`${API}/maintenance/calendar-grid?${qp.toString()}`).then(r => r.ok ? r.json() : null).catch(() => null);
    if (seq !== this.calGridSeq) return;   // a newer month / property request superseded this one
    this.calGrid = j;
    this.calGridError = !j;
    if (j && j.month) this.calMonth = j.month;
  }
  // The month view fetches itself whenever it is drawn without data (after a
  // WO change invalidated the cache); one fetch in flight at a time; a failed
  // fetch renders the unavailable state with a Retry instead of looping.
  private ensureCalGrid(): void {
    if (this.calGrid || this.calGridLoading || this.calGridError) return;
    this.calGridLoading = true;
    void this.reloadCalGrid().then(() => { this.calGridLoading = false; if ((this.tab === 'calendar' && this.calView === 'month') || (this.tab === 'project' && this.projectCal)) this.draw(); });
  }
  private calItemHtml(it: any): string {
    const pr = it.priority; const dot = pr === 'emergency' ? '🚨' : pr === 'urgent' ? '⚡' : pr === 'pm' ? '🛠' : (it.kind === 'done' ? '✓' : '·');
    const who = it.vendor || it.assigned_to || '';
    const kindWord = it.kind === 'scheduled' ? 'booked' : it.kind === 'due' ? 'DUE (no day booked yet)' : it.kind === 'pm' ? (it.due_now ? 'PM plan due now' : 'PM plan due') : it.kind === 'done' ? 'completed' : it.kind;
    const label = `${this.calLid ? '' : `${this.esc(this.shortListing(it.listing_name))} · `}${this.esc(it.title)}`;
    const span = it.scheduled_end && it.scheduled_date && it.scheduled_end !== it.scheduled_date ? ` · window ${this.esc(this.fmtDay(it.scheduled_date))} → ${this.esc(this.fmtDay(it.scheduled_end))}` : '';
    return `<div class="mn-cal-it k-${this.esc(it.kind)} p-${this.esc(pr)}" data-mn-cal-item="${this.esc(it.kind)}|${this.esc(it.id)}" draggable="${it.kind === 'scheduled' ? 'true' : 'false'}" title="${this.esc(it.listing_name)} — ${this.esc(it.title)} · ${kindWord}${span}${who ? ` · ${this.esc(who)}` : ' · nobody assigned'}${it.due_date ? ` · due ${this.esc(it.due_date)}` : ''}${it.kind === 'scheduled' ? ' · drag to another day' : ''}">${dot} ${label}${span ? ' <i>↔</i>' : ''}${who ? ` <i>· ${this.esc(who)}</i>` : ''}</div>`;
  }
  private renderCalendar(): string {
    const seg = `<div class="mn-seg"><button data-mn-cal-view="month" class="${this.calView === 'month' ? 'on' : ''}">📅 Month</button><button data-mn-cal-view="windows" class="${this.calView === 'windows' ? 'on' : ''}">Vacancy windows</button></div>`;
    if (this.calView === 'windows') return `<div class="mn-filters">${seg}<span class="mn-sub">occupancy + the next vacancy window per property, with the work booked inside it</span></div>` + this.renderCalWindows();
    return this.renderCalMonth(seg);
  }
  private renderCalMonth(seg: string, lockName?: string): string {
    const g = this.calGrid;
    const listingOpts = this.incListingOpts();
    const monthKey = (g && g.month) || this.calMonth || this.todayLocal().slice(0, 7);
    const [my, mm] = monthKey.split('-').map(Number);
    const monthLabel = new Date(Date.UTC(my, mm - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    const c = (g && g.counts) || {};
    const head = `<div class="mn-cal-head">
        ${seg}
        <div class="mn-cal-nav"><button class="mn-btn sm" data-mn-cal-month="-1" title="previous month">‹</button><b>${this.esc(monthLabel)}</b><button class="mn-btn sm" data-mn-cal-month="1" title="next month">›</button><button class="mn-btn sm" data-mn-cal-month="0">Today</button></div>
        ${lockName ? `<b style="font-size:13px;">${this.esc(lockName)}</b>` : `<select class="mn-sel" data-mn-cal-lid><option value="">All properties</option>${[...listingOpts.entries()].map(([id, n]) => `<option value="${this.esc(id)}" ${this.calLid === id ? 'selected' : ''}>${this.esc(n)}</option>`).join('')}</select>`}
        ${g ? `<span class="mn-sub">${c.scheduled || 0} booked · ${c.due_only || 0} due with no day · ${c.pm || 0} PM due · ${c.done || 0} done · <b>${c.unscheduled || 0} unscheduled</b></span>` : ''}
      </div>`;
    if (!g) {
      if (this.calGridError) return head + `<div class="mn-card" style="padding:26px; text-align:center;"><div style="font-weight:700; font-size:14px;">Calendar unavailable</div><div class="mn-sub" style="margin:4px 0 12px;">GET ${API}/maintenance/calendar-grid failed — check the server log.</div><button class="mn-btn sm primary" data-mn-cal-retry>Retry</button></div>`;
      this.ensureCalGrid();
      return head + `<div class="mn-muted" style="padding:30px; text-align:center;">Loading calendar…</div>`;
    }
    const first = new Date(g.first + 'T00:00:00Z'); const lead = first.getUTCDay();
    const cells: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => `<div class="mn-cal-dow">${d}</div>`);
    for (let i = 0; i < lead; i++) cells.push('<div class="mn-cal-day pad"></div>');
    for (const d of (g.days || [])) {
      const isToday = d.date === g.today; const past = d.date < g.today;
      const items: any[] = d.items || []; const shown = items.slice(0, 4);
      cells.push(`<div class="mn-cal-day ${isToday ? 'today' : ''} ${past ? 'past' : ''} ${this.calPick && !past ? 'droppable' : ''} ${this.calDaySel === d.date ? 'sel' : ''}" data-mn-cal-day="${this.esc(d.date)}" title="${this.calPick && !past ? 'click to book the picked work order on this day' : 'click to see this day'}">
        <div class="mn-cal-dn"><span>${Number(String(d.date).slice(8))}</span>${d.checkouts ? `<span class="mn-cal-turn" title="${d.checkouts} checkout${d.checkouts === 1 ? '' : 's'} — a good day to get in">🧳${d.checkouts}</span>` : ''}${d.checkins ? `<span class="mn-cal-turn in" title="${d.checkins} check-in${d.checkins === 1 ? '' : 's'}">🔑${d.checkins}</span>` : ''}</div>
        ${shown.map(it => this.calItemHtml(it)).join('')}
        ${items.length > 4 ? `<div class="mn-cal-more" data-mn-cal-more="${this.esc(d.date)}">+${items.length - 4} more</div>` : ''}
      </div>`);
    }
    const q = this.calSearch.trim().toLowerCase();
    const allUn: any[] = g.unscheduled || [];
    const un = allUn.filter((it: any) => !q || `${it.title} ${it.listing_name} ${it.vendor || ''} ${it.assigned_to || ''}`.toLowerCase().includes(q));
    const daySel = this.calDaySel ? (g.days || []).find((d: any) => d.date === this.calDaySel) : null;
    const pick = this.calPick ? (allUn.find((it: any) => it.id === this.calPick) || (this.workOrders || []).find(w => w.id === this.calPick)) : null;
    // who's on it — editable from the calendar (brief #3/#5: a day AND a person)
    const whoLine = (it: any) => `<div class="mn-q-acts mn-cal-who"><select data-mn-cal-vendor="${this.esc(it.id)}" title="vendor"><option value="">— vendor —</option>${(this.vendors || []).map(v => `<option value="${this.esc(v.id)}" ${it.vendor_id === v.id ? 'selected' : ''}>${this.esc(v.name)}</option>`).join('')}</select><input type="text" list="mn-cal-roster" data-mn-cal-assign="${this.esc(it.id)}" value="${this.esc(it.assigned_to || '')}" placeholder="tech / person" title="in-house tech or person"><button class="mn-btn sm" data-mn-cal-who="${this.esc(it.id)}" title="save the vendor / person (no date change)">save</button></div>`;
    const rail = `<div class="mn-cal-rail">
      ${this.assignDatalist('mn-cal-roster')}
      ${pick ? `<div class="mn-cal-picking">📌 Placing <b>${this.esc(pick.title)}</b> — click a day on the calendar. <button class="mn-btn sm" data-mn-cal-pick="${this.esc(pick.id)}">cancel</button></div>` : ''}
      ${daySel ? `<div class="mn-cal-dayd"><div class="mn-q-h">${this.esc(this.fmtDay(daySel.date))} <span class="mn-sub">${(daySel.items || []).length} item${(daySel.items || []).length === 1 ? '' : 's'}${daySel.checkouts ? ` · ${daySel.checkouts} checkout` : ''}</span><button class="mn-btn sm" data-mn-cal-dayclose style="margin-left:auto;">✕</button></div>
        ${(daySel.items || []).length ? (daySel.items || []).map((it: any) => `<div class="mn-cal-rrow">${this.calItemHtml(it)}<div class="mn-q-acts">${it.kind !== 'pm' ? `<button class="mn-btn sm" data-mn-cal-open="${this.esc(it.id)}">open</button>` : ''}${it.kind === 'scheduled' ? `<input type="date" data-mn-cal-bookdate="${this.esc(it.id)}" value="${this.esc(it.on)}"><button class="mn-btn sm" data-mn-cal-book="${this.esc(it.id)}">move</button><button class="mn-btn sm" data-mn-cal-unbook="${this.esc(it.id)}" title="take it off the calendar">✕</button>` : ''}${it.kind === 'due' ? `<button class="mn-btn sm primary" data-mn-cal-book="${this.esc(it.id)}" data-mn-cal-bookon="${this.esc(daySel.date)}">book this day</button>` : ''}</div>${(it.kind === 'scheduled' || it.kind === 'due') ? whoLine(it) : ''}</div>`).join('') : '<div class="mn-muted" style="font-size:12px;">Nothing on this day.</div>'}
      </div>` : ''}
      <div class="mn-q-h">📥 Unscheduled <span class="mn-sub">${un.length}${q ? ` of ${allUn.length}` : ''} — 📌 pick one and click a day, drag it onto a day, or type the date</span></div>
      <input type="search" class="mn-search" data-mn-cal-search value="${this.esc(this.calSearch)}" placeholder="search work orders… (Enter)">
      <div class="mn-cal-un">${un.slice(0, 40).map((it: any) => `<div class="mn-cal-urow ${this.calPick === it.id ? 'on' : ''}">
          <div class="mn-cal-umain" draggable="true" data-mn-cal-drag="${this.esc(it.id)}"><div>${this.priorityBadge(it.priority)} <b>${this.esc(it.title)}</b></div><div class="mn-sub">${this.esc(it.listing_name)}${it.vendor ? ` · 🏷 ${this.esc(it.vendor)}` : (it.assigned_to ? ` · 👤 ${this.esc(it.assigned_to)}` : ' · <span class="warn">nobody on it</span>')}${it.due_date ? ` · due ${this.fmtDay(it.due_date)}` : ''}</div></div>
          <div class="mn-q-acts"><button class="mn-btn sm ${this.calPick === it.id ? 'primary' : ''}" data-mn-cal-pick="${this.esc(it.id)}">${this.calPick === it.id ? 'placing…' : '📌 pick'}</button><input type="date" data-mn-cal-bookdate="${this.esc(it.id)}" value="${this.esc(it.due_date || '')}"><button class="mn-btn sm" data-mn-cal-book="${this.esc(it.id)}">book</button><button class="mn-btn sm" data-mn-cal-open="${this.esc(it.id)}" title="open the work order">↗</button></div>
          ${whoLine(it)}
        </div>`).join('') || '<div class="mn-muted" style="font-size:12px; padding:6px 0;">Every open work order has a day. 🎉</div>'}${un.length > 40 ? `<div class="mn-sub">+${un.length - 40} more — refine the search</div>` : ''}</div>
    </div>`;
    // R115 maint (phone ≤640px): seven 50px columns can't hold a work order, so
    // the month renders as an AGENDA list instead — every day that has work,
    // plus every upcoming day (so 📌 pick → tap a day still works), each row the
    // same data-mn-cal-day target the grid uses (click / pick-to-book / drop).
    const agendaDays = (g.days || []).filter((d: any) => (d.items || []).length || d.date >= g.today);
    const dayLabel = (iso: string) => new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
    const agenda = `<div class="mn-cal-agenda" aria-label="Month agenda">
        ${pick ? `<div class="mn-cal-picking mn-cal-ag-pick">📌 Placing <b>${this.esc(pick.title)}</b> — tap a day. <button class="mn-btn sm" data-mn-cal-pick="${this.esc(pick.id)}">cancel</button></div>` : ''}
        ${agendaDays.map((d: any) => {
          const items: any[] = d.items || []; const isToday = d.date === g.today; const past = d.date < g.today;
          return `<div class="mn-cal-ag-day ${isToday ? 'today' : ''} ${past ? 'past' : ''} ${this.calPick && !past ? 'droppable' : ''} ${this.calDaySel === d.date ? 'sel' : ''}" data-mn-cal-day="${this.esc(d.date)}" role="button" tabindex="0">
            <div class="mn-cal-ag-h"><b>${this.esc(dayLabel(d.date))}</b>${isToday ? '<span class="mn-cal-ag-today">today</span>' : ''}${d.checkouts ? `<span class="mn-cal-turn" title="${d.checkouts} checkout${d.checkouts === 1 ? '' : 's'}">🧳${d.checkouts}</span>` : ''}${d.checkins ? `<span class="mn-cal-turn in" title="${d.checkins} check-in${d.checkins === 1 ? '' : 's'}">🔑${d.checkins}</span>` : ''}<span class="mn-sub" style="margin-left:auto;">${items.length ? `${items.length} item${items.length === 1 ? '' : 's'}` : (this.calPick && !past ? 'tap to book here' : 'nothing booked')}</span></div>
            ${items.map(it => this.calItemHtml(it)).join('')}
          </div>`;
        }).join('') || '<div class="mn-muted" style="padding:16px 0; font-size:12px;">Nothing this month.</div>'}
      </div>`;
    return `${head}
      <p class="mn-basis">${this.esc(g.basis || '')}</p>
      <div class="mn-cal-wrap">
        <div class="mn-cal-main"><div class="mn-cal-grid">${cells.join('')}</div>${agenda}</div>
        ${rail}
      </div>`;
  }

  // ── tab: incidents (in-stay emergency war room) ──────────────
  // A live, emergency-first board of in-stay failures. Each card carries a
  // severity pill, the SLA clock, the in-house guest, category + status, and the
  // property. Click a card → incident-command view (timeline, deflection,
  // access/safety snapshot, dispatch, comp, status). Source: GET /incidents.
  private incListingOpts(): Map<string, string> {
    const opts = new Map<string, string>();
    (this.board?.rows || []).forEach((r: any) => opts.set(String(r.listing_id), r.listing_name));
    (this.incidents || []).forEach(i => { if (!opts.has(String(i.listing_id))) opts.set(String(i.listing_id), i.listing_name); });
    return opts;
  }

  private renderIncidents(): string {
    if (this.incidents == null) return emptyState('Incidents unavailable', `GET ${API}/incidents failed — check the server log.`, '⚠️');

    const filterBar = `
      <div class="mn-filters">
        <label>Severity</label>
        <select data-mn-incfilter="severity">
          <option value="">All</option>
          ${INC_SEVERITIES.map(([k, l]) => `<option value="${k}" ${this.incSeverityFilter === k ? 'selected' : ''}>${l}</option>`).join('')}
        </select>
        <label>Status</label>
        <select data-mn-incfilter="status">
          <option value="">All</option>
          ${(['open', 'acknowledged', 'dispatched', 'resolved']).map(s => `<option value="${s}" ${this.incStatusFilter === s ? 'selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>`).join('')}
        </select>
        <label>Property</label>
        <select data-mn-incfilter="listing">
          <option value="">All</option>
          ${[...this.incListingOpts().entries()].map(([id, n]) => `<option value="${this.esc(id)}" ${this.incListingFilter === id ? 'selected' : ''}>${this.esc(n)}</option>`).join('')}
        </select>
        <button class="mn-btn primary" data-mn-inc-log="1">${this.showLogIncident ? 'Close form' : '+ Log incident'}</button>
      </div>`;

    const addForm = this.showLogIncident ? this.renderIncidentForm() : '';

    const rows = this.incListingFilter ? this.incidents.filter(i => String(i.listing_id) === this.incListingFilter) : this.incidents;   // R102 #1: board → one property's emergencies
    const openCount = rows.filter(i => i.status !== 'resolved').length;
    const emCount = rows.filter(i => i.severity === 'emergency' && i.status !== 'resolved').length;

    if (!rows.length) {
      return `${filterBar}${addForm}` + emptyState('No incidents', this.incSeverityFilter || this.incStatusFilter || this.incListingFilter ? 'No incidents match the current filters.' : "When a guest reports an in-stay failure, log it here — it's triaged for severity, deflection, and a comp suggestion.", '🚨');
    }

    const list = rows.map(i => this.renderIncidentCard(i)).join('');
    return `
      <p class="mn-basis">${rows.length} incident(s) · emergency-first, then most-recently reported · ${openCount} active (not resolved)${emCount ? ` · ${emCount} emergency open` : ''}. Clocks: reported → acknowledged → dispatched → resolved. Click a card to open command view.</p>
      ${filterBar}
      ${addForm}
      ${list}`;
  }

  // Inline log-incident form. Leaving severity/category blank triggers AI triage.
  private renderIncidentForm(): string {
    const listingOpts = this.incListingOpts();
    return `
      <div class="mn-inc-form" data-mn-inc-formfor="new">
        <label class="full">Property
          <select data-mn-if="listing_id">
            <option value="">— pick a property —</option>
            ${[...listingOpts.entries()].map(([id, name]) => `<option value="${this.esc(id)}">${this.esc(name)}</option>`).join('')}
          </select>
        </label>
        <label class="full">Title
          <input type="text" data-mn-if="title" placeholder="e.g. No A/C — house is 85°F"/>
        </label>
        <label class="full">Description
          <textarea data-mn-if="description" placeholder="What the guest reported. The more detail, the better the triage."></textarea>
        </label>
        <label>Severity (optional)
          <select data-mn-if="severity">
            <option value="">— let AI triage —</option>
            ${INC_SEVERITIES.map(([k, l]) => `<option value="${k}">${l}</option>`).join('')}
          </select>
        </label>
        <label>Category (optional)
          <select data-mn-if="category">
            <option value="">— let AI triage —</option>
            ${INC_CATEGORIES.map(([k, l]) => `<option value="${k}">${l}</option>`).join('')}
          </select>
        </label>
        <div class="mn-inc-form-actions">
          <button class="mn-btn primary" data-mn-inc-create="1" ${this.incCreating ? 'disabled' : ''}>${this.incCreating ? 'Logging…' : 'Log incident'}</button>
          <button class="mn-btn" data-mn-inc-cancel="1">Cancel</button>
        </div>
      </div>
      <p class="mn-basis">Saved the moment you tap Log. Leave severity &amp; category blank to let AI triage them (and propose guest self-fix steps) — that fills in a few seconds later. With severity blank, these page the team as an emergency right away: a gas or propane smell/leak, fire, smoke, a burning smell, sparks, a smoke/fire/CO alarm that is going off (not a low-battery chirp), carbon monoxide, flooding, a burst pipe, water pouring or leaking through the ceiling, an overflowing toilet, a sewer backup, a lockout, a break-in or intruder, no heat, or no power to the home. A plain drip, a flood light, hot-tub/pool heat, one dead outlet or "no …" (e.g. "no flooding") doesn't page. A severity you pick always stands. If the AI is unavailable it stays urgent / other and you set them by hand.</p>`;
  }

  // R109 #g (Jesse: "I want to see all the text here"): the Issues "Add issue"
  // modal stores a SHORTENED title ("Kitchen light not working. Not the light
  // above the sink, ...") with the full report in description, and the card
  // only ever showed the title. Show the report itself: the description when
  // the title is just its start, else the title plus the description. Stored
  // titles stay as they are (Slack alerts and the board chip use them).
  private incReportText(i: any): { main: string; extra: string } {
    const title = String(i.title || '').trim();
    const desc = String(i.description || '').trim();
    const stem = title.replace(/(…|\.\.\.)\s*$/, '').trim();
    if (desc && desc.length > stem.length && desc.toLowerCase().startsWith(stem.toLowerCase())) return { main: desc, extra: '' };
    return { main: title, extra: desc && desc !== title ? desc : '' };
  }
  private renderIncidentCard(i: any): string {
    const open = this.expandedInc === i.id;
    const guest = i.reservation?.guest_name ? `👤 ${this.esc(i.reservation.guest_name)}` : '<span style="color:var(--text-faint, #9ca3af);">no guest in house</span>';
    const catLabel = (INC_CATEGORIES.find(([k]) => k === i.category) || [i.category, i.category])[1];
    const bodyHtml = open ? this.renderIncidentCommand(i) : '';
    const rep = this.incReportText(i);
    const full = rep.extra ? `${rep.main}\n${rep.extra}` : rep.main;
    // the whole report wraps on the card; a very long one is clamped to a few lines until the card is opened
    return `
      <div class="mn-inc sev-${this.esc(i.severity)}">
        <div class="mn-inc-row" data-mn-inc-toggle="${this.esc(i.id)}">
          <div style="min-width:0;">
            <div class="mn-inc-title${open ? '' : ' clamp'}" title="${this.esc(full)}">${this.severityPill(i.severity)} <span class="mn-inc-text">${this.esc(rep.main)}</span></div>
            ${rep.extra ? `<div class="mn-inc-desc${open ? '' : ' clamp'}">${this.esc(rep.extra)}</div>` : ''}
            <div class="mn-inc-meta" title="${this.esc(i.listing_name)}">${guest} · ${this.esc(catLabel)} · ${this.esc(i.listing_name)}${i.reported_by ? ` · <span title="Reported by — ask them for details">✍️ ${this.esc(i.reported_by)}</span>` : ''}</div>
          </div>
          <div class="mn-inc-clock" title="reported ${this.fmtDateTime(i.sla?.reported_at)}">reported ${this.fmtAgo(i.sla?.reported_at)}</div>
          <div>${this.incStatusPill(i.status)}</div>
          <div style="font-size:11px; color:var(--text-muted);">${i.review_risk ? '<span class="mn-chip risk">review risk</span>' : ''}${i.wo_id ? ' 🔧 WO' : ''}</div>
          <div class="mn-inc-caret">${open ? '▾' : '▸'}</div>
        </div>
        ${bodyHtml}
      </div>`;
  }

  // Incident-command view (expand-in-place). Uses the lazily-fetched detail
  // (GET /incidents/:id) for the on-call vendor roster; falls back to the list
  // record's fields while that loads.
  private renderIncidentCommand(i: any): string {
    const detail = this.incDetail[i.id];
    const inc = detail?.incident || i;
    const onCall = detail?.on_call_vendors || [];
    const sla = inc.sla || {};
    const r = inc.reservation;
    const a = inc.access || {};

    // R109 #g: the open card already shows the whole report (unclamped) — add the
    // photos / videos that came with it, which the command view never showed.
    const media: string[] = Array.isArray(inc.photos) ? inc.photos : [];
    const reportPanel = media.length ? `
      <div class="mn-inc-panel full">
        <h5>Photos from the report</h5>
        ${mediaStrip(media, { size: 'md', alt: 'reported photo' })}
      </div>` : '';

    // SLA timeline.
    const stamp = (key: string) => sla[key] ? `<span class="ts">${this.fmtDateTime(sla[key])}</span>` : '<span class="ts">—</span>';
    const tlRow = (label: string, key: string) =>
      `<li class="${sla[key] ? 'on' : 'off'}"><span class="dot"></span><span class="lab">${label}</span>${stamp(key)}</li>`;
    const timeline = `
      <div class="mn-inc-panel">
        <h5>SLA clock</h5>
        <ul class="mn-inc-tl">
          ${tlRow('Reported', 'reported_at')}
          ${tlRow('Acknowledged', 'acknowledged_at')}
          ${tlRow('Dispatched', 'dispatched_at')}
          ${tlRow('Resolved', 'resolved_at')}
        </ul>
      </div>`;

    // Guest in house.
    const guestPanel = `
      <div class="mn-inc-panel">
        <h5>Guest in house</h5>
        ${r ? `
          <div class="mn-inc-kv">
            <div><span class="k">Name</span> <b>${this.esc(r.guest_name || '—')}</b></div>
            <div><span class="k">Phone</span> ${r.guest_phone ? `<span class="mn-mono">${this.esc(r.guest_phone)}</span>` : '—'}</div>
            <div><span class="k">Stay</span> ${this.fmtDate(r.check_in)} → ${this.fmtDate(r.check_out)}${r.nights ? ` (${r.nights}n)` : ''}</div>
            <div><span class="k">Nightly</span> ${r.nightly != null ? `<span class="mn-mono">${this.fmtMoney(r.nightly)}</span>` : '<span style="color:var(--text-faint, #9ca3af);">unknown — no comp suggestion</span>'}</div>
            ${r.confirmation_code ? `<div><span class="k">Conf</span> <span class="mn-mono">${this.esc(r.confirmation_code)}</span></div>` : ''}
          </div>` : '<div style="font-size:12px; color:var(--text-muted);">No confirmed guest is currently in the home (check-out is exclusive).</div>'}
      </div>`;

    // Deflection self-fix steps.
    const steps = (inc.deflection?.steps || []);
    // AI-unblock: triage runs after the incident is saved — say so instead of "no steps".
    const tri = inc.triage || null;
    // a 'pending' older than any AI call can take was orphaned (server restart) — don't promise it
    const triStale = !!tri && tri.status === 'pending' && Date.now() - Date.parse(tri.at || '') > 6 * 60000;
    const triNote = tri && tri.status === 'pending' && !triStale
      ? '<div style="font-size:12px; color:var(--text-muted);">⏳ AI triage running — severity, category and self-fix steps fill in here shortly. Act now if it can\'t wait.</div>'
      : tri && (tri.status === 'failed' || tri.status === 'skipped' || triStale)
        ? `<div style="font-size:12px; color:var(--text-muted);">AI triage unavailable${tri.label ? ` (${this.esc(tri.label)})` : ''} — set severity / category by hand, or re-triage later.</div>`
        : '';
    const deflectPanel = `
      <div class="mn-inc-panel full">
        <h5>Deflection — guided self-fix ${inc.deflection?.self_fixable ? '<span class="mn-pill" style="background:var(--success-soft); color:var(--success-text, #166534);">self-fixable</span>' : ''}</h5>
        ${steps.length
          ? `<ol class="mn-inc-steps">${steps.map((s: string) => `<li>${this.esc(s)}</li>`).join('')}</ol>`
          : (triNote || '<div style="font-size:12px; color:var(--text-muted);">No self-fix steps — re-triage or dispatch a tech.</div>')}
        <div class="mn-inc-actions" style="margin-top:8px;">
          <button class="mn-btn sm ${inc.deflection?.sent ? 'primary' : ''}" data-mn-inc-deflect="${this.esc(inc.id)}|sent">${inc.deflection?.sent ? '✓ Self-fix sent' : 'Mark self-fix sent'}</button>
          <button class="mn-btn sm ${inc.deflection?.self_resolved ? 'primary' : ''}" data-mn-inc-deflect="${this.esc(inc.id)}|self_resolved">${inc.deflection?.self_resolved ? '✓ Guest self-resolved' : 'Guest self-resolved'}</button>
        </div>
      </div>`;

    // Access / safety snapshot for the field tech.
    const kv = (label: string, val: string) => val ? `<div><span class="k">${label}</span> ${this.esc(val)}</div>` : '';
    const accessPanel = `
      <div class="mn-inc-panel full">
        <h5>Access &amp; safety — for the field tech</h5>
        ${inc.safety_script ? `<div class="mn-inc-safety">⚠ ${this.esc(inc.safety_script)}</div>` : ''}
        <div class="mn-inc-kv">
          ${kv('Lockbox', a.lockbox)}
          ${kv('Backup code', a.backup_code)}
          ${kv('Gate', a.gate)}
          ${kv('Breaker', a.breaker)}
          ${kv('Breaker reset', a.breaker_reset)}
          ${kv('Water shutoff', a.water_shutoff)}
          ${kv('Water heater', a.water_heater)}
          ${kv('HVAC', a.hvac)}
          ${kv('Safety gear', a.safety)}
          ${![a.lockbox, a.backup_code, a.gate, a.breaker, a.breaker_reset, a.water_shutoff, a.water_heater, a.hvac, a.safety].some(Boolean) ? '<div style="font-size:12px; color:var(--text-muted);">No PDS access details on file for this property.</div>' : ''}
        </div>
      </div>`;

    // Dispatch.
    const dispatchPanel = `
      <div class="mn-inc-panel">
        <h5>Dispatch — roll a truck (creates the repair WO)</h5>
        ${inc.wo_id
          ? `<div style="font-size:12px;">Dispatched into work order <b>${this.esc(inc.wo_id)}</b>${inc.vendor_id ? ` · vendor ${this.esc((this.vendors || []).find(v => v.id === inc.vendor_id)?.name || inc.vendor_id)}` : ''}.</div>`
          : `<div class="mn-inc-actions">
              <label>On-call vendor (${inc.category})
                <select data-mn-inc-vendor="${this.esc(inc.id)}">
                  <option value="">— unassigned —</option>
                  ${onCall.map((v: any) => `<option value="${this.esc(v.id)}" ${inc.vendor_id === v.id ? 'selected' : ''}>${this.esc(v.name)}${v.trades?.length ? ` (${this.esc(v.trades.join(', '))})` : ''}</option>`).join('')}
                </select>
              </label>
              <button class="mn-btn primary" data-mn-inc-dispatch="${this.esc(inc.id)}">Dispatch (${severityToPriorityLabel(inc.severity)})</button>
            </div>
            ${onCall.length ? '' : '<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-top:6px;">No on-call vendors match this category — add one on the Vendors tab (set on-call), or dispatch unassigned.</div>'}`}
      </div>`;

    // Comp decision.
    const comp = inc.comp || {};
    const compPanel = `
      <div class="mn-inc-panel">
        <h5>Comp / refund decision</h5>
        <div class="mn-inc-kv" style="margin-bottom:8px;">
          <span class="k">Recommended</span> ${comp.recommended != null ? `<b class="mn-mono">${this.fmtMoney(comp.recommended)}</b> <span style="color:var(--text-muted);">(suggested — ${inc.severity})</span>` : '<span style="color:var(--text-faint, #9ca3af);">no suggestion (nightly unknown)</span>'}
        </div>
        <div class="mn-inc-actions">
          <label>Decided $
            <input type="number" min="0" step="1" data-mn-inc-comp="${this.esc(inc.id)}|decided" value="${comp.decided != null ? this.esc(comp.decided) : ''}" placeholder="$" style="width:90px;"/>
          </label>
          <label>Payer
            <select data-mn-inc-comp="${this.esc(inc.id)}|payer">
              <option value="" ${!comp.payer ? 'selected' : ''}>—</option>
              ${WO_PAYERS.map(([k, l]) => `<option value="${k}" ${comp.payer === k ? 'selected' : ''}>${l}</option>`).join('')}
            </select>
          </label>
          <label style="flex:1; min-width:140px;">Note
            <input type="text" data-mn-inc-comp="${this.esc(inc.id)}|note" value="${this.esc(comp.note || '')}" placeholder="why / how applied"/>
          </label>
          <button class="mn-btn primary" data-mn-inc-comp-save="${this.esc(inc.id)}">Save comp</button>
        </div>
        <p class="mn-honest" style="margin-top:8px;">Comp is Asset Ops' own labeled record — not synced to owner statements (those are generated externally).</p>
      </div>`;

    // Status + review-risk controls.
    const controls = `
      <div class="mn-inc-panel full">
        <h5>Status &amp; review risk</h5>
        <div class="mn-inc-actions">
          <button class="mn-btn sm" data-mn-inc-status="${this.esc(inc.id)}|acknowledged" ${inc.status !== 'open' ? 'disabled style="opacity:.5; cursor:default;"' : ''}>Acknowledge</button>
          <button class="mn-btn sm primary" data-mn-inc-status="${this.esc(inc.id)}|resolved" ${inc.status === 'resolved' ? 'disabled style="opacity:.5; cursor:default;"' : ''}>Resolve</button>
          <button class="mn-btn sm" data-mn-inc-retriage="${this.esc(inc.id)}">Re-triage</button>
          <button class="mn-btn sm ${inc.review_risk ? 'danger' : ''}" data-mn-inc-risk="${this.esc(inc.id)}">${inc.review_risk ? '⚑ Review risk (on)' : 'Flag review risk'}</button>
        </div>
      </div>`;

    return `
      <div class="mn-inc-body">
        <div class="mn-inc-grid">
          ${reportPanel}
          ${timeline}
          ${guestPanel}
          ${deflectPanel}
          ${accessPanel}
          ${dispatchPanel}
          ${compPanel}
          ${controls}
        </div>
      </div>`;
  }

  // ── tab: calendar (vacancy-aware cockpit) ────────────────────
  // One row per active listing over a horizon: occupancy now, the next free
  // window, scheduled WOs (⚠ when their window overlaps a confirmed stay), and
  // due-PM chips. Source: GET /maintenance/calendar (real reservations).
  private renderCalWindows(): string {
    if (this.calendar == null) return emptyState('Calendar unavailable', `GET ${API}/maintenance/calendar failed — check the server log.`, '⚠️');
    const listings: any[] = this.calendar.listings || [];
    const toggle = `
      <div class="mn-filters">
        <label>Horizon</label>
        <div class="mn-seg">
          ${[7, 30, 60].map(d => `<button data-mn-cal-days="${d}" class="${this.calDays === d ? 'on' : ''}">${d}d</button>`).join('')}
        </div>
      </div>`;
    if (!listings.length) {
      return toggle + emptyState('No active listings', "No listings with status='active' were found.", '🏠');
    }

    const vacantNow = listings.filter(l => l.status === 'vacant_now').length;
    const atRisk = listings.reduce((n, l) => n + (l.scheduled_wos || []).filter((w: any) => w.revenue_at_risk).length, 0);

    const rows = listings.map(l => {
      const occupied = l.status === 'occupied';
      let occHtml: string;
      if (occupied) {
        const free = l.vacant_from;
        const inDays = this.daysFromToday(free);
        occHtml = `<div class="mn-cal-occ"><span class="mn-dot occ"></span>Occupied — free ${this.fmtDate(free)}${inDays != null ? ` (in ${inDays}d)` : ''}</div>`;
      } else {
        occHtml = `<div class="mn-cal-occ"><span class="mn-dot vac"></span>Vacant now</div>`;
      }
      const gapHtml = l.next_booking_start
        ? `<div class="mn-cal-sub">Next guest ${this.fmtDate(l.next_booking_start)}${l.gap_days != null ? ` · ${l.gap_days}-day window` : ''}</div>`
        : `<div class="mn-cal-sub">No booking ahead — open-ended window</div>`;

      const woChips = (l.scheduled_wos || []).map((w: any) => {
        const sw = w.scheduled_window || {};
        const win = sw.start ? `${this.fmtDate(sw.start)}${sw.end ? `–${this.fmtDate(sw.end)}` : '+'}` : '';
        return `<span class="mn-chip ${w.revenue_at_risk ? 'risk' : ''}" title="${this.esc(w.title)} · scheduled ${win}${w.revenue_at_risk ? ' · overlaps a confirmed stay' : ''}">${w.revenue_at_risk ? '⚠ ' : ''}${this.priorityBadgeMini(w.priority)} ${this.esc(w.title)}</span>`;
      }).join('');
      const pmChips = (l.due_pms || []).map((p: any) => {
        const cls = p.due_now ? 'due' : '';
        const when = p.due_now ? 'due now' : (p.next_due ? `due ${this.fmtDate(p.next_due)}` : 'no date yet');
        return `<span class="mn-chip ${cls}" title="PM plan: ${this.esc(p.name)}">🛠 ${this.esc(p.name)} · ${when}</span>`;
      }).join('');

      const sched = `
        <div>
          <div class="mn-cal-label">Scheduled work / due PM</div>
          ${woChips || pmChips ? `${woChips}${pmChips}` : '<span style="font-size:11px; color:var(--text-faint, #9ca3af);">Nothing scheduled in this window.</span>'}
        </div>`;

      return `
        <div class="mn-cal">
          <div style="min-width:0;">
            <div class="mn-cal-prop" title="${this.esc(l.listing_name)}" style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(l.listing_name)}</div>
            ${occHtml}
            ${gapHtml}
          </div>
          <div>
            <div class="mn-cal-label">Upcoming stays (${this.calDays}d)</div>
            ${(l.upcoming || []).length
              ? (l.upcoming || []).map((u: any) => `<div class="mn-cal-sub">${this.fmtDate(u.check_in)} → ${this.fmtDate(u.check_out)}</div>`).join('')
              : '<span style="font-size:11px; color:var(--text-faint, #9ca3af);">No stays in this window.</span>'}
          </div>
          ${sched}
        </div>`;
    }).join('');

    return `
      <p class="mn-basis">One row per active listing over the next ${this.calDays} days · occupancy + the next vacancy window are computed from confirmed reservations (check_out is exclusive — the checkout day is free) · ${vacantNow} of ${listings.length} vacant now · ${atRisk} scheduled work order(s) overlap a confirmed stay (⚠).</p>
      ${toggle}
      ${rows}`;
  }

  // Compact inline priority dot/label used inside calendar chips.
  private priorityBadgeMini(priority: string): string {
    const map: Record<string, string> = {
      emergency: 'var(--danger, #dc2626)', urgent: 'var(--warning-text, #b45309)',
      routine: 'var(--text-muted)', pm: 'var(--info, #1d4ed8)',
    };
    const c = map[priority] || map.routine;
    return `<span style="display:inline-block; width:7px; height:7px; border-radius:999px; background:${c};"></span>`;
  }

  // ── tab: PM plans (recurring maintenance + due-now generator) ─
  private pmListingOpts(): Map<string, string> {
    const opts = new Map<string, string>();
    (this.board?.rows || []).forEach((r: any) => opts.set(String(r.listing_id), r.listing_name));
    (this.pmPlans || []).forEach(p => { if (!opts.has(String(p.listing_id))) opts.set(String(p.listing_id), p.listing_name); });
    return opts;
  }

  private renderPmPlans(): string {
    if (this.pmPlans == null) return emptyState('PM plans unavailable', `GET ${API}/pm-plans failed — check the server log.`, '⚠️');
    const listingOpts = this.pmListingOpts();

    // Due-now section (driven by GET /pm/due) — loaded lazily into this.pmDue.
    let dueHtml = '';
    if (this.pmDue && Array.isArray(this.pmDue.due)) {
      const dueNow = this.pmDue.due.filter((d: any) => d.due_now);
      if (dueNow.length) {
        const cards = dueNow.map((d: any) => {
          const p = d.plan;
          return `
            <div class="mn-pm" style="border-color:var(--warning-soft, #fde68a); background:var(--warning-soft, #fffbeb);">
              <div class="mn-pm-head">
                <span class="mn-pm-name">${this.esc(p.name)}</span>
                <span class="mn-pm-meta">${this.esc(p.listing_name)} · ${this.esc(d.reason)}</span>
                <button class="mn-btn primary sm" data-mn-pm-gen="${this.esc(p.id)}" style="margin-left:auto;">Generate WO</button>
              </div>
            </div>`;
        }).join('');
        dueHtml = `
          <div style="margin-bottom:14px;">
            <div class="mn-cal-label" style="margin-bottom:6px;">Due now · ${dueNow.length} plan(s) · as of ${this.esc(this.pmDue.as_of)}</div>
            ${cards}
          </div>`;
      } else {
        dueHtml = `<p class="mn-basis">No PM plans are due right now (checked ${this.pmDue.due.length} active plan(s) as of ${this.esc(this.pmDue.as_of)}).</p>`;
      }
    }

    const filterBar = `
      <div class="mn-filters">
        <label>Property</label>
        <select data-mn-pmfilter>
          <option value="">All</option>
          ${[...listingOpts.entries()].map(([id, name]) => `<option value="${this.esc(id)}" ${this.pmListingFilter === id ? 'selected' : ''}>${this.esc(name)}</option>`).join('')}
        </select>
        <button class="mn-btn primary" data-mn-pm-add="1">${this.showAddPm ? 'Close form' : '+ New PM plan'}</button>
        <label style="display:flex; flex-direction:row; align-items:center; gap:6px; text-transform:none; font-weight:500; font-size:12px; cursor:pointer;" title="When on, a missed inspection check auto-creates a Re-check work order. Default is OFF — the miss ledger still records everything.">
          <input type="checkbox" data-mn-autogen-recheck ${this.autogenFlags?.recheck_wos === true ? 'checked' : ''} ${this.autogenFlags == null ? 'disabled' : ''}/>
          Auto-create Re-check WOs from missed inspection checks <span style="color:var(--text-faint, #9ca3af);">(${this.autogenFlags == null ? 'loading…' : 'default off'})</span>
        </label>
      </div>`;

    const addForm = this.showAddPm ? this.renderPmForm(null) : '';

    let plans = this.pmPlans || [];
    if (this.pmListingFilter) plans = plans.filter(p => String(p.listing_id) === this.pmListingFilter);
    const list = plans.map(p => this.renderPmCard(p)).join('');
    const body = plans.length ? list
      : emptyState('No PM plans', this.pmPlans!.length ? 'No plans match the current filter.' : 'Create one to schedule recurring maintenance (interval, usage, or seasonal) into vacancy windows.', '🛠');

    return `
      <p class="mn-basis">A PM plan recurs on an interval (calendar), usage (booked nights), or seasonal trigger. "Generate WO" creates a work order scheduled into the property's next vacancy window; the plan advances only when that WO is marked done. ${this.pmPlans!.length} active plan(s).</p>
      ${dueHtml}
      ${filterBar}
      ${addForm}
      ${body}`;
  }

  private triggerSummary(t: any): string {
    if (!t) return '—';
    if (t.kind === 'interval') return `every ${t.every} ${t.unit}`;
    if (t.kind === 'usage') return `every ${t.every} booked nights`;
    if (t.kind === 'season') return `seasonal · ${MONTHS[t.season_month] || t.season_month}`;
    return String(t.kind);
  }

  private renderPmCard(p: any): string {
    if (this.editingPm === p.id) return this.renderPmForm(p);
    const due = this.pmDue?.due?.find((d: any) => d.plan?.id === p.id);
    const dueChip = due
      ? (due.due_now
        ? `<span class="mn-chip due">due now</span>`
        : `<span class="mn-chip">${due.next_due ? `due ${this.fmtDate(due.next_due)}` : 'no date yet'}</span>`)
      : '';
    const assetName = p.asset_id ? (this.assets || []).find(a => a.id === p.asset_id)?.name : '';
    return `
      <div class="mn-pm">
        <div class="mn-pm-head">
          <span class="mn-pm-name">${this.esc(p.name)}</span>
          ${dueChip}
          <span class="mn-pm-meta">${this.esc(p.listing_name)} · ${this.esc(this.triggerSummary(p.trigger))}${assetName ? ` · 🔧 ${this.esc(assetName)}` : ''}${p.last_done ? ` · last done ${this.fmtDate(p.last_done)}` : ' · never run'}</span>
          <span style="margin-left:auto; display:flex; gap:6px;">
            <button class="mn-btn primary sm" data-mn-pm-gen="${this.esc(p.id)}">Generate WO</button>
            <button class="mn-btn sm" data-mn-pm-edit="${this.esc(p.id)}">Edit</button>
            <button class="mn-btn danger sm" data-mn-pm-del="${this.esc(p.id)}">Delete</button>
          </span>
        </div>
      </div>`;
  }

  // Inline add/edit form for a PM plan. plan=null → new; else editing.
  private renderPmForm(plan: any | null): string {
    const p = plan || {};
    const editing = !!plan;
    const t = p.trigger || { kind: 'interval', every: 90, unit: 'days', season_month: 1 };
    const listingOpts = this.pmListingOpts();
    const selListing = editing ? String(p.listing_id) : (this.pmListingFilter || '');
    const assetsForListing = (this.assets || []).filter(a => String(a.listing_id) === selListing);
    const v = editing ? this.esc(p.id) : 'new';
    return `
      <div class="mn-pm-form" data-mn-pm-formfor="${v}">
        <label class="full">Name
          <input type="text" data-mn-pf="name" value="${this.esc(p.name || '')}" placeholder="e.g. HVAC filter swap, Deep clean, Winterize"/>
        </label>
        <label>Property
          <select data-mn-pf="listing_id" ${editing ? 'disabled' : ''}>
            <option value="">— pick —</option>
            ${[...listingOpts.entries()].map(([id, name]) => `<option value="${this.esc(id)}" ${selListing === id ? 'selected' : ''}>${this.esc(name)}</option>`).join('')}
          </select>
        </label>
        <label>Asset (optional)
          <select data-mn-pf="asset_id">
            <option value="">— none —</option>
            ${assetsForListing.map(a => `<option value="${this.esc(a.id)}" ${p.asset_id === a.id ? 'selected' : ''}>${this.esc(a.name)}</option>`).join('')}
          </select>
        </label>
        <label>Default vendor (optional)
          <select data-mn-pf="default_vendor_id">
            ${this.vendorOptions(p.default_vendor_id || null)}
          </select>
        </label>
        <label>Trigger
          <select data-mn-pf="kind">
            ${PM_TRIGGER_KINDS.map(([k, l]) => `<option value="${k}" ${t.kind === k ? 'selected' : ''}>${l}</option>`).join('')}
          </select>
        </label>
        <label>Every (N)
          <input type="number" min="1" data-mn-pf="every" value="${this.esc(t.every || 1)}"/>
        </label>
        <label data-mn-pf-unitwrap>Unit
          <select data-mn-pf="unit">
            <option value="days" ${t.unit === 'days' ? 'selected' : ''}>days</option>
            <option value="months" ${t.unit === 'months' ? 'selected' : ''}>months</option>
            <option value="nights" ${t.unit === 'nights' ? 'selected' : ''}>nights</option>
          </select>
        </label>
        <label data-mn-pf-seasonwrap>Season month
          <select data-mn-pf="season_month">
            ${MONTHS.slice(1).map((m, i) => `<option value="${i + 1}" ${Number(t.season_month) === i + 1 ? 'selected' : ''}>${m}</option>`).join('')}
          </select>
        </label>
        <div class="mn-pm-form-actions">
          <button class="mn-btn primary" data-mn-pm-save="${v}">${editing ? 'Save changes' : 'Add plan'}</button>
          <button class="mn-btn" data-mn-pm-cancel="${v}">Cancel</button>
        </div>
      </div>`;
  }

  // ── tab 2: work orders ───────────────────────────────────────
  private renderWorkOrders(): string {
    if (this.workOrders == null) return emptyState('Work orders unavailable', `GET ${API}/work-orders failed — check the server log.`, '⚠️');

    // listing options from the board (active listings) plus any listing referenced by a WO.
    const listingOpts = new Map<string, string>();
    (this.board?.rows || []).forEach((r: any) => listingOpts.set(String(r.listing_id), r.listing_name));
    (this.workOrders || []).forEach(w => { if (!listingOpts.has(String(w.listing_id))) listingOpts.set(String(w.listing_id), w.listing_name); });

    // Distinct assignees for the "My Queue" filter (a vendor/tech picks their
    // own name to see just their work). Today = priority emergency/urgent or a
    // scheduled window ending today/overdue.
    const assignees = [...new Set((this.workOrders || []).map(w => String(w.assigned_to || '').trim()).filter(Boolean))].sort();
    // A field tech's default scope is their OWN name even with zero matching
    // WOs — keep it in the options so the select shows the active filter.
    if (this.woAssigneeFilter && !assignees.some(a => a.toLowerCase() === this.woAssigneeFilter.trim().toLowerCase())) assignees.push(this.woAssigneeFilter);
    const assignDatalist = this.assignDatalist('mn-assign-roster');   // R53: pickable roster for every card's Assigned-to
    const todayStr = new Date().toISOString().slice(0, 10);
    const isUrgentToday = (w: any) => w.priority === 'emergency' || w.priority === 'urgent' ||
      (w.scheduled_window && w.scheduled_window.end && String(w.scheduled_window.end).slice(0, 10) <= todayStr && w.status !== 'done');

    // R109 #d: the search runs first (best match first) — every filter below
    // still applies to its results, including a field tech's own-name scope.
    const pq: any = this.woQ.trim() ? parseQuery(this.woQ, this.todayLocal()) : null;
    const searching = !!(pq && pq.active);
    let found: any = null;
    let rows: any[] = this.workOrders || [];
    if (searching) {
      const ctx = this.woSearchCtx();
      found = searchDocs(rows.map(w => this.woDoc(w, ctx)), pq);
      rows = found.rows.map((r: any) => r.w);
    }
    const matchCount = rows.length;
    if (this.woStatusFilter) rows = rows.filter(w => w.status === this.woStatusFilter);
    if (this.woListingFilter) rows = rows.filter(w => String(w.listing_id) === this.woListingFilter);
    if (this.woRoleFilter) rows = rows.filter(w => (w.role || 'maintenance') === this.woRoleFilter);
    if (this.woAssigneeFilter) { const af = this.woAssigneeFilter.trim().toLowerCase(); rows = rows.filter(w => String(w.assigned_to || '').trim().toLowerCase() === af); }
    if (this.woBilledFilter === 'unbilled') rows = rows.filter(w => !w.billing_status);
    else if (this.woBilledFilter === 'billed') rows = rows.filter(w => !!w.billing_status);
    // R110 #2: filter by HOW the owner was charged too (invoiced vs on the statement).
    else if (this.woBilledFilter === 'billed_owner' || this.woBilledFilter === 'billed_os') rows = rows.filter(w => w.billing_status === this.woBilledFilter);
    if (this.woUrgentOnly) rows = rows.filter(isUrgentToday);
    if (this.woReviewOnly) rows = rows.filter(w => this.pendingReview(w) > 0);
    // R50 — created-date range + explicit sort; list used to render storage
    // order (oldest first) which buried the newest work. Review fix: compare
    // in the DEVICE's local day, not the UTC slice — an evening-created WO
    // belongs to the day the user saw it created (the R25 float class).
    // R109 #c: "Completed" mode filters finished work on its completion day
    // and lists it by when it was finished.
    const byDone = this.woDateMode === 'completed';
    if (byDone) rows = rows.filter(w => this.woDoneAt(w));
    const dayOf = (w: any) => this.localDay(byDone ? this.woDoneAt(w) : w.created_at);
    if (this.woDateFrom) rows = rows.filter(w => dayOf(w) >= this.woDateFrom);
    if (this.woDateTo) rows = rows.filter(w => dayOf(w) <= this.woDateTo);
    if (!searching) rows = [...rows].sort((a, b) => {   // a search keeps its best-match-first order
      if (byDone) {
        const da = String(this.woDoneAt(a) || ''), db = String(this.woDoneAt(b) || '');
        const c = da < db ? 1 : da > db ? -1 : 0;
        return this.woSortDir === 'oldest' ? -c : c;
      }
      // Done WOs ALWAYS sink to the bottom, whatever the chosen sort.
      const doneCmp = (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0);
      if (doneCmp) return doneCmp;
      const cmp = a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0;
      return this.woSortDir === 'oldest' ? -cmp : cmp;
    });
    const hiddenByFilters = searching ? matchCount - rows.length : 0;
    const totalRows = rows.length;
    if (searching && rows.length > this.woQShown) rows = rows.slice(0, this.woQShown);   // rendering, not scoring, is the cost on a broad search
    const hl = found ? { terms: found.hlTerms as string[], byId: new Map<string, any>(found.rows.map((r: any) => [r.w.id, r])) } : null;

    // R109 #d: what the search did — count, how dates were read, spelling fixes, matches the filters hide
    const qInfo = !searching ? '' : (() => {
      const parts: string[] = [];
      if (found.loose) parts.push('<span class="loose">No work order matches every word — closest matches:</span>');
      parts.push(`<span><b>${totalRows}</b> match${totalRows === 1 ? '' : 'es'} for “${this.esc(this.woQ.trim())}” · best match first</span>`);
      const tgtLabel: Record<string, string> = { done: 'completed', created: 'created', due: 'due', sched: 'booked' };
      if (pq.ranges.length) parts.push(`<span>dates read as ${pq.ranges.map((r: any) => `${this.esc(r.label)}${r.target === 'any' ? ' — created, done, due or booked' : ` — ${tgtLabel[r.target] || r.target}`}`).join('; ')}</span>`);
      const fz = Object.entries(found.fuzzy || {}).filter(([, a]) => (a as string[]).length);
      if (fz.length) parts.push(`<span>spelling: ${fz.map(([t, a]) => `${this.esc(t)} → ${this.esc((a as string[]).slice(0, 3).join(' / '))}`).join(', ')}</span>`);
      if (hiddenByFilters > 0) parts.push(`<span>+${hiddenByFilters} more match outside your filters — <button class="mn-link" data-mn-wo-qallf title="Clear the filters, keep the search">show them</button></span>`);
      if (this.woGroupByProperty) parts.push('<span>grouping is off while searching</span>');
      parts.push('<button class="mn-link" data-mn-wo-qclear>clear search</button>');
      return `<div class="mn-wo-qinfo">${parts.join('')}</div>`;
    })();
    const qHelp = 'Type anything you remember — words from the title, the property (name or number), a person or vendor, the checklist, notes or comments. Typos are OK.\n'
      + 'Dates: today, yesterday, last week, this month, last 30 days, june, jun 5, 6/5 — add done / created / due to pick which date (done last week).\n'
      + 'Status: open, done, urgent, emergency, overdue, unassigned, unbilled, billed (billed = owner already charged — not who pays).\n'
      + '"quotes" keep words together · -word leaves it out.\n'
      + 'Enter opens the top match · Esc clears.';
    const filterBar = `
      <div class="mn-filters">
        <div class="mn-wo-qrow">
          <input type="search" class="mn-search mn-wo-q" data-mn-wo-q value="${this.esc(this.woQ)}" autocomplete="off" spellcheck="false" aria-label="Search work orders" placeholder="🔎 Find a work order — e.g. 740 kitchen light · Allan last week · done june · unbilled plumber">
          <span class="mn-wo-qhelp" title="${this.esc(qHelp)}">?</span>
        </div>
        <label>Status</label>
        <select data-mn-wofilter="status">
          <option value="">All</option>
          ${WO_STATUSES.map(([k, l]) => `<option value="${k}" ${this.woStatusFilter === k ? 'selected' : ''}>${l}</option>`).join('')}
        </select>
        <label>Route</label>
        <select data-mn-wofilter="role">
          <option value="">All</option>
          ${WO_ROLES.map(([k, l]) => `<option value="${k}" ${this.woRoleFilter === k ? 'selected' : ''}>${l}</option>`).join('')}
        </select>
        ${assignees.length ? `<label>Assignee</label>
        <select data-mn-wofilter="assignee" title="My Queue — pick a technician/vendor to see only their work">
          <option value="">Anyone</option>
          ${assignees.map(a => `<option value="${this.esc(a)}" ${this.woAssigneeFilter === a ? 'selected' : ''}>${this.esc(a)}</option>`).join('')}
        </select>` : ''}
        <button class="mn-btn ${this.woUrgentOnly ? 'primary' : ''}" data-mn-wo-urgent="1" title="Emergency/urgent or scheduled for today">🔥 Urgent / today</button>
        ${(() => { const n = (this.workOrders || []).reduce((a, w) => a + this.pendingReview(w), 0); return n || this.woReviewOnly ? `<button class="mn-btn ${this.woReviewOnly ? 'primary' : ''}" data-mn-wo-review="1" title="Vendor quotes and invoices waiting for a decision" style="${n && !this.woReviewOnly ? 'border-color:#f59e0b; color:#92400e; background:var(--warning-soft,#fffbeb);' : ''}">🔔 ${n} to review</button>` : ''; })()}
        <label>Sort</label>
        <select data-mn-wofilter="sortdir">
          <option value="newest" ${this.woSortDir === 'newest' ? 'selected' : ''}>Newest first</option>
          <option value="oldest" ${this.woSortDir === 'oldest' ? 'selected' : ''}>Oldest first</option>
        </select>
        <button class="mn-btn ${this.woGroupByProperty ? 'primary' : ''}" data-mn-wo-group="1" title="Group work orders under property headers">⊞ Group by property</button>
        <select data-mn-wofilter="datemode" title="Which date the range filters on — Completed lists finished work by the day it was done">
          <option value="created" ${!byDone ? 'selected' : ''}>Created</option>
          <option value="completed" ${byDone ? 'selected' : ''}>Completed</option>
        </select>
        <input type="date" data-mn-wofilter="datefrom" value="${this.esc(this.woDateFrom)}" title="${byDone ? 'Completed' : 'Created'} on/after">
        <label>to</label>
        <input type="date" data-mn-wofilter="dateto" value="${this.esc(this.woDateTo)}" title="${byDone ? 'Completed' : 'Created'} on/before">
        <label>Billed yet?</label>
        <select data-mn-wofilter="billed" title="Michelle's tracker — has the owner been charged yet, and how? (Not who pays — that's the payer on each work order.)">
          <option value="" ${!this.woBilledFilter ? 'selected' : ''}>All</option>
          <option value="unbilled" ${this.woBilledFilter === 'unbilled' ? 'selected' : ''}>Not billed yet</option>
          <option value="billed" ${this.woBilledFilter === 'billed' ? 'selected' : ''}>Billed (any)</option>
          <option value="billed_owner" ${this.woBilledFilter === 'billed_owner' ? 'selected' : ''}>Billed · invoiced</option>
          <option value="billed_os" ${this.woBilledFilter === 'billed_os' ? 'selected' : ''}>Billed · on statement</option>
        </select>
        <label>Property</label>
        <select data-mn-wofilter="listing">
          <option value="">All</option>
          ${[...listingOpts.entries()].map(([id, name]) => `<option value="${this.esc(id)}" ${this.woListingFilter === id ? 'selected' : ''}>${this.esc(name)}</option>`).join('')}
        </select>
        <button class="mn-btn primary" data-mn-newwo="${this.woListingFilter ? this.esc(this.woListingFilter) + '|' + this.esc(listingOpts.get(this.woListingFilter) || '') : ''}">+ New work order</button>
        <button class="mn-btn" data-mn-wocsv title="Download every loaded work order as a CSV (Michelle's ask — same as the Resolutions export)">⬇ CSV</button>
        ${this.woListingFilter ? `<button class="mn-btn" data-mn-owner-summary="${this.esc(this.woListingFilter)}">Copy owner summary</button>` : ''}
      </div>
      ${qInfo}
      ${MaintenancePage.fieldRole() === 'maintenance' ? `<p class="mn-basis">showing your work orders — switch Assignee to Anyone to see all WOs at a property</p>` : ''}
      ${assignDatalist}
      ${this.dayPlanHtml()}`;

    if (!rows.length) {
      if (searching) return filterBar + emptyState('No matching work orders', `Nothing matches “${this.esc(this.woQ.trim())}”${hiddenByFilters > 0 ? ` with these filters — ${hiddenByFilters} match outside them (use “show them” above)` : ''}. Try fewer or different words.`, '🔎');
      return filterBar + emptyState('No work orders', this.workOrders!.length ? `No work orders match the current filters.${byDone ? ' Completed shows finished work only.' : ''}` : "Create one from a PM template (Start PM on the Board) or '+ New work order' above.", '🔧');
    }

    let list: string;
    if (searching) {
      // R109 #d: best match first, matched words highlighted — grouping would scramble the ranking
      list = rows.map(w => this.renderWoCard(w, hl)).join('')
        + (totalRows > rows.length ? `<div style="text-align:center; margin:10px 0 20px;"><button class="mn-btn" data-mn-wo-qmore>Show ${Math.min(100, totalRows - rows.length)} more · ${totalRows - rows.length} not shown</button></div>` : '');
    } else if (this.woGroupByProperty) {
      // Group cards under property headers (open count per group) — same card
      // markup/handlers, just partitioned by listing.
      const groups = new Map<string, any[]>();
      for (const w of rows) {
        const key = String(w.listing_name || w.listing_id || '—');
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(w);
      }
      list = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([name, ws]) => {
        const openCount = ws.filter(x => x.status !== 'done').length;
        return `
          <div style="margin:16px 0 7px; display:flex; align-items:baseline; gap:8px;">
            <span style="font-size:12.5px; font-weight:700; color:var(--text);">${this.esc(name)}</span>
            <span style="font-size:10.5px; font-weight:600; color:var(--text-muted);">${openCount} open · ${ws.length} shown</span>
          </div>
          ${ws.map(w => this.renderWoCard(w)).join('')}`;
      }).join('');
    } else {
      list = rows.map(w => this.renderWoCard(w)).join('');
    }
    return `
      <p class="mn-basis">${totalRows} of ${this.workOrders!.length} work orders ${searching ? 'match' : 'shown'}${this.woStatusFilter ? ` · status '${this.woStatusFilter}'` : ''}${this.woListingFilter ? ` · ${this.esc(listingOpts.get(this.woListingFilter) || this.woListingFilter)}` : ''}${byDone ? ` · completed${this.woDateFrom || this.woDateTo ? ` ${this.woDateFrom ? this.fmtDate(this.woDateFrom) : 'any time'} → ${this.woDateTo ? this.fmtDate(this.woDateTo) : 'today'}` : ' (any date)'}${searching ? '' : `, ${this.woSortDir === 'oldest' ? 'oldest' : 'most recently'} finished first`} — your local day` : ''}. Click a row to open its checklist.</p>
      ${filterBar}
      ${list}`;
  }

  // R105 #3 (Jesse: "internal comments on these WOs so accounting and the ground
  // team can communicate"): the thread lives on the WO (wo.comments) and shows
  // here AND under the QuickBooks line on Supplies & Expenses. Drafts are
  // state-backed so a card redraw can't eat a half-typed comment.
  private cmtDrafts: Record<string, string> = {};
  // One thread: the pre-R105 🔒 internal notes (32 live WOs) show as older
  // entries so the conversation isn't split across two boxes.
  private woCommentsList(w: any): string {
    const legacy: any[] = (Array.isArray(w.internal_notes) ? w.internal_notes : []).map((n: any) => ({ ...n, legacy: true }));
    const list: any[] = [...legacy, ...(Array.isArray(w.comments) ? w.comments : [])].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
    const admin = ['admin', 'director'].includes((() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })());
    return `<div class="mn-cmts-h">💬 Internal comments${list.length ? ` <span class="mn-pill" style="background:var(--bg-subtle); color:var(--text-muted);">${list.length}</span>` : ''} <span class="mn-sub">accounting ↔ ground team · never shown to owners or vendors</span></div>
        ${list.slice(-40).map((c: any) => `<div class="mn-cmt"><div class="mn-cmt-h"><b>${this.esc(c.by || '—')}</b>${c.legacy ? ' · <span class="mn-sub">note</span>' : (c.role ? ` · ${this.esc(String(c.role).replace('_', ' '))}` : '')} · ${this.esc(String(c.at || '').slice(0, 16).replace('T', ' '))}${admin && !c.legacy && c.id ? ` <a data-mn-cmt-rm="${this.esc(w.id)}|${this.esc(c.id)}" title="Remove this comment (admin)" style="cursor:pointer; color:var(--text-faint);">✕</a>` : ''}</div><div class="mn-cmt-t">${this.esc(c.text)}</div></div>`).join('')}`;
  }
  private woCommentsHtml(w: any): string {
    const draft = this.cmtDrafts[w.id] || '';
    return `
      <div class="mn-cmts">
        <div data-mn-cmts-list="${this.esc(w.id)}">${this.woCommentsList(w)}</div>
        <div class="mn-cmt-compose"><textarea data-mn-cmt-draft="${this.esc(w.id)}" rows="1" placeholder="Comment for the team… (⌘/Ctrl+Enter posts)">${this.esc(draft)}</textarea><button class="mn-btn sm primary" data-mn-cmt-post="${this.esc(w.id)}" ${draft.trim() ? '' : 'disabled'}>Post</button></div>
      </div>`;
  }
  // After a post/remove: patch ONLY the thread list (a full draw() would revert
  // every unsaved edit elsewhere on the card — the internal-notes rule).
  private patchWoComments(id: string): void {
    const wo = (this.workOrders || []).find(w => w.id === id);
    const host = this.container.querySelector<HTMLElement>(`[data-mn-cmts-list="${CSS.escape(id)}"]`);
    if (!wo || !host) { this.draw(); return; }
    host.innerHTML = this.woCommentsList(wo);
    this.bindCmtRemove(host);
    const ta = this.container.querySelector<HTMLTextAreaElement>(`[data-mn-cmt-draft="${CSS.escape(id)}"]`);
    const btn = this.container.querySelector<HTMLButtonElement>(`[data-mn-cmt-post="${CSS.escape(id)}"]`);
    if (ta) ta.value = this.cmtDrafts[id] || '';
    if (btn) btn.disabled = !(this.cmtDrafts[id] || '').trim();
  }
  private bindCmtRemove(scope: ParentNode): void {
    scope.querySelectorAll<HTMLElement>('[data-mn-cmt-rm]').forEach(el => el.addEventListener('click', async () => {
      const [id, cid] = el.dataset.mnCmtRm!.split('|');
      if (!confirm('Remove this comment?')) return;
      const j = await this.post(`/work-orders/${encodeURIComponent(id)}/comments/remove`, { comment_id: cid, by: this.who() });
      if (!j) return;
      const wo = (this.workOrders || []).find(w => w.id === id); if (wo) wo.comments = j.comments;
      this.patchWoComments(id);
    }));
  }
  private who(): string { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || '').trim(); } catch { return ''; } }

  // hl (R109 #d): set only by the Work orders search — the matched words are
  // highlighted on the card, and a match in a field the collapsed card doesn't
  // show (instructions, checklist, notes, comments…) gets a "found in" line.
  private renderWoCard(w: any, hl?: { terms: string[]; byId: Map<string, any> } | null): string {
    const open = this.expandedWo.has(w.id);
    const doneCount = (w.items || []).filter((it: any) => it.done).length;
    const total = (w.items || []).length;
    const typeTag = w.type === 'pm'
      ? `<span class="mn-pill" style="background:var(--info-soft, #dbeafe); color:var(--info, #1d4ed8);">PM</span>`
      : `<span class="mn-pill" style="background:var(--bg-subtle); color:var(--text-muted);">WO</span>`;

    let bodyHtml = '';
    if (open) {
      const items = (w.items || []).map((it: any) => {
        // R108: videos from the issue (merged quick-logs keep theirs); R116 #5: one strip — clips play in place
        const photos = this.itemMediaHtml(it);
        return `
          <div class="mn-item">
            <div class="mn-item-head">
              <div class="mn-check ${it.done ? 'done' : ''}" data-mn-toggle="${this.esc(w.id)}|${this.esc(it.key)}" title="${it.done ? 'Mark not done' : 'Mark done'}">${it.done ? '✓' : ''}</div>
              <div style="flex:1; min-width:0;">
                <div class="mn-item-title">${this.esc(it.title)}</div>
                ${it.instructions ? `<div class="mn-item-instr">${this.esc(it.instructions)}</div>` : ''}
                ${it.note ? `<div class="mn-item-note">“${this.esc(it.note)}”</div>` : ''}
                <div data-mn-itemmedia="${this.esc(w.id)}|${this.esc(it.key)}">${photos}</div>
                <div class="mn-item-actions">
                  <a data-mn-itemnote="${this.esc(w.id)}|${this.esc(it.key)}">Add note</a>
                  <a data-mn-itemphoto="${this.esc(w.id)}|${this.esc(it.key)}">Attach photos / video</a>
                </div>
              </div>
            </div>
          </div>`;
      }).join('');

      // Before/after photo groups (Mark: "give me a picture… pre-photos, post-photos").
      // R116 #5: built by woMediaGroups (clips join their kind group) and patched
      // in place when a background upload lands.
      const photoGroups = `<div data-mn-media="${this.esc(w.id)}">${this.woMediaGroups(w)}</div>`;

      // Instructions block — the office's briefing for the tech; ✎ swaps in a
      // textarea + Save (mirrors the R57 inline title rename).
      const descHtml = this.editingWoDesc === w.id
        ? `<textarea data-mn-wodesc-input="${this.esc(w.id)}" style="width:100%; box-sizing:border-box; padding:7px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px; font-family:inherit; resize:vertical; min-height:60px; margin-top:4px;" placeholder="Instructions — what needs to get done, where, what you need after…">${this.esc(w.description || '')}</textarea>
           <button class="mn-btn sm primary" data-mn-wodesc-save="${this.esc(w.id)}" style="margin-top:4px;">Save</button>`
        : (w.description
          ? `<div style="white-space:pre-wrap; font-size:12.5px; margin-top:4px;">${this.esc(w.description)}</div>`
          : `<div style="font-size:11.5px; color:var(--text-faint,#9ca3af); margin-top:4px;">No instructions yet — ✎ to add.</div>`);

      const assetsForWo = (this.assets || []).filter(a => String(a.listing_id) === String(w.listing_id));
      bodyHtml = `
        <div class="mn-wo-body">
          <div style="margin-bottom:10px;">
            <label style="font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">Instructions <a data-mn-wodesc-edit="${this.esc(w.id)}" title="Edit instructions" style="cursor:pointer;">✎</a></label>
            ${descHtml}
          </div>
          ${total ? items : '<div style="font-size:12px; color:var(--text-muted); margin-bottom:8px;">No checklist items — ad-hoc work order.</div>'}
          ${this.woCommentsHtml(w)}
          ${(() => {
            // R101 #8: four labeled groups (who / when / money / access) and ONE
            // action bar — same fields, same data attributes, less noise.
            // R115 maint: the fieldsets come from the shared WoEditor (the calendar's
            // WO inspector renders the same markup; one diff builds both bodies).
            return `${woEditFormHtml(w, { money: true, vendors: this.vendors || [], assets: assetsForWo, rosterId: 'mn-assign-roster' })}
          <div class="mn-wo-actions">
            <button class="mn-btn primary" data-mn-wosave="${this.esc(w.id)}">Save</button>
            <button class="mn-btn sm" data-mn-wotext="${this.esc(w.id)}" title="Text this work order to a vendor with a no-login link — they quote, update progress and invoice from their phone">📲 Text vendor</button>
            <details class="mn-dd"><summary class="mn-btn sm" title="Photos or video — pick several at once">📷 Photos &amp; video ▾</summary><div class="mn-dd-menu">
              <button class="mn-btn sm" data-mn-wophoto="${this.esc(w.id)}">Attach photos / video</button>
              <button class="mn-btn sm" data-mn-wophoto-kind="${this.esc(w.id)}|before">📷 Before</button>
              <button class="mn-btn sm" data-mn-wophoto-kind="${this.esc(w.id)}|after">✅ After</button>
              <button class="mn-btn sm" data-mn-wophoto-kind="${this.esc(w.id)}|receipt" title="Receipts ride the WO so Michelle can code and bill the owner">🧾 Receipt</button>
            </div></details>
            ${this.opButtons(w)}
            <span style="flex:1;"></span>
            ${MaintenancePage.fieldRole() !== 'maintenance' ? `<button class="mn-btn danger sm" data-mn-wodel="${this.esc(w.id)}" title="Delete this work order">🗑 Delete</button>` : ''}
          </div>`;
          })()}
          <p class="mn-honest" style="margin-top:8px;">Cost &amp; payer are Asset Ops' own record — not synced to owner statements (those are generated externally).</p>
          ${this.renderTimePanel(w)}
          ${this.renderVendorFlow(w)}
          ${this.renderCompletion(w)}
          <label style="display:block; margin-top:10px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">Owner-facing summary (the "maintenance done" record)</label>
          <textarea data-mn-summary="${this.esc(w.id)}" style="width:100%; box-sizing:border-box; padding:7px 9px; border:1px solid var(--border); border-radius:6px; font-size:12px; font-family:inherit; resize:vertical; min-height:50px; margin-top:4px;" placeholder="What was done, in owner-readable language. Becomes part of the owner report.">${this.esc(w.summary)}</textarea>
          ${this.renderInternalNotes(w)}
          ${photoGroups}
          ${this.renderFieldPanel(w)}
          ${this.woDoneAt(w) ? `<div style="font-size:11px; color:var(--text-muted); margin-top:8px;">Completed ${this.fmtDateTime(this.woDoneAt(w))}</div>` : ''}
        </div>`;
    }

    const prio = this.woPriority(w);
    // R109 #d: escape-safe highlight (ranges found on the raw text, each piece escaped)
    const H = (s: any): string => (hl ? hlText(s == null ? '' : String(s), hl.terms, (x: string) => this.esc(x)) : this.esc(s));
    const foundIn = (() => {
      const r = hl && hl.byId.get(w.id);
      const h = r && (r.hits || []).find((x: any) => HIDDEN_FIELDS.has(x.field));
      if (!h) return '';
      return `<div class="mn-wo-found" title="${this.esc(String(h.raw || '').slice(0, 400))}">🔎 found in ${this.esc(FIELD_LABEL[h.field] || h.field)}: ${hlSnippet(h.raw, hl!.terms, (x: string) => this.esc(x), 60)}</div>`;
    })();
    const doneAt = this.woDoneAt(w);   // R109 #c: a reopened WO shows "opened", never its stale completion
    const assetName = w.asset_id ? (this.assets || []).find(a => a.id === w.asset_id)?.name : '';
    const vendorName = w.vendor_id ? (this.vendors || []).find(v => v.id === w.vendor_id)?.name : '';
    const costTag = w.cost != null ? ` · ${this.fmtMoney(w.cost)}${w.payer ? ` (${w.payer === 'wch' ? 'WCH pays' : 'owner pays'})` : ''}` : '';
    // R110 #2: payer vs billed at a glance — an owner-pays card carries Michelle's billed
    // state beside the cost: amber until the owner has actually been charged, green with HOW.
    const billedTag = w.payer === 'owner'
      ? (w.billing_status
        ? ` · <span class="mn-pill" style="background:var(--success-soft,#ecfdf5); color:var(--success-text,#065f46);" title="${this.esc(WO_BILLED_TITLE[w.billing_status] || 'Owner has been charged')}">${this.esc(WO_BILLED_LABEL[w.billing_status] || 'billed')}</span>`
        : ` · <span class="mn-pill" style="background:var(--warning-soft,#fffbeb); color:var(--warning-text,#92400e);" title="Owner pays, but hasn't been charged yet (Billed yet? is empty)">unbilled</span>`)
      : '';
    const timeTag = w.timer_started_at ? ` · <span class="mn-time-run">⏱ running</span>` : (Number(w.total_minutes) ? ` · ⏱ ${this.fmtMins(w.total_minutes)}` : '');
    // Due-date chip — red when past due and not done. Bare YYYY-MM-DD pinned to
    // UTC (same rule as fmtDate) so it never renders the previous day.
    const dueTag = (() => {
      // R70: checkout-tracked WOs show the LIVE effective due, never the stale
      // fixed date (which would flag a fresh WO red as "past due").
      if (w.due_source === 'checkout') {
        const eff = w.effective_due ? new Date(String(w.effective_due) + 'T00:00:00Z') : null;
        const effLabel = eff && !isNaN(eff.getTime()) ? eff.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '';
        return ` · <span style="color:#1d4ed8;" title="Due tracks this property's next checkout${w.effective_due ? ` — currently ${this.esc(w.effective_due)}` : ''}">🔁 due at checkout${effLabel ? ` (${effLabel})` : ''}</span>`;
      }
      if (!w.due_date) return '';
      const d = new Date(String(w.due_date) + 'T00:00:00Z');
      if (isNaN(d.getTime())) return '';
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      const past = (this.daysFromToday(String(w.due_date)) ?? 0) < 0 && w.status !== 'done';
      return ` · <span style="${past ? 'color:var(--danger-text, #b91c1c); font-weight:700;' : ''}" title="due ${this.esc(w.due_date)}${past ? ' — past due' : ''}">📅 due ${label}</span>`;
    })();
    const claimTag = w.claim_tracked ? ` · <span class="mn-pill" style="background:var(--bg-subtle); color:var(--text-muted);" title="Tracked for an insurance / damage claim">⚖ claim</span>` : '';
    // R102 #5: the booked day (red once it has passed and the WO is still open) + owner-portal state.
    const schedTag = (() => {
      const sd = this.schedOf(w); if (!sd || w.status === 'done') return '';
      const late = sd < this.todayLocal();
      return ` · <span style="${late ? 'color:var(--danger-text,#b91c1c); font-weight:700;' : 'color:#3730a3;'}" title="booked ${this.esc(sd)}${late ? ' — day has passed, rebook or close' : ''}">📆 ${late ? 'was booked' : 'booked'} ${this.fmtDay(sd)}</span>`;
    })();
    const opTag = w.owner_portal && w.owner_portal.state ? ` · ${this.opChip(w)}` : '';
    const laborTag = (() => { const l = this.laborCost(w); return l != null ? ` · 💵 labor ≈ ${this.fmtMoney(l)}` : ''; })();
    // R60: a filed report that flagged more work keeps the WO open AND visibly
    // badged — a follow-up that only lives inside the expanded card dies quietly.
    // R101 #5: a vendor quote / invoice waiting on the office is visible from the list
    const reviewN = this.pendingReview(w);
    const reviewTag = reviewN ? ` · <span style="background:var(--warning-soft,#fffbeb); color:#92400e; border:1px solid #fcd34d; border-radius:999px; padding:1px 7px; font-size:10.5px; font-weight:700;" title="Vendor quote or invoice awaiting your decision — open the card">🔔 ${reviewN} to review</span>` : '';
    const fuTag = (w.completion && w.completion.needs_followup && w.status !== 'done')
      ? ` · <span style="background:var(--warning-soft,#fffbeb); color:var(--warning-text,#92400e); border-radius:999px; padding:1px 7px; font-size:10.5px; font-weight:700;" title="${this.esc(w.completion.followup_note || 'The tech flagged more work/inspection needed')}">🔁 Follow-up</span>` : '';
    const allDoneTag = this.allItemsDoneTag(w);   // R116 Q7
    // Inline title rename (open card only) — ✏️ swaps the title for an input + Save.
    const titleHtml = this.editingWoTitle === w.id
      ? `<input type="text" data-mn-wotitle-input="${this.esc(w.id)}" value="${this.esc(w.title)}" style="padding:4px 8px; border:1px solid var(--border); border-radius:6px; font-size:12.5px; font-family:inherit; width:min(280px, 60%);"/> <button class="mn-btn sm primary" data-mn-wotitle-save="${this.esc(w.id)}">Save</button>`
      : `${H(w.title)}${open ? ` <button class="mn-btn sm" data-mn-wotitle-edit="${this.esc(w.id)}" title="Rename this work order" style="padding:2px 6px;">✏️</button>` : ''}`;
    return `
      <div class="mn-wo">
        <div class="mn-wo-row" data-mn-wotoggle="${this.esc(w.id)}">
          <div style="min-width:0;">
            <div class="mn-wo-title">${typeTag} ${this.priorityBadge(prio)} ${w.role && w.role !== 'maintenance' ? `<span class="mn-pill mn-role-${this.esc(w.role)}">${this.esc(WO_ROLE_LABEL[w.role] || w.role)}</span> ` : ''}${titleHtml}</div>
            <div class="mn-wo-meta">${H(w.listing_name)}${w.created_by ? ` · <span title="Submitted by — ask them for more info">✍️ ${H(w.created_by)}</span>` : ''}${w.assigned_to ? ` · ${H(w.assigned_to)}` : ''}${assetName ? ` · 🔧 ${H(assetName)}` : ''}${vendorName ? ` · 🏷 ${H(vendorName)}` : ''}${costTag}${billedTag}${timeTag}${laborTag}${schedTag}${dueTag}${claimTag}${fuTag}${allDoneTag}${reviewTag}${opTag}</div>
            ${foundIn}
          </div>
          <div style="font-size:11px; color:var(--text-muted);">${total ? `${doneCount}/${total} items done` : 'ad-hoc'}</div>
          <div>${this.statusPill(w.status)}</div>
          <div style="font-size:11px; color:var(--text-muted);" title="opened ${this.esc(this.fmtDate(w.created_at))}${doneAt ? ` · completed ${this.esc(this.fmtDateTime(doneAt))}` : ''}">${doneAt ? `done ${this.fmtDate(doneAt)}` : `opened ${this.fmtDate(w.created_at)}`}</div>
          <div class="mn-wo-caret">${open ? '▾' : '▸'}</div>
        </div>
        ${bodyHtml}
      </div>`;
  }

  // R116 Q7 (Madel): an open WO whose every checklist item is ticked — the
  // work looks finished but nobody closed it (Issues reads its issues as
  // resolved / "vendor says done" meanwhile). A flag, never an auto-close:
  // open the card and file the report to close it.
  private allItemsDoneTag(w: any): string {
    const items = Array.isArray(w && w.items) ? w.items.filter(Boolean) : [];
    if (!w || w.status === 'done' || !items.length || !items.every((i: any) => i.done)) return '';
    const byVendor = items.some((i: any) => /^vendor:/.test(String(i.closed_by || '')));
    return ` · <span class="mn-alldone" style="background:var(--success-soft); color:var(--success-text); border:1px solid var(--success-border); border-radius:999px; padding:1px 8px; font-size:10.5px; font-weight:700; white-space:nowrap;" title="${byVendor ? 'The vendor ticked the last items — check the work, then file the report to close it' : 'Every checklist item is ticked — file the report to close it'}">✓ all items done · close?</span>`;
  }

  // Compact "Field access" panel for an expanded high-priority work order
  // (priority emergency/urgent, or sourced from an incident). Shows the
  // guest-present courtesy banner when the home is occupied + the access lines
  // a tech needs on the phone. Data is the per-listing cache populated lazily on
  // expand; renders a tiny loading note until it arrives. Mobile-legible (~390px).
  private renderFieldPanel(w: any): string {
    const prio = this.woPriority(w);
    const wants = prio === 'emergency' || prio === 'urgent' || w.source === 'incident';
    if (!wants) return '';
    const f = this.fieldByListing[String(w.listing_id)];
    if (!f) {
      return `<div class="mn-field"><div class="mn-field-h">Field access</div><div class="mn-field-loading">Loading access details…</div></div>`;
    }
    const a = f.access || {};
    const line = (label: string, val: any) => {
      const v = String(val == null ? '' : val).trim();
      if (!v) return '';
      return `<div class="mn-field-line"><span class="mn-field-k">${this.esc(label)}</span><span class="mn-field-v">${this.esc(v)}</span></div>`;
    };
    const lines = [
      line('Lockbox', a.lockbox),
      line('Backup code', a.backup_code),
      line('Gate', a.gate),
      line('Breaker', a.breaker),
      line('Breaker reset', a.breaker_reset),
      line('Water shutoff', a.water_shutoff),
      line('Water heater', a.water_heater),
      line('HVAC', a.hvac),
      line('Safety', a.safety),
    ].filter(Boolean).join('');
    return `
      <div class="mn-field">
        <div class="mn-field-h">Field access${f.listing_name ? ` · ${this.esc(f.listing_name)}` : ''}</div>
        ${f.occupied ? `<div class="mn-field-guest">⚠ ${this.esc(f.guest_present_note)}</div>` : ''}
        ${lines || '<div class="mn-field-loading">No access details on file for this property.</div>'}
      </div>`;
  }

  // ── tab 3: assets (registry per property) ────────────────────
  // Property options come from the board (active listings) plus any listing a
  // known asset references — same source pattern the work-orders tab uses.
  private assetListingOpts(): Map<string, string> {
    const opts = new Map<string, string>();
    (this.board?.rows || []).forEach((r: any) => opts.set(String(r.listing_id), r.listing_name));
    (this.assets || []).forEach(a => { if (!opts.has(String(a.listing_id))) opts.set(String(a.listing_id), a.listing_name); });
    return opts;
  }

  private renderAssets(): string {
    if (this.assets == null) return emptyState('Assets unavailable', `GET ${API}/assets failed — check the server log.`, '⚠️');
    const listingOpts = this.assetListingOpts();
    const picker = `
      <div class="mn-filters">
        <label>Property</label>
        <select data-mn-astfilter>
          <option value="">— pick a property —</option>
          ${[...listingOpts.entries()].map(([id, name]) => `<option value="${this.esc(id)}" ${this.assetListingFilter === id ? 'selected' : ''}>${this.esc(name)}</option>`).join('')}
        </select>
        ${this.assetListingFilter ? `
          <button class="mn-btn" data-mn-ast-seed="${this.esc(this.assetListingFilter)}">Seed from PDS</button>
          <button class="mn-btn" data-mn-care-report="${this.esc(this.assetListingFilter)}" title="Open the photo-forward owner care report — Print / Save as PDF from there">📄 Care report</button>
          <button class="mn-btn primary" data-mn-ast-add="1">${this.showAddAsset ? 'Close form' : '+ Add asset'}</button>
        ` : ''}
      </div>`;

    if (!this.assetListingFilter) {
      return picker + emptyState('Pick a property', 'Choose a property above to see and manage its asset registry (appliances, HVAC, water heater, …).', '🏠');
    }

    const mine = (this.assets || []).filter(a => String(a.listing_id) === this.assetListingFilter);
    const addForm = this.showAddAsset ? this.renderAssetForm(null) : '';

    const list = mine.map(a => this.renderAssetCard(a)).join('');
    const body = mine.length
      ? list
      : emptyState('No assets yet', 'Use "Seed from PDS" to pull appliances/HVAC/water heater from this property\'s details, or "+ Add asset" to enter one.', '🔧');

    return `
      <p class="mn-basis">The asset registry tracks the physical things you maintain. A work order can be linked to an asset; an asset's service history is every work order linked to it. ${mine.length} asset(s) on file for this property.</p>
      ${picker}
      ${addForm}
      ${body}`;
  }

  // Inline add/edit form. asset=null → new; else editing an existing asset.
  private renderAssetForm(asset: any | null): string {
    const a = asset || {};
    const editing = !!asset;
    const cats = ASSET_CATEGORIES.map(c => `<option value="${c}" ${(a.category || 'Other') === c ? 'selected' : ''}>${c}</option>`).join('');
    return `
      <div class="mn-ast-form" data-mn-ast-formfor="${editing ? this.esc(a.id) : 'new'}">
        <label class="full">Name
          <input type="text" data-mn-af="name" value="${this.esc(a.name || '')}" placeholder="e.g. Refrigerator, Furnace, Pool pump"/>
        </label>
        <label>Category
          <select data-mn-af="category">${cats}</select>
        </label>
        <label>Make
          <input type="text" data-mn-af="make" value="${this.esc(a.make || '')}" placeholder="brand"/>
        </label>
        <label>Model
          <input type="text" data-mn-af="model" value="${this.esc(a.model || '')}" placeholder="model #"/>
        </label>
        <label>Serial
          <input type="text" data-mn-af="serial" value="${this.esc(a.serial || '')}" placeholder="serial #"/>
        </label>
        <label class="full">Location
          <input type="text" data-mn-af="location_note" value="${this.esc(a.location_note || '')}" placeholder="where it lives (e.g. garage, attic)"/>
        </label>
        <label class="full">Warranty / notes
          <input type="text" data-mn-af="warranty_note" value="${this.esc(a.warranty_note || '')}" placeholder="warranty expiry, vendor, notes"/>
        </label>
        <label class="full">Photo URL ${a.source === 'pds' && a.photo_url ? '<span style="font-weight:400; color:var(--text-muted);">— pulled from the property details sheet</span>' : ''}
          <input type="text" data-mn-af="photo_url" value="${this.esc(a.photo_url || '')}" placeholder="appliance photo link (auto-filled when seeded from PDS)"/>
        </label>
        ${a.photo_url ? `<div class="full"><img src="${this.esc(a.photo_url)}" alt="" style="max-width:140px; max-height:120px; border-radius:6px; border:1px solid var(--border); object-fit:cover;"/></div>` : ''}
        <div class="mn-ast-form-actions">
          <button class="mn-btn primary" data-mn-ast-save="${editing ? this.esc(a.id) : 'new'}">${editing ? 'Save changes' : 'Add asset'}</button>
          <button class="mn-btn" data-mn-ast-cancel="${editing ? this.esc(a.id) : 'new'}">Cancel</button>
        </div>
      </div>`;
  }

  private renderAssetCard(a: any): string {
    const open = this.expandedAsset.has(a.id);
    const ids = [a.make, a.model, a.serial].map(s => String(s || '').trim()).filter(Boolean).join(' / ');
    const editing = open && this.editingAsset === a.id;
    const intel = (this.assetIntel || []).find(i => i.id === a.id);
    const reviewBadge = intel?.flag === 'review'
      ? `<span class="mn-pill" style="background:var(--warning-soft); color:var(--warning-text);" title="${this.esc(intel.flag_reason)}">⚠ Review</span>`
      : '';

    let bodyHtml = '';
    if (open) {
      if (editing) {
        bodyHtml = `<div class="mn-ast-body">${this.renderAssetForm(a)}</div>`;
      } else {
        const hist = this.assetHistory[a.id];
        let histHtml: string;
        if (!hist) {
          histHtml = `<div style="font-size:12px; color:var(--text-muted);">Loading service history…</div>`;
        } else if (!hist.history.length) {
          histHtml = `<div style="font-size:12px; color:var(--text-muted);">No work orders linked to this asset yet.</div>`;
        } else {
          histHtml = hist.history.map(w => `
            <div class="mn-ast-hist">
              ${this.priorityBadge(this.woPriority(w))}
              ${this.statusPill(w.status)}
              <span style="font-weight:600;">${this.esc(w.title)}</span>
              <span style="color:var(--text-muted); margin-left:auto;">${this.woDoneAt(w) ? `done ${this.fmtDate(this.woDoneAt(w))}` : `opened ${this.fmtDate(w.created_at)}`}</span>
            </div>`).join('');
        }
        const costToDate = intel ? this.fmtMoney(intel.cost_to_date) : null;
        const idLine = [['Make', a.make], ['Model', a.model], ['Serial', a.serial]]
          .filter(([, v]) => String(v || '').trim())
          .map(([k, v]) => `<span style="color:var(--text-muted);">${k}:</span> ${this.esc(String(v))}`).join('&nbsp;&nbsp;·&nbsp;&nbsp;');
        bodyHtml = `
          <div class="mn-ast-body">
            ${a.photo_url ? `<a href="${this.esc(a.photo_url)}" target="_blank" rel="noopener"><img src="${this.esc(a.photo_url)}" alt="${this.esc(a.name)}" style="max-width:180px; max-height:160px; border-radius:8px; border:1px solid var(--border); margin-bottom:8px; object-fit:cover;"/></a>` : ''}
            ${idLine ? `<div style="font-size:12px; margin-bottom:6px;">${idLine}</div>` : ''}
            ${a.location_note ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Location:</span> ${this.esc(a.location_note)}</div>` : ''}
            ${a.warranty_note ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Warranty/notes:</span> ${this.esc(a.warranty_note)}</div>` : ''}
            ${intel ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Spend to date:</span> <span class="mn-mono">${costToDate}</span> over ${intel.wo_count} work order(s)</div>` : ''}
            ${intel?.flag === 'review' ? `<div class="mn-honest" style="margin-bottom:8px;">⚠ ${this.esc(intel.flag_reason)}. Asset Ops spend — not synced to owner statements.</div>` : ''}
            ${a.source === 'pds' ? `<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-bottom:8px;">Seeded from property details${a.pds_field ? ` (${this.esc(a.pds_field)})` : ''}.</div>` : ''}
            <div style="font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; color:var(--text-muted); margin-bottom:6px;">Service history${hist ? ` · ${hist.open_count} open` : ''}</div>
            ${histHtml}
            <div style="display:flex; gap:8px; margin-top:10px;">
              <button class="mn-btn sm" data-mn-ast-edit="${this.esc(a.id)}">Edit</button>
              <button class="mn-btn danger sm" data-mn-ast-del="${this.esc(a.id)}">Delete</button>
            </div>
          </div>`;
      }
    }

    return `
      <div class="mn-ast">
        <div class="mn-ast-row" data-mn-asttoggle="${this.esc(a.id)}">
          <div style="min-width:0; display:flex; align-items:center; gap:8px;">
            ${a.photo_url ? `<img src="${this.esc(a.photo_url)}" alt="" style="width:40px; height:40px; border-radius:6px; object-fit:cover; border:1px solid var(--border); flex-shrink:0;"/>` : ''}
            <div style="min-width:0;">
              <div class="mn-ast-name">${this.esc(a.name)} ${reviewBadge}</div>
              ${ids ? `<div class="mn-ast-sub">${this.esc(ids)}</div>` : ''}
            </div>
          </div>
          <div><span class="mn-ast-cat">${this.esc(a.category || 'Other')}</span></div>
          <div style="font-size:11px; color:var(--text-muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(a.location_note || '—')}</div>
          <div style="font-size:11px; color:var(--text-muted);">${a.source === 'pds' ? 'from PDS' : 'manual'}</div>
          <div class="mn-ast-caret">${open ? '▾' : '▸'}</div>
        </div>
        ${bodyHtml}
      </div>`;
  }

  // ── tab: performance (per-tech / vendor scorecard) ───────────
  // Jesse R2 #9: "I want to see performance broken down by maintenance tech."
  // Mirrors the inspector quality table + Housekeeping cleaner scorecards.
  private renderPerformance(): string {
    if (this.techStats == null) return `<div style="padding:30px; text-align:center; color:var(--text-muted);">Loading performance…</div>`;
    const rows: any[] = this.techStats.rows || [];
    if (!rows.length) return emptyState('No assigned work yet', 'Assign work orders to a technician or vendor and their throughput, speed, on-time rate and cost will appear here.', '🧰');
    const pct = (n: any, basis: number) => n == null ? '<span style="color:var(--text-faint,#9ca3af);">—</span>' : `${n}%${basis ? ` <span style="color:var(--text-faint,#9ca3af); font-size:10px;">/${basis}</span>` : ''}`;
    const num = (n: any) => n == null ? '—' : String(n);
    const body = rows.map(r => `
      <tr>
        <td style="font-weight:600;">${this.esc(r.assignee)}</td>
        <td class="mn-num">${r.completed}</td>
        <td class="mn-num">${r.open + r.in_progress}</td>
        <td class="mn-num">${r.avg_days_to_close == null ? '—' : r.avg_days_to_close + 'd'}${r.avg_days_basis ? ` <span style="color:var(--text-faint,#9ca3af); font-size:10px;">/${r.avg_days_basis}</span>` : ''}</td>
        <td class="mn-num">${pct(r.on_time_pct, r.on_time_basis)}</td>
        <td class="mn-num">${r.urgent_handled}</td>
        <td class="mn-num">${r.total_cost == null ? '—' : this.fmtMoney(r.total_cost)}</td>
        <td class="mn-num">${r.avg_cost == null ? '—' : this.fmtMoney(r.avg_cost)}</td>
      </tr>`).join('');
    return `
      <p class="mn-basis">${this.esc(this.techStats.basis)} · last ${this.techStats.days} days · as of ${this.esc(this.techStats.as_of)}. On-time is measured only where a scheduled window was set (basis shown).</p>
      <div class="mn-card" style="overflow-x:auto;">
        <table class="mn-tbl">
          <thead><tr>
            <th>Technician / vendor</th>
            <th title="Work orders completed in the window">Completed</th>
            <th title="Currently open or in progress">Open now</th>
            <th title="Average days from open to done">Avg close</th>
            <th title="Completed by the scheduled window (where one was set)">On-time</th>
            <th title="Emergency or urgent work orders handled">Urgent</th>
            <th title="Total Asset-Ops cost recorded">Total cost</th>
            <th title="Average cost per completed WO with a cost">Avg cost</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
      <p class="mn-honest" style="margin-top:8px;">Assignees are grouped by the free-text name (or vendor) on each work order — two spellings of one person count as two rows. Linking technicians to logins is a follow-up.</p>`;
  }

  // ── tab: vendors (roster + derived jobs/spend) ───────────────
  private renderVendors(): string {
    if (this.vendors == null) return emptyState('Vendors unavailable', `GET ${API}/vendors failed — check the server log.`, '⚠️');

    // Distinct trades for the filter, from the roster.
    const trades = new Set<string>();
    (this.vendors || []).forEach(v => (v.trades || []).forEach((t: string) => trades.add(t)));
    const tradeOpts = [...trades].sort();

    let rows = this.vendors || [];
    if (this.vendorTradeFilter) rows = rows.filter(v => (v.trades || []).some((t: string) => t === this.vendorTradeFilter));
    if (this.vendorOnCallOnly) rows = rows.filter(v => !!v.on_call);

    const filterBar = `
      <div class="mn-filters">
        <label>Trade</label>
        <select data-mn-venfilter="trade">
          <option value="">All</option>
          ${tradeOpts.map(t => `<option value="${this.esc(t)}" ${this.vendorTradeFilter === t ? 'selected' : ''}>${this.esc(t)}</option>`).join('')}
        </select>
        <label style="display:flex; align-items:center; gap:5px; text-transform:none;">
          <input type="checkbox" data-mn-venfilter="on_call" ${this.vendorOnCallOnly ? 'checked' : ''}/> On-call only
        </label>
        <button class="mn-btn" data-mn-ven-seed="1">Seed from PDS</button>
        <button class="mn-btn primary" data-mn-ven-add="1">${this.showAddVendor ? 'Close form' : '+ Add vendor'}</button>
      </div>`;

    const addForm = this.showAddVendor ? this.renderVendorForm(null) : '';

    const header = `
      <div class="mn-ven-row" style="cursor:default; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.05em; color:var(--text-muted); border-bottom:1px solid var(--border);">
        <div>Vendor</div><div>Trades</div><div>Phone</div><div>On-call</div><div>Jobs</div><div>Spend</div><div></div>
      </div>`;
    const list = rows.length
      ? `<div class="mn-ven">${header}${rows.map(v => this.renderVendorCard(v)).join('')}</div>`
      : emptyState('No vendors', this.vendors!.length ? 'No vendors match the current filter.' : 'Use "Seed from PDS" to pull pest/lawn/pool/trash contacts from property details, or "+ Add vendor".', '🏷');

    return `
      <p class="mn-basis">The vendor roster is who Asset Ops dispatches. A vendor's jobs/spend are derived live from work orders linked to it (vendor_id). ${this.vendors!.length} vendor(s) on the roster.</p>
      <p class="mn-honest">Spend shown here is Asset Ops' own record from work-order costs — not synced to owner statements (those are generated externally).</p>
      ${filterBar}
      ${addForm}
      ${list}
      ${this.renderInhouseTechs()}`;
  }

  // In-house techs (ao_cleaners roster) who appear as assigned_to on any work
  // order — WO count + minutes + recorded cost, labeled 'in-house'. Read-only
  // rollup; rates for these people are set on the Housekeeping cleaner roster.
  private renderInhouseTechs(): string {
    const wos = this.workOrders || [];
    const rows = (this.cleaners || []).map(c => {
      const nm = String(c.name || '').trim().toLowerCase();
      if (!nm) return null;
      const mine = wos.filter(w => String(w.assigned_to || '').trim().toLowerCase() === nm);
      if (!mine.length) return null;
      const minutes = mine.reduce((n, w) => n + (Number(w.total_minutes) || 0), 0);
      const cost = mine.reduce((n, w) => n + (Number(w.cost) || 0), 0);
      const rate = Number(c.hourly_rate);
      const labor = Number.isFinite(rate) && rate > 0 && minutes > 0 ? rate * minutes / 60 : null;
      return { name: String(c.name), count: mine.length, open: mine.filter(w => w.status !== 'done').length, minutes, cost, rate: Number.isFinite(rate) && rate > 0 ? rate : null, labor };
    }).filter(Boolean) as any[];
    if (!rows.length) return '';
    const body = rows.sort((a, b) => b.count - a.count).map(r => `
      <tr>
        <td class="mn-prop">${this.esc(r.name)} <span class="mn-pill" style="background:var(--info-soft, #dbeafe); color:var(--info, #1d4ed8);">in-house</span></td>
        <td class="mn-num">${r.count}</td>
        <td class="mn-num">${r.open}</td>
        <td class="mn-num">${this.fmtMins(r.minutes)}</td>
        <td class="mn-num">${r.rate != null ? '$' + r.rate + '/h' : '—'}</td>
        <td class="mn-num mn-mono">${r.labor != null ? this.fmtMoney(r.labor) : '—'}</td>
        <td class="mn-num mn-mono">${this.fmtMoney(r.cost)}</td>
      </tr>`).join('');
    return `
      <div class="mn-cost-sec" style="margin-top:18px;">
        <h4>In-house techs · from the cleaner roster, matched by assigned-to name</h4>
        <div class="mn-card" style="overflow-x:auto;"><table class="mn-tbl">
          <thead><tr>
            <th>Tech</th>
            <th title="Work orders assigned to this name">WOs</th>
            <th>Open</th>
            <th title="Total tracked labor time">Time</th>
            <th title="Hourly rate from the cleaner roster">Rate</th>
            <th title="rate × tracked time — display only">Labor ≈</th>
            <th title="Recorded WO costs (parts/materials)">Cost</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table></div>
      </div>`;
  }

  // Inline add/edit form. vendor=null → new; else editing an existing vendor.
  private renderVendorForm(vendor: any | null): string {
    const v = vendor || {};
    const editing = !!vendor;
    const id = editing ? this.esc(v.id) : 'new';
    return `
      <div class="mn-ven-form" data-mn-ven-formfor="${id}">
        <label class="full">Name
          <input type="text" data-mn-vf="name" value="${this.esc(v.name || '')}" placeholder="e.g. Blue Wave Pools"/>
        </label>
        <label>Trades (comma-separated)
          <input type="text" data-mn-vf="trades" value="${this.esc((v.trades || []).join(', '))}" placeholder="Pool/spa service, Plumbing"/>
        </label>
        <label>Regions (comma-separated)
          <input type="text" data-mn-vf="regions" value="${this.esc((v.regions || []).join(', '))}" placeholder="San Diego, North County"/>
        </label>
        <label class="chk">On-call (emergencies)
          <input type="checkbox" data-mn-vf="on_call" ${v.on_call ? 'checked' : ''}/>
        </label>
        <label>Phone
          <input type="text" data-mn-vf="phone" value="${this.esc(v.phone || '')}" placeholder="(619) 555-0100"/>
        </label>
        <label>Email
          <input type="text" data-mn-vf="email" value="${this.esc(v.email || '')}" placeholder="ops@vendor.com"/>
        </label>
        <label>Hourly rate ($)
          <input type="number" min="0" step="0.01" data-mn-vf="hourly_rate" value="${v.hourly_rate != null ? this.esc(v.hourly_rate) : ''}" placeholder="e.g. 85" title="Used to estimate WO labor cost from tracked time (display only)"/>
        </label>
        <label class="full">Rate note
          <input type="text" data-mn-vf="rate_note" value="${this.esc(v.rate_note || '')}" placeholder="$120/visit, after-hours 1.5x"/>
        </label>
        <label class="full">Notes
          <input type="text" data-mn-vf="notes" value="${this.esc(v.notes || '')}" placeholder="free-text notes"/>
        </label>
        <div class="mn-ven-form-actions">
          <button class="mn-btn primary" data-mn-ven-save="${id}">${editing ? 'Save changes' : 'Add vendor'}</button>
          <button class="mn-btn" data-mn-ven-cancel="${id}">Cancel</button>
        </div>
      </div>`;
  }

  private renderVendorCard(v: any): string {
    const open = this.expandedVendor.has(v.id);
    const editing = open && this.editingVendor === v.id;
    const tradesStr = (v.trades || []).join(', ') || '—';

    let bodyHtml = '';
    if (open) {
      if (editing) {
        bodyHtml = `<div class="mn-ven-body">${this.renderVendorForm(v)}</div>`;
      } else {
        const wos = this.vendorWos[v.id];
        let woHtml: string;
        if (!wos) {
          woHtml = `<div style="font-size:12px; color:var(--text-muted);">Loading work orders…</div>`;
        } else if (!wos.length) {
          woHtml = `<div style="font-size:12px; color:var(--text-muted);">No work orders linked to this vendor yet.</div>`;
        } else {
          woHtml = wos.map(w => `
            <div class="mn-ast-hist">
              ${this.statusPill(w.status)}
              <span style="font-weight:600;">${this.esc(w.title)}</span>
              <span style="color:var(--text-muted);">${this.esc(w.listing_name)}</span>
              <span class="mn-mono" style="margin-left:auto;">${this.fmtMoney(w.cost)}</span>
              <span style="color:var(--text-muted);">${this.woDoneAt(w) ? `done ${this.fmtDate(this.woDoneAt(w))}` : `opened ${this.fmtDate(w.created_at)}`}</span>
            </div>`).join('');
        }
        bodyHtml = `
          <div class="mn-ven-body">
            ${v.regions?.length ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Regions:</span> ${this.esc(v.regions.join(', '))}</div>` : ''}
            ${v.email ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Email:</span> ${this.esc(v.email)}</div>` : ''}
            ${v.rate_note ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Rate:</span> ${this.esc(v.rate_note)}</div>` : ''}
            ${v.notes ? `<div style="font-size:12px; margin-bottom:6px;"><span style="color:var(--text-muted);">Notes:</span> ${this.esc(v.notes)}</div>` : ''}
            ${v.source === 'pds' ? `<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-bottom:8px;">Seeded from property details (vendors section).</div>` : ''}
            <div style="font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.04em; color:var(--text-muted); margin-bottom:6px;">Work orders · ${this.fmtMoney(v.spend)} total spend</div>
            ${woHtml}
            <div style="display:flex; gap:8px; margin-top:10px;">
              <button class="mn-btn sm" data-mn-ven-edit="${this.esc(v.id)}">Edit</button>
              <button class="mn-btn danger sm" data-mn-ven-del="${this.esc(v.id)}">Delete</button>
            </div>
          </div>`;
      }
    }

    return `
      <div>
        <div class="mn-ven-row" data-mn-ventoggle="${this.esc(v.id)}">
          <div class="mn-ven-name" style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(v.name)}</div>
          <div style="font-size:11px; color:var(--text-muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(tradesStr)}</div>
          <div style="font-size:11px; color:var(--text-muted);">${this.esc(v.phone || '—')}</div>
          <div>${v.on_call ? '<span class="mn-pill" style="background:var(--success-soft); color:var(--success-text, #166534);">On-call</span>' : '<span style="color:var(--text-faint, #9ca3af); font-size:11px;">—</span>'}</div>
          <div class="mn-num">${v.jobs ?? 0}</div>
          <div class="mn-num mn-mono">${this.fmtMoney(v.spend)}</div>
          <div class="mn-ven-caret">${open ? '▾' : '▸'}</div>
        </div>
        ${bodyHtml}
      </div>`;
  }

  // ── tab: costs (Asset Ops spend rollup) ──────────────────────
  private renderCosts(): string {
    const listingOpts = this.assetListingOpts();
    const filterBar = `
      <div class="mn-filters">
        <label>Window</label>
        <span class="mn-seg">
          ${[90, 365].map(d => `<button data-mn-cost-days="${d}" class="${this.costDays === d ? 'on' : ''}">${d === 365 ? '1y' : d + 'd'}</button>`).join('')}
        </span>
        <label>Property</label>
        <select data-mn-costfilter>
          <option value="">All</option>
          ${[...listingOpts.entries()].map(([id, name]) => `<option value="${this.esc(id)}" ${this.costListingFilter === id ? 'selected' : ''}>${this.esc(name)}</option>`).join('')}
        </select>
      </div>`;

    if (this.costs == null) {
      return filterBar + `<div style="padding:30px; text-align:center; color:var(--text-muted);">${LoadingSpinner.render()}<div style="margin-top:8px; font-size:12px;">Loading spend rollup…</div></div>`;
    }
    const c = this.costs;

    const summary = `
      <div class="kpi-grid cols-3">
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Total spend</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
          <div class="kpi-card-value">${this.fmtMoney(c.total)}</div>
          <div class="kpi-card-cap">${c.wo_count} work order(s) · last ${c.window_days} days</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">By payer</span><span class="kpi-card-ic">${icon('receipt', 17)}</span></div>
          <div class="kpi-card-value">${this.fmtMoney(c.by_payer?.wch)}</div>
          <div class="kpi-card-cap">WCH · Owner ${this.fmtMoney(c.by_payer?.owner)} · Unassigned ${this.fmtMoney(c.by_payer?.unassigned)}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Breakdowns</span><span class="kpi-card-ic">${icon('package', 17)}</span></div>
          <div class="kpi-card-value">${(c.by_property || []).length}</div>
          <div class="kpi-card-cap">propert(ies) · ${(c.by_vendor || []).length} vendor(s) · ${(c.by_asset || []).length} asset(s)</div>
        </div>
      </div>`;

    const breakdownTbl = (title: string, items: any[], nameKey: string, countKey: string, countLabel: string) => {
      if (!items.length) return '';
      const tr = items.map(it => `
        <tr>
          <td class="mn-prop" style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${this.esc(it[nameKey])}</td>
          <td class="mn-num">${it[countKey]}</td>
          <td class="mn-num mn-mono" style="text-align:right;">${this.fmtMoney(it.total)}</td>
        </tr>`).join('');
      return `
        <div class="mn-cost-sec">
          <h4>${title}</h4>
          <div class="mn-card"><table class="mn-tbl">
            <thead><tr><th>Name</th><th style="width:80px;">${countLabel}</th><th style="width:110px; text-align:right;">Spend</th></tr></thead>
            <tbody>${tr}</tbody>
          </table></div>
        </div>`;
    };

    const empty = c.wo_count === 0
      ? `<p class="mn-basis">No work orders carry a cost in this window yet. Record cost + payer on a work order (Work orders tab) and it will roll up here.</p>`
      : '';

    return `
      <p class="mn-basis">Asset Ops spend over the selected window — derived from work orders with a recorded cost > 0 (effective date = completed, else opened).</p>
      ${filterBar}
      ${summary}
      ${empty}
      ${breakdownTbl('By property', c.by_property || [], 'listing_name', 'wo_count', 'WOs')}
      ${breakdownTbl('By vendor', c.by_vendor || [], 'vendor_name', 'jobs', 'Jobs')}
      ${breakdownTbl('By asset', c.by_asset || [], 'name', 'wo_count', 'WOs')}
      <p class="mn-honest">${this.esc(c.note)}</p>`;
  }

  // ── tab 4: PM templates ──────────────────────────────────────
  private renderTemplates(): string {
    if (this.templates == null) return emptyState('Templates unavailable', `GET ${API}/pm-templates failed — check the server log.`, '⚠️');
    const list = (this.templates || []).map(t => this.renderTplCard(t)).join('');
    return `
      <p class="mn-basis">A PM template's cadence (days) drives the Board's "due for PM" coloring; its items are copied into every work order created from it. ${this.templates!.length} template(s).</p>
      <div style="margin-bottom:12px;"><button class="mn-btn primary" data-mn-newtpl="1">+ New template</button></div>
      ${list || emptyState('No templates', 'Create one to drive PM cadence and WO checklists.', '📋')}`;
  }

  private renderTplCard(t: any): string {
    const open = this.expandedTpl.has(t.id);
    const items = (t.items || []);
    let body = '';
    if (open) {
      const rows = items.map((it: any, i: number) => `
        <div class="mn-tpl-item" data-mn-tpl-itemrow="${this.esc(t.id)}|${i}">
          <input type="text" data-mn-tpl-itemtitle value="${this.esc(it.title)}" placeholder="Item title"/>
          <textarea data-mn-tpl-iteminstr placeholder="Instructions for the technician">${this.esc(it.instructions || '')}</textarea>
          <button class="mn-btn danger sm" data-mn-tpl-itemdel="${this.esc(t.id)}|${i}" title="Remove item">✕</button>
        </div>`).join('');
      body = `
        <div class="mn-tpl-body">
          <div style="display:flex; gap:10px; align-items:flex-end; margin-bottom:12px;">
            <label style="display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">Name
              <input type="text" data-mn-tpl-name="${this.esc(t.id)}" value="${this.esc(t.name)}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; min-width:240px;"/>
            </label>
            <label style="display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em;">Cadence (days)
              <input type="number" min="1" data-mn-tpl-cadence="${this.esc(t.id)}" value="${this.esc(t.cadence_days)}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; width:100px;"/>
            </label>
          </div>
          <div data-mn-tpl-items="${this.esc(t.id)}">${rows}</div>
          <div style="display:flex; gap:8px; margin-top:6px;">
            <button class="mn-btn sm" data-mn-tpl-additem="${this.esc(t.id)}">+ Add item</button>
            <button class="mn-btn primary" data-mn-tpl-save="${this.esc(t.id)}">Save template</button>
            <button class="mn-btn danger" data-mn-tpl-del="${this.esc(t.id)}">Delete</button>
          </div>
        </div>`;
    }
    return `
      <div class="mn-tpl">
        <div class="mn-tpl-head" data-mn-tpltoggle="${this.esc(t.id)}">
          <div>
            <span class="mn-tpl-name">${this.esc(t.name)}</span>
            <span class="mn-tpl-meta"> · every ${this.esc(t.cadence_days)} days · ${items.length} item(s)</span>
          </div>
          <span style="color:var(--text-muted); font-size:12px;">${open ? '▾' : '▸'}</span>
        </div>
        ${body}
      </div>`;
  }

  // ── new-WO modal ─────────────────────────────────────────────
  private showNewWoModal(listingId: string, listingName: string): void {
    const listingOpts = new Map<string, string>();
    (this.board?.rows || []).forEach((r: any) => listingOpts.set(String(r.listing_id), r.listing_name));
    // Duplicate listing names get an "(id)" suffix in the picker — otherwise
    // the label→id lookup silently resolves every dupe to the LAST listing.
    const nameCount = new Map<string, number>();
    listingOpts.forEach(name => { const k = String(name).trim().toLowerCase(); nameCount.set(k, (nameCount.get(k) || 0) + 1); });
    const labelOf = (id: string) => {
      const name = String(listingOpts.get(id) || id);
      return (nameCount.get(name.trim().toLowerCase()) || 0) > 1 ? `${name} (${id})` : name;
    };
    const tplOpts = (this.templates || []);
    const wrap = document.createElement('div');
    wrap.className = 'mn-modal-bg';
    wrap.innerHTML = `
      <div class="mn-modal" style="max-width:520px;">
        <h3>New work order</h3>
        <label>Property
          <input type="text" id="mn-nw-listing" list="mn-nw-listing-dl" autocomplete="off" value="${this.esc(listingOpts.has(String(listingId)) ? labelOf(String(listingId)) : '')}" placeholder="type to search properties…"/>
          <datalist id="mn-nw-listing-dl">
            ${[...listingOpts.keys()].map(id => `<option value="${this.esc(labelOf(id))}"></option>`).join('')}
          </datalist>
        </label>
        <label>Type
          <select id="mn-nw-kind">
            <option value="pm">Preventive maintenance (from a template)</option>
            <option value="wo">Ad-hoc work order</option>
          </select>
        </label>
        <div id="mn-nw-pm-wrap">
          <label>PM template
            <select id="mn-nw-tpl">
              ${tplOpts.map(t => `<option value="${this.esc(t.id)}">${this.esc(t.name)} (${t.items?.length || 0} items, every ${t.cadence_days}d)</option>`).join('')}
            </select>
          </label>
        </div>
        <div id="mn-nw-wo-wrap" style="display:none;">
          <label>Title
            <input type="text" id="mn-nw-title" placeholder="e.g. Replace garbage disposal"/>
          </label>
        </div>
        <label>Instructions (optional)
          <textarea id="mn-nw-desc" placeholder="Instructions — what needs to get done, where, what you need after…" style="min-height:70px; font-family:inherit; font-size:12.5px;"></textarea>
        </label>
        <label>📷 Photos for the tech
          <input type="file" id="mn-nw-photos" accept="${EVIDENCE_ACCEPT}" multiple/>
        </label>
        <label>Priority
          <select id="mn-nw-prio">
            ${WO_PRIORITIES.map(([k, l]) => `<option value="${k}">${l}</option>`).join('')}
          </select>
        </label>
        <label>Skill level <span style="font-weight:400; color:var(--text-muted);">(sets the owner billing rate)</span>
          <select id="mn-nw-skill">
            <option value="">—</option>
            <option value="low">Low skill ($45/hr)</option>
            <option value="high">High skill ($65/hr)</option>
          </select>
        </label>
        <label>Due date (optional)
          <input type="date" id="mn-nw-due"/>
        </label>
        <label title="R102: the day it's booked for — shows on the calendar, the board and that day's plan">Booked day (optional)
          <input type="date" id="mn-nw-sched"/>
        </label>
        <label>Route to
          <select id="mn-nw-role">
            ${WO_ROLES.map(([k, l]) => `<option value="${k}">${l}</option>`).join('')}
          </select>
        </label>
        <label>Asset (optional)
          <select id="mn-nw-asset"><option value="">— none —</option></select>
        </label>
        <label>Vendor (optional)
          <select id="mn-nw-vendor">${this.vendorOptions(null)}</select>
        </label>
        <label>Assigned to (optional)
          <input type="text" list="mn-nw-assign-roster" id="mn-nw-assign" placeholder="pick a teammate or vendor — or type a name"/>
          ${this.assignDatalist('mn-nw-assign-roster')}
        </label>
        <div style="display:flex; gap:10px;">
          <label style="flex:1;">Cost (optional)
            <input type="number" min="0" step="0.01" id="mn-nw-cost" placeholder="$"/>
          </label>
          <label style="flex:1;">Payer
            <select id="mn-nw-payer">
              <option value="">—</option>
              ${WO_PAYERS.map(([k, l]) => `<option value="${k}">${l}</option>`).join('')}
            </select>
          </label>
        </div>
        <p class="mn-honest">Cost &amp; payer are Asset Ops' own record — not synced to owner statements (those are generated externally).</p>
        <div class="mn-modal-actions">
          <button class="mn-btn" data-mn-nw-cancel="1">Cancel</button>
          <button class="mn-btn primary" data-mn-nw-create="1">Create</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    const close = () => wrap.remove();
    wrap.addEventListener('click', (e) => { if (e.target === wrap) close(); });

    const kind = wrap.querySelector('#mn-nw-kind') as HTMLSelectElement;
    const prioSel = wrap.querySelector('#mn-nw-prio') as HTMLSelectElement;
    const assetSel = wrap.querySelector('#mn-nw-asset') as HTMLSelectElement;
    const listingInput = wrap.querySelector('#mn-nw-listing') as HTMLInputElement;
    const pmWrap = wrap.querySelector('#mn-nw-pm-wrap') as HTMLElement;
    const woWrap = wrap.querySelector('#mn-nw-wo-wrap') as HTMLElement;
    // Type-ahead property picker — the datalist shows NAMES; a label match
    // resolves the id (case-insensitive). fillAssets re-fires on resolution.
    const nameToId = new Map<string, string>();
    listingOpts.forEach((_name, id) => nameToId.set(labelOf(id).trim().toLowerCase(), id));
    let selectedListingId = listingOpts.has(String(listingId)) ? String(listingId) : '';
    const fillAssets = () => {
      const mine = (this.assets || []).filter(a => String(a.listing_id) === String(selectedListingId));
      assetSel.innerHTML = `<option value="">— none —</option>` +
        mine.map(a => `<option value="${this.esc(a.id)}">${this.esc(a.name)}</option>`).join('');
    };
    const resolveListing = () => {
      const id = nameToId.get(listingInput.value.trim().toLowerCase()) || '';
      if (id !== selectedListingId) { selectedListingId = id; fillAssets(); }
    };
    listingInput.addEventListener('input', resolveListing);
    listingInput.addEventListener('change', resolveListing);
    fillAssets();
    const syncPrio = () => {
      // PM type defaults the priority to 'pm'; ad-hoc defaults to 'routine'.
      prioSel.value = kind.value === 'pm' ? 'pm' : 'routine';
    };
    kind.addEventListener('change', () => {
      const isPm = kind.value === 'pm';
      pmWrap.style.display = isPm ? '' : 'none';
      woWrap.style.display = isPm ? 'none' : '';
      syncPrio();
    });
    syncPrio();
    if (!tplOpts.length) { kind.value = 'wo'; kind.dispatchEvent(new Event('change')); }

    (wrap.querySelector('[data-mn-nw-cancel]') as HTMLElement)?.addEventListener('click', close);
    (wrap.querySelector('[data-mn-nw-create]') as HTMLElement)?.addEventListener('click', async () => {
      resolveListing();
      const lid = selectedListingId;
      if (!lid) { alert('Pick a property from the list.'); listingInput.focus(); return; }
      const lname = listingOpts.get(lid) || listingName;
      const assigned = (wrap.querySelector('#mn-nw-assign') as HTMLInputElement).value.trim();
      const priority = prioSel.value;
      const asset_id = assetSel.value || null;
      const vendor_id = (wrap.querySelector('#mn-nw-vendor') as HTMLSelectElement).value || null;
      const costRaw = (wrap.querySelector('#mn-nw-cost') as HTMLInputElement).value.trim();
      const cost = costRaw === '' ? null : Number(costRaw);
      const payer = (wrap.querySelector('#mn-nw-payer') as HTMLSelectElement).value || null;
      const role = (wrap.querySelector('#mn-nw-role') as HTMLSelectElement).value || 'maintenance';
      const description = (wrap.querySelector('#mn-nw-desc') as HTMLTextAreaElement).value.trim();
      const due_date = (wrap.querySelector('#mn-nw-due') as HTMLInputElement).value || null;
      const scheduled_date = (wrap.querySelector('#mn-nw-sched') as HTMLInputElement | null)?.value || null;   // R102 #5
      const skill = (wrap.querySelector('#mn-nw-skill') as HTMLSelectElement)?.value || null;   // R88 #3: owner billing rate picked at creation
      let body: any;
      if (kind.value === 'pm') {
        const tplId = (wrap.querySelector('#mn-nw-tpl') as HTMLSelectElement).value;
        if (!tplId) { alert('Pick a PM template, or switch to ad-hoc.'); return; }
        body = { type: 'pm', template_id: tplId, listing_id: lid, listing_name: lname, assigned_to: assigned, priority, role, asset_id, vendor_id, cost, payer, description, due_date, scheduled_date, skill };
      } else {
        const title = (wrap.querySelector('#mn-nw-title') as HTMLInputElement).value.trim();
        if (!title) { alert('Give the work order a title.'); return; }
        body = { type: 'wo', title, listing_id: lid, listing_name: lname, assigned_to: assigned, priority, role, asset_id, vendor_id, cost, payer, description, due_date, scheduled_date, skill };
      }
      body.by = MaintenancePage.actorName();
      let j = await this.post('/work-orders', body);
      // R70 #8: owner-handled property — office may deliberately override
      // (e.g. the owner asked us to step in on this one).
      if (!j && (this as any)._lastErrCode === 'owner_handled' && ['admin', 'director', 'manager', 'remote_ops'].includes(MaintenancePage.fieldRole())) {
        if (confirm('The owner handles maintenance at this property. Create this work order anyway (owner asked us to step in)?')) {
          j = await this.post('/work-orders', { ...body, owner_handled_override: true });
        }
      }
      if (j) {
        // Upload the tech-briefing photos (if any picked) before the reload so
        // the freshly opened card already shows them.
        const newId = j.work_order?.id;
        const files = Array.from((wrap.querySelector('#mn-nw-photos') as HTMLInputElement).files || []);
        // R116 #5: every briefing photo / clip rides the background queue, one per
        // request (one multi-file POST hit the server's 5-file wall and lost them all).
        if (newId && files.length) {
          const btn = wrap.querySelector('[data-mn-nw-create]') as HTMLButtonElement | null;
          if (btn) { btn.disabled = true; btn.textContent = `Queuing ${files.length} file${files.length === 1 ? '' : 's'}…`; }
          try { await enqueuePhotos(`${API}/work-orders/${newId}/photo`, files, { by: MaintenancePage.actorName() }, 'work-order photo'); }
          catch { alert('The photos could not be queued — the work order was created without them; add them on its card.'); }
        }
        close();
        if (newId) this.expandedWo.add(newId);
        // R102 #4 / R103: a task added from the property's Project stays there; one added from the
        // board's emergency expansion opens that property's Project (the board shows no WO cards).
        if (this.tab === 'board') { this.projectLid = lid; this.tab = 'project'; }
        else if (this.tab !== 'project') {
          this.tab = 'work_orders';
          this.woListingFilter = '';
          this.woStatusFilter = '';
          this.clearWoSearchForNew();
        }
        await Promise.all([this.reloadWos(), this.reloadBoard()]);
        this.draw();
      }
    });
  }

  // ── photo upload (shared) ────────────────────────────────────
  // R116 #5 (Jorge: "upload videos… multiple pictures at once"): the shared
  // hidden input takes photos AND video, several at once; every file rides the
  // persisted background queue (lib/photoUploader — one file per request,
  // photos 3-wide / clips 1-wide, client_key, the server's size cap), tagged
  // with who added it. The card patches itself as each file lands — no
  // full redraw, so a half-typed report survives.
  private uploadPhoto(woId: string, itemKey: string | null, kind: string | null = null): void {
    const input = document.getElementById('mn-photo-file') as HTMLInputElement | null;
    if (!input || input.disabled) return;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      input.value = '';
      if (!files.length) return;
      input.disabled = true;   // double-pick guard (a second pick mints new keys the server can't dedupe)
      const fields: Record<string, string> = { by: MaintenancePage.actorName() };
      if (itemKey) fields.item_key = itemKey;
      if (kind) fields.kind = kind; // 'before' | 'after' | 'receipt' — lands in photos_meta
      try {
        await enqueuePhotos(`${API}/work-orders/${woId}/photo`, files, fields, kind === 'receipt' ? 'receipt' : 'work-order photo');
      } finally { input.disabled = false; }
      this.patchWoMedia(woId);
    };
    input.click();
  }

  /** R116 #5: a checklist item's photos + clips as one strip. */
  private itemMediaHtml(it: any): string {
    const vids: string[] = Array.isArray(it && it.videos) ? it.videos.filter((v: any) => typeof v === 'string') : [];
    const items: MediaItem[] = [
      ...(Array.isArray(it && it.photos) ? it.photos : []).filter((u: any) => typeof u === 'string' && u).map((u: string) => ({ url: u, video: isVideoUrl(u) })),
      ...vids.map(u => ({ url: u, video: true })),
    ];
    return items.length ? `<div class="mn-item-media">${mediaStrip(items, { size: 'sm', alt: 'item photo' })}</div>` : '';
  }
  /** Who / when for a media caption (an email shows its name part). */
  private mediaCaption(m: any, label: string): string {
    const by = String((m && m.by) || '').replace(/^vendor:/, 'vendor · ').split('@')[0];
    const at = m && m.at ? this.fmtDateTime(m.at) : '';
    return [label, by, at].filter(Boolean).join(' · ');
  }
  /** The WO's evidence by kind — photos AND clips (photos_meta.media / the url says which). */
  private woMediaByKind(w: any): { before: MediaItem[]; after: MediaItem[]; receipt: MediaItem[]; plain: MediaItem[] } {
    const meta: any[] = Array.isArray(w.photos_meta) ? w.photos_meta : [];
    const vids = new Set<string>((Array.isArray(w.videos) ? w.videos : []).filter((v: any) => typeof v === 'string'));
    const isVid = (m: any, u: string) => (m && m.media === 'video') || vids.has(u) || isVideoUrl(u);
    const byKind = (k: string, label: string): MediaItem[] => {
      const seen = new Set<string>();
      return meta.filter((m: any) => m && m.kind === k && m.url && !seen.has(m.url) && seen.add(m.url)).map((m: any) => ({ url: String(m.url), video: isVid(m, String(m.url)), caption: this.mediaCaption(m, label) }));
    };
    const kinded = new Set<string>(meta.filter((m: any) => m && m.kind && m.url).map((m: any) => String(m.url)));
    const plainUrls = [...(Array.isArray(w.photos) ? w.photos : []), ...vids].filter((u: any) => typeof u === 'string' && u && !kinded.has(u));
    const plain = [...new Set(plainUrls)].map((u: string) => { const m = meta.find((x: any) => x && x.url === u); return { url: u, video: isVid(m, u), caption: this.mediaCaption(m, 'Photo') }; });
    return { before: byKind('before', 'Before'), after: byKind('after', 'After'), receipt: byKind('receipt', 'Receipt'), plain };
  }
  /** Before / After / Receipts / Photos & video groups + the WO's files still uploading. */
  private woMediaGroups(w: any): string {
    const g = this.woMediaByKind(w);
    const label = (t: string, n: number) => `<div style="display:flex; align-items:center; gap:6px; font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.04em; margin:10px 0 5px;">${t}<span class="mn-chip" style="font-size:10px; padding:0 6px;">${n}</span></div>`;
    const group = (t: string, items: MediaItem[], alt: string) => items.length ? `${label(t, items.length)}${mediaStrip(items, { size: 'md', alt, cls: alt === 'after photo' ? 'mx-after' : '' })}` : '';
    // the report form's picker shows its own queued after files — don't list them twice
    const pickerOpen = !w.completion;
    const pend = pendingItems(`${API}/work-orders/${w.id}/photo`, (f) => !(pickerOpen && f.kind === 'after'));
    const anyVid = g.plain.some(m => m.video);
    return [
      group('📷 Before', g.before, 'before photo'),
      group('✅ After', g.after, 'after photo'),
      group('🧾 Receipts', g.receipt, 'receipt'),
      group(anyVid ? 'Photos &amp; video' : 'Photos', g.plain, 'work-order photo'),
      pend.length ? `${label('⏳ Uploading', pend.length)}<div class="mx-strip mx-md" data-mx-strip>${pendingTilesHtml(pend)}</div>` : '',
    ].join('');
  }
  /** The Tech report form's "After evidence" picker for one WO. */
  private afterPickerCfg(w: any): EvidencePickerCfg {
    const id = String(w.id);
    return {
      id: `mn-after-${id}`,
      title: 'After evidence',
      url: `${API}/work-orders/${id}/photo`,
      fields: { kind: 'after', by: MaintenancePage.actorName() },
      match: (f) => f.kind === 'after' && !f.item_key,
      existing: this.woMediaByKind(w).after,
      label: 'after photo',
      hint: 'Photos or a short video of the finished work — files keep uploading after you file the report.',
      onUploaded: (resp: any) => {
        const wo = (this.workOrders || []).find(x => x.id === id);
        if (wo && resp && resp.work_order) mergeLandedMedia(wo, resp.work_order);   // answers can land out of order — never lose a file
        this.patchWoMedia(id, true);
        return wo ? this.woMediaByKind(wo).after : undefined;
      },
    };
  }
  /** Patch one WO's media (groups + checklist strips) in place — never a full draw(). */
  private patchWoMedia(id: string, skipPicker = false): void {
    const wo = (this.workOrders || []).find(w => w.id === id);
    if (!wo) return;
    this.container.querySelectorAll<HTMLElement>(`[data-mn-media="${CSS.escape(id)}"]`).forEach(el => { el.innerHTML = this.woMediaGroups(wo); bindMediaStrips(el); });
    for (const it of (wo.items || [])) {
      const el = this.container.querySelector<HTMLElement>(`[data-mn-itemmedia="${CSS.escape(`${id}|${it.key}`)}"]`);
      if (el) { el.innerHTML = this.itemMediaHtml(it); bindMediaStrips(el); }
    }
    if (!skipPicker) { /* the picker redraws itself on the queue events */ }
  }
  /** A short, polite note (aria-live) that fades — e.g. "report filed, 2 files still uploading". */
  private toastNote(text: string): void {
    let el = document.getElementById('mn-toast-note');
    if (!el) {
      el = document.createElement('div');
      el.id = 'mn-toast-note';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = 'position:fixed; left:50%; top:16px; transform:translateX(-50%); z-index:9998; max-width:min(92vw, 520px); padding:10px 16px; border-radius:12px; background:var(--card); color:var(--text); border:1px solid var(--border); box-shadow:var(--shadow-card); font-size:13px; line-height:1.4;';
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.style.display = 'block';
    window.clearTimeout((el as any)._t);
    (el as any)._t = window.setTimeout(() => { if (el) el.style.display = 'none'; }, 6000);
  }
  /** One listener for the page's lifetime: a WO upload (any surface on this page) lands → patch that card. */
  private listenWoMedia(): void {
    const woOf = (url: string) => { const m = /\/work-orders\/([^/]+)\/photo$/.exec(String(url || '')); return m ? decodeURIComponent(m[1]) : ''; };
    window.addEventListener('surge-photo-uploaded', (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const id = woOf(d.url);
      if (!id || !this.container.isConnected) return;
      const wo = (this.workOrders || []).find(w => w.id === id);
      if (wo && d.response && d.response.work_order) mergeLandedMedia(wo, d.response.work_order);
      this.patchWoMedia(id);
    });
    // only the cards whose queued files actually changed (a repaint reloads every clip poster)
    const sig = new Map<string, string>();
    const repaint = () => {
      if (!this.container.isConnected) return;
      this.container.querySelectorAll<HTMLElement>('[data-mn-media]').forEach(el => {
        const id = el.dataset.mnMedia || '';
        const now = pendingItems(`${API}/work-orders/${id}/photo`).map(p => p.key).join(',');
        if ((sig.get(id) || '') === now) return;
        sig.set(id, now);
        this.patchWoMedia(id);
      });
    };
    window.addEventListener('surge-photo-queue', repaint);
    window.addEventListener('surge-photo-progress', (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (woOf(d.url) && this.container.isConnected) patchPendingProgress(this.container, String(d.clientKey || ''), Number(d.sent) || 0, Number(d.total) || 0);
    });
  }

  // ── owner summary recap (v1: plain-text of completed WOs) ─────
  private async copyOwnerSummary(listingId: string): Promise<void> {
    const wos = (this.workOrders || []).filter(w => String(w.listing_id) === listingId && w.status === 'done');
    const name = wos[0]?.listing_name || (this.board?.rows || []).find((r: any) => String(r.listing_id) === listingId)?.listing_name || listingId;
    if (!wos.length) { alert('No completed work orders for this property yet.'); return; }
    const lines: string[] = [`Maintenance recap — ${name}`, ''];
    for (const w of wos) {
      lines.push(`• ${w.title} (${this.fmtDate(this.woDoneAt(w))})`);
      if (w.summary) lines.push(`  ${w.summary}`);
      const photoCount = (w.photos || []).length + (w.items || []).reduce((n: number, it: any) => n + (it.photos || []).length, 0);
      if (photoCount) lines.push(`  ${photoCount} photo(s) on file.`);
      lines.push('');
    }
    const text = lines.join('\n').trim();
    try {
      await navigator.clipboard.writeText(text);
      alert(`Copied a recap of ${wos.length} completed work order(s) to the clipboard.`);
    } catch {
      // clipboard blocked — show it so they can copy manually.
      window.prompt('Owner summary (copy below):', text);
    }
  }

  // ── bindings ─────────────────────────────────────────────────
  private bind(): void {
    bindFieldViewSwitcher(this.container);   // R64: field role self-switch chips
    // R116 #5: tap a photo / clip → the shared viewer; the report form's After-evidence pickers
    bindMediaStrips(this.container);
    this.container.querySelectorAll<HTMLElement>('[data-evp^="mn-after-"]').forEach(el => {
      const wo = (this.workOrders || []).find(w => `mn-after-${w.id}` === el.dataset.evp);
      if (wo) bindEvidencePicker(this.container, this.afterPickerCfg(wo));
    });
    const $ = (sel: string) => this.container.querySelector<HTMLElement>(sel);
    const all = (sel: string) => this.container.querySelectorAll<HTMLElement>(sel);

    // tabs
    all('[data-mn-tab]').forEach(el => el.addEventListener('click', async () => {
      this.tab = el.dataset.mnTab as any;
      this.draw();
      if (this.tab === 'prop_review' && this.prListings === null) {
        // roster for the picker — active listings, any role (R82 picker source)
        const l = await fetch('/api/listings/active?include=onboarding').then(r => r.ok ? r.json() : null).catch(() => null);
        // review #30: null = failed (retries on the next tab click); [] would brick the picker for the session
        this.prListings = l ? (Array.isArray(l) ? l : (l.listings || [])) : null;
        if (l === null) alert('Property list didn\'t load — click the tab again to retry.');
        if (this.tab === 'prop_review') this.draw();
      }
      // Lazy-load the calendar / PM-due / cost / asset-intel data the first time
      // those tabs open.
      if (this.tab === 'calendar') {
        if (this.calView === 'windows' && this.calendar == null) { await this.reloadCalendar(); this.draw(); }   // the month grid fetches itself (ensureCalGrid)
      } else if ((this.tab === 'recurring' || this.tab === 'project') && (this.pmDue == null || this.autogenFlags == null)) {
        await Promise.all([
          this.pmDue == null ? this.reloadPmDue() : Promise.resolve(),
          this.autogenFlags == null ? this.reloadAutogenFlags() : Promise.resolve(),
        ]);
        this.draw();
      } else if (this.tab === 'costs') {
        if (this.costSub === 'performance' && this.techStats == null) { this.techStats = await this.get('/maintenance/tech-stats?days=90'); this.draw(); }
        else if (this.costSub === 'costs' && this.costs == null) { await this.reloadCosts(); this.draw(); }
      } else if (this.tab === 'assets' && this.assetIntel == null) {
        await this.reloadAssetIntel(); this.draw();
      }
    }));
    // R102 #7/#8: sub-views inside Recurring maintenance and Costs & performance.
    all('[data-mn-recsub]').forEach(el => el.addEventListener('click', () => { this.recSub = el.dataset.mnRecsub as any; this.draw(); }));
    all('[data-mn-costsub]').forEach(el => el.addEventListener('click', async () => {
      this.costSub = el.dataset.mnCostsub as any;
      if (this.costSub === 'performance' && this.techStats == null) this.techStats = await this.get('/maintenance/tech-stats?days=90');
      if (this.costSub === 'costs' && this.costs == null) await this.reloadCosts();
      this.draw();
    }));

    // ── board: click a property / count → drill into its work orders (Jesse R2
    // #8: "clicking those properties does nothing"). Reuses the Work Orders tab's
    // per-listing filter + expandable action card — no new endpoint needed.
    all('[data-mn-board-drill]').forEach(el => el.addEventListener('click', () => {
      const [id, , status] = (el.dataset.mnBoardDrill || '').split('|');
      if (!id) return;
      this.tab = 'work_orders';
      this.resetWoFilters();   // review: a stale assignee/role/date filter from earlier in the session would hide the property's WOs
      this.woListingFilter = id;
      this.woStatusFilter = status || '';
      this.draw();
      this.container.querySelector('.mn-wo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));

    // ── R102 board / project / calendar / owner portal handlers ──
    const boardQ = $('[data-mn-board-q]') as HTMLInputElement | null;
    boardQ?.addEventListener('change', () => { this.boardQ = boardQ.value; this.draw(); });
    boardQ?.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') { this.boardQ = boardQ.value; this.draw(); } });
    // R112: ONE focus filter behind the KPI cards and the card-head chips (click the active one again = every property).
    const setFocus = (k: BoardFocus) => { this.boardFocus = (k && this.boardFocus === k) ? '' : k; this.draw(); };
    all('[data-mn-board-focus]').forEach(el => {
      el.addEventListener('click', () => setFocus((el.dataset.mnBoardFocus || '') as BoardFocus));
      // the KPI cards are divs (role=button) — Enter / Space work like a click; a nested real button keeps its own key
      if (el.tagName !== 'BUTTON') el.addEventListener('keydown', (e) => { const k = (e as KeyboardEvent).key; if (e.target === el && (k === 'Enter' || k === ' ')) { e.preventDefault(); setFocus((el.dataset.mnBoardFocus || '') as BoardFocus); } });
    });
    $('[data-mn-board-openonly]')?.addEventListener('click', () => setFocus('open'));   // the old "only with open work" toggle, same attribute
    all('[data-mn-board-queue]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.boardQueueOpen = !this.boardQueueOpen; this.draw(); }));   // sits inside the Owner-portal KPI card too
    // R103: the emergency chip works the incidents IN PLACE (expands under the row); the tab is one click away.
    all('[data-mn-board-emg]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const lid = el.dataset.mnBoardEmg!;
      if (this.boardEmgOpen.has(lid)) { this.boardEmgOpen.delete(lid); this.draw(); return; }
      this.boardEmgOpen.add(lid);
      if (this.incStatusFilter || this.incSeverityFilter || this.incidents == null) { this.incStatusFilter = ''; this.incSeverityFilter = ''; await this.reloadIncidents(); }   // the cached list may be a filtered subset — or never loaded (review R103 #26)
      this.draw();
    }));
    all('[data-mn-board-emg-retry]').forEach(el => el.addEventListener('click', async (e) => { e.stopPropagation(); await this.reloadIncidents(); this.draw(); }));
    all('[data-mn-board-emgtab]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const hadServerFilter = !!(this.incStatusFilter || this.incSeverityFilter);
      this.tab = 'incidents'; this.incListingFilter = el.dataset.mnBoardEmgtab!; this.incStatusFilter = ''; this.incSeverityFilter = ''; this.showLogIncident = false;
      if (hadServerFilter) await this.reloadIncidents();
      this.draw();
    }));
    // R103: the property's calendar lives INSIDE its project (the Calendar tab stays for the portfolio view).
    all('[data-mn-board-cal]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const lid = el.dataset.mnBoardCal || '';
      this.projectLid = lid; this.tab = 'project'; this.projectCal = true;
      this.calView = 'month'; this.calLid = lid; this.calDaySel = ''; this.calPick = null; this.calPickWho = null;
      this.calGrid = null; this.calGridError = false; this.calGridSeq++; this.calGridLoading = false;   // the panel fetches itself (ensureCalGrid); any in-flight grid is dropped
      this.draw();
      if (this.pmDue == null) { await this.reloadPmDue(); if (this.tab === 'project') this.draw(); }
    }));
    all('[data-mn-pj-cal]').forEach(el => el.addEventListener('click', () => {
      this.projectCal = !this.projectCal;
      if (this.projectCal && this.calLid !== this.projectLid) { this.calLid = this.projectLid; this.calGrid = null; this.calGridError = false; this.calDaySel = ''; this.calPick = null; this.calPickWho = null; this.calGridSeq++; this.calGridLoading = false; }
      this.draw();
    }));
    // R103: work orders open IN the project (and in the board's owner queue) — the full card, same handlers.
    all('[data-mn-pj-toggle]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); void this.toggleWo(el.dataset.mnPjToggle!); }));
    all('[data-mn-board-project]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      this.projectLid = el.dataset.mnBoardProject!; this.tab = 'project';
      this.draw();
      if (this.pmDue == null) { await this.reloadPmDue(); if (this.tab === 'project') this.draw(); }
    }));
    all('[data-mn-board-assets]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      this.tab = 'assets'; this.assetListingFilter = el.dataset.mnBoardAssets!;
      this.draw();
      if (this.assetIntel == null) { await this.reloadAssetIntel(); this.draw(); }
    }));
    all('[data-mn-board-expenses]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.openExpensesPdf(el.dataset.mnBoardExpenses!); }));
    all('[data-mn-exp-open]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.expPopLid = el.dataset.mnExpOpen!; this.draw(); }));
    all('[data-mn-exp-close]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.expPopLid = ''; this.draw(); }));
    all('[data-mn-exp-pop]').forEach(el => el.addEventListener('click', (e) => e.stopPropagation()));
    all('[data-mn-exp-go]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const lid = el.dataset.mnExpGo!;
      const from = (this.container.querySelector(`[data-mn-exp-from="${CSS.escape(lid)}"]`) as HTMLInputElement | null)?.value || '';
      const to = (this.container.querySelector(`[data-mn-exp-to="${CSS.escape(lid)}"]`) as HTMLInputElement | null)?.value || '';
      const photos = !!(this.container.querySelector(`[data-mn-exp-photos="${CSS.escape(lid)}"]`) as HTMLInputElement | null)?.checked;
      if (!from || !to) { alert('Pick both dates.'); return; }
      this.expPopLid = '';
      this.openExpensesPdf(lid, from, to, photos);
      this.draw();
    }));
    all('[data-mn-op]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const [id, action] = (el.dataset.mnOp || '').split('|');
      if (id && action) void this.opAction(id, action);
    }));
    all('[data-mn-payer]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [id, payer] = (el.dataset.mnPayer || '').split('|');
      if (!id || !payer) return;
      const j = await this.post(`/work-orders/${encodeURIComponent(id)}`, { payer, by: MaintenancePage.actorName() });
      if (!j) return;
      await this.reloadWos();
      if (payer === 'owner') await this.opAction(id, 'publish');   // a cancelled note prompt must still leave the queue redrawn
      await this.reloadBoard(); this.draw();
    }));
    // project (punch list)
    $('[data-mn-proj-back]')?.addEventListener('click', () => { this.tab = 'board'; this.draw(); });
    all('[data-mn-proj-open]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.drillWo(el.dataset.mnProjOpen!); }));
    all('[data-mn-proj-inc]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnProjInc!;
      const inc = (this.incidents || []).find(i => i.id === id);
      const hadServerFilter = !!(this.incStatusFilter || this.incSeverityFilter);
      this.tab = 'incidents'; this.incListingFilter = inc ? String(inc.listing_id) : ''; this.incStatusFilter = ''; this.incSeverityFilter = ''; this.expandedInc = id;
      if (hadServerFilter) await this.reloadIncidents();
      this.draw();
      if (!this.incDetail[id]) { await this.reloadIncidentDetail(id); if (this.expandedInc === id) this.draw(); }   // review: command view needs the on-call detail
    }));
    all('[data-mn-proj-book]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnProjBook!;
      const wo = (this.workOrders || []).find(w => w.id === id);
      const date = (this.container.querySelector(`[data-mn-proj-date="${CSS.escape(id)}"]`) as HTMLInputElement | null)?.value || '';
      const vendor = (this.container.querySelector(`[data-mn-proj-vendor="${CSS.escape(id)}"]`) as HTMLSelectElement | null)?.value ?? '';
      const assign = (this.container.querySelector(`[data-mn-proj-assign="${CSS.escape(id)}"]`) as HTMLInputElement | null)?.value ?? '';
      const body: any = {};
      if (date !== (this.schedOf(wo) || '')) body.date = date || null;
      if ((vendor || null) !== (wo?.vendor_id || null)) body.vendor_id = vendor || null;
      if (assign.trim() !== String(wo?.assigned_to || '').trim()) body.assigned_to = assign.trim();
      if (!Object.keys(body).length) { alert('Nothing changed — pick a day, a vendor or a person.'); return; }
      if (!date && !wo?.vendor_id && !vendor && !assign.trim()) { alert('Give it a day, a vendor or a person.'); return; }
      if (await this.scheduleWo(id, body)) this.draw();
    }));
    all('[data-mn-proj-unbook]').forEach(el => el.addEventListener('click', async () => { if (await this.scheduleWo(el.dataset.mnProjUnbook!, { date: null })) this.draw(); }));
    // calendar (month grid = the scheduler)
    all('[data-mn-cal-view]').forEach(el => el.addEventListener('click', async () => {
      this.calView = el.dataset.mnCalView as any;
      if (this.calView === 'month' && !this.calGrid) await this.reloadCalGrid();
      if (this.calView === 'windows' && this.calendar == null) await this.reloadCalendar();
      this.draw();
    }));
    all('[data-mn-cal-month]').forEach(el => el.addEventListener('click', async () => {
      const d = Number(el.dataset.mnCalMonth);
      const cur = this.calMonth || this.todayLocal().slice(0, 7);
      if (d === 0) this.calMonth = this.todayLocal().slice(0, 7);
      else { const [y, m] = cur.split('-').map(Number); this.calMonth = new Date(Date.UTC(y, m - 1 + d, 1)).toISOString().slice(0, 7); }
      this.calDaySel = '';
      await this.reloadCalGrid(); this.draw();
    }));
    $('[data-mn-cal-lid]')?.addEventListener('change', async (e) => { this.calLid = (e.currentTarget as HTMLSelectElement).value; this.calDaySel = ''; await this.reloadCalGrid(); this.draw(); });
    const calSearchEl = $('[data-mn-cal-search]') as HTMLInputElement | null;
    calSearchEl?.addEventListener('change', () => { this.calSearch = calSearchEl.value; this.draw(); });
    calSearchEl?.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') { this.calSearch = calSearchEl.value; this.draw(); } });
    // vendor / tech typed in the rail ride along with a booking (or save on their own) —
    // scoped to the clicked row (review-2: a due-only WO renders in the day rail AND the backlog)
    const calWho = (id: string, from?: Element | null): any => {
      const g = this.calGrid; const out: any = {};
      const it = g ? ([...(g.unscheduled || []), ...(g.days || []).flatMap((d: any) => d.items || [])].find((x: any) => x.id === id)) : null;
      const scope: ParentNode = (from && from.closest('.mn-cal-rrow, .mn-cal-urow')) || this.container;
      const vSel = scope.querySelector(`[data-mn-cal-vendor="${CSS.escape(id)}"]`) as HTMLSelectElement | null;
      const aInp = scope.querySelector(`[data-mn-cal-assign="${CSS.escape(id)}"]`) as HTMLInputElement | null;
      if (vSel && (vSel.value || null) !== ((it && it.vendor_id) || null)) out.vendor_id = vSel.value || null;
      if (aInp && aInp.value.trim() !== String((it && it.assigned_to) || '').trim()) out.assigned_to = aInp.value.trim();
      return out;
    };
    all('[data-mn-cal-pick]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = el.dataset.mnCalPick!;
      const on = this.calPick !== id;
      this.calPickWho = on ? calWho(id, el) : null;   // capture the row's vendor / tech BEFORE the redraw resets the inputs
      this.calPick = on ? id : null;
      this.draw();
      // R115 maint: on a phone the agenda sits ABOVE the rail — bring it up so the next tap is a day
      if (on && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) this.container.querySelector('.mn-cal-agenda')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    all('[data-mn-cal-day]').forEach(el => {
      el.addEventListener('click', async () => {
        const date = el.dataset.mnCalDay!;
        // R115 maint: a picked WO is never booked into the past (the calendar hub refuses past days too)
        if (this.calPick && date < this.calToday()) { alert('That day has passed — pick today or later.'); return; }
        if (this.calPick) { const id = this.calPick; if (await this.scheduleWo(id, { date, ...(this.calPickWho || {}) })) { this.calPick = null; this.calPickWho = null; } this.draw(); return; }
        this.calDaySel = this.calDaySel === date ? '' : date; this.draw();
        // R115 maint: the day's detail lives in the rail, below the agenda on a phone
        if (this.calDaySel && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) this.container.querySelector('.mn-cal-dayd')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      el.addEventListener('keydown', (e) => { const k = (e as KeyboardEvent).key; if ((k === 'Enter' || k === ' ') && el.classList.contains('mn-cal-ag-day')) { e.preventDefault(); (el as HTMLElement).click(); } });
      el.addEventListener('dragover', (e) => { e.preventDefault(); el.classList.add('over'); });
      el.addEventListener('dragleave', () => el.classList.remove('over'));
      el.addEventListener('drop', async (e) => {
        e.preventDefault(); el.classList.remove('over');
        const id = (e as DragEvent).dataTransfer?.getData('text/wo') || '';
        if (!id) return;
        if (el.dataset.mnCalDay! < this.calToday()) { alert('That day has passed — pick today or later.'); return; }
        const dragRow = this.container.querySelector(`[data-mn-cal-drag="${CSS.escape(id)}"]`);
        if (await this.scheduleWo(id, { date: el.dataset.mnCalDay!, ...calWho(id, dragRow) })) { this.calPick = null; this.calPickWho = null; this.draw(); }
      });
    });
    all('[data-mn-cal-drag], .mn-cal-it[draggable="true"]').forEach(el => el.addEventListener('dragstart', (e) => {
      const id = el.dataset.mnCalDrag || (el.dataset.mnCalItem || '').split('|')[1] || '';
      (e as DragEvent).dataTransfer?.setData('text/wo', id);
    }));
    all('[data-mn-cal-item]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const [kind, id] = (el.dataset.mnCalItem || '').split('|');
      if (kind === 'pm') { this.tab = 'recurring'; this.recSub = 'plans'; this.draw(); return; }
      if (id) this.drillWo(id);
    }));
    all('[data-mn-cal-open]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.drillWo(el.dataset.mnCalOpen!); }));
    all('[data-mn-cal-book]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnCalBook!;
      const row = el.closest('.mn-cal-rrow, .mn-cal-urow');
      const on = el.dataset.mnCalBookon || ((row || this.container).querySelector(`[data-mn-cal-bookdate="${CSS.escape(id)}"]`) as HTMLInputElement | null)?.value || '';
      if (!on) { alert('Pick a day first.'); return; }
      if (await this.scheduleWo(id, { date: on, ...calWho(id, el) })) { this.calPick = null; this.calPickWho = null; this.draw(); }
    }));
    all('[data-mn-cal-who]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnCalWho!;
      const who = calWho(id, el);
      if (!Object.keys(who).length) { alert('Nothing changed — pick a vendor or type a person.'); return; }
      if (await this.scheduleWo(id, who)) this.draw();
    }));
    $('[data-mn-cal-retry]')?.addEventListener('click', async () => { this.calGridError = false; this.calGrid = null; await this.reloadCalGrid(); this.draw(); });
    all('[data-mn-cal-unbook]').forEach(el => el.addEventListener('click', async (e) => { e.stopPropagation(); if (await this.scheduleWo(el.dataset.mnCalUnbook!, { date: null })) this.draw(); }));
    $('[data-mn-cal-dayclose]')?.addEventListener('click', () => { this.calDaySel = ''; this.draw(); });
    all('[data-mn-cal-more]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); this.calDaySel = el.dataset.mnCalMore!; this.draw(); }));
    // day plan staffing (R102 #9)
    $('[data-mn-dayplan-staff]')?.addEventListener('change', (e) => {
      const v = Number((e.currentTarget as HTMLSelectElement).value);
      this.dayPlanStaff = v > 0 ? v : null;
      this.dayPlanEditWo = null;
      void this.loadDayPlan();
    });
    // A BOOKED job moves its booked day; a due-dated job moves its due date; checkout-tracked jobs can't be pushed.
    const deferOne = async (id: string, date: string) => {
      const next = this.addDaysStr(date, 1);
      const row = (this.dayPlan?.groups || []).flatMap((g: any) => g.wos || []).find((w: any) => w.id === id);   // review: the plan row is the truth, not the cached WO list
      const booked = row ? !!(row.booked || row.overdue_booking) : this.schedOf((this.workOrders || []).find(w => w.id === id)) === date;
      if (booked) return this.post(`/work-orders/${encodeURIComponent(id)}/schedule`, { date: next, by: MaintenancePage.actorName() });
      return this.post(`/work-orders/${encodeURIComponent(id)}`, { due_date: next, note: 'deferred from the day plan (staffing)' });
    };
    all('[data-mn-dp-defer]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!this.dayPlan) return;
      await deferOne(el.dataset.mnDpDefer!, this.dayPlan.date);
      this.calGrid = null;
      await Promise.all([this.loadDayPlan(), this.reloadWos()]); this.draw();
    }));
    $('[data-mn-dp-deferall]')?.addEventListener('click', async () => {
      const st = this.dayPlan && this.dayPlan.staffing; if (!st) return;
      const list = (st.deferred || []).filter((d: any) => d.booked || d.overdue_booking || !((this.workOrders || []).find(w => w.id === d.id)?.due_source === 'checkout'));
      if (!list.length) { alert('Every deferred job follows a checkout — those can\'t be pushed.'); return; }
      if (!window.confirm(`Move ${list.length} deferred job${list.length === 1 ? '' : 's'} to ${this.addDaysStr(this.dayPlan.date, 1)}?`)) return;
      for (const d of list) await deferOne(d.id, this.dayPlan.date);
      this.calGrid = null;
      await Promise.all([this.loadDayPlan(), this.reloadWos()]); this.draw();
    });

    // ── board ──
    // R81 #6 (Michelle: "download work orders as a CSV, the same way I can
    // download resolutions"): every loaded WO, all statuses, flat columns.
    all('[data-mn-wocsv]').forEach(el => el.addEventListener('click', () => {
      const wos = this.workOrders || [];
      const esc2 = (v: any) => { const s2 = String(v == null ? '' : v); return /[",\n\r]/.test(s2) ? `"${s2.replace(/"/g, '""')}"` : s2; };
      // Field fallbacks span WO generations (old rows carry summary/internal_notes,
      // newer ones description/priority/cost — union verified against the live store).
      const head = ['id', 'property', 'title', 'description', 'status', 'priority', 'skill', 'type', 'assignee', 'created_at', 'completed_at', 'due', 'due_source', 'created_by', 'cost', 'billing_status', 'payer', 'source', 'minutes_tracked', 'notes'];
      const lines = [head.join(',')];
      for (const w of wos) {
        lines.push([
          w.id, w.listing_name, w.title, w.description || w.summary || '', w.status, w.priority || '', w.skill || '', w.type || '',
          w.assigned_to || '', String(w.created_at || '').slice(0, 10), String(this.woDoneAt(w) || '').slice(0, 10),   // R109 #c: a reopened WO has no completion date
          w.effective_due || w.due_date || '', w.due_source || '', w.created_by || '',
          w.cost != null ? w.cost : '', w.billing_status || '', w.payer || '',
          w.source || '', w.total_minutes != null ? w.total_minutes : '',
          (Array.isArray(w.internal_notes) ? w.internal_notes : []).map((n2: any) => (n2 && n2.text) || '').filter(Boolean).join(' | '),   // review #8: notes are {at,by,text} objects
        ].map(esc2).join(','));
      }
      const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `work_orders_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }));
    // ── R90 #2: property expense review binds ──
    (this.container.querySelector('#mn-pr-prop') as HTMLInputElement | null)?.addEventListener('change', async (e) => {
      const typed = (e.target as HTMLInputElement).value.trim().toLowerCase();
      if (!typed) return;   // review #27: whitespace must not silently load the first roster row
      const ls = this.prListings || [];
      const label = (l: any) => String(l.short_name || l.name || l.id);
      let hit = ls.find((l: any) => label(l).toLowerCase() === typed);
      if (!hit) {
        const fuzzy = ls.filter((l: any) => label(l).toLowerCase().includes(typed) || String(l.name || '').toLowerCase().includes(typed));
        if (fuzzy.length > 1) { alert('Several properties match that — keep typing or pick one from the list.'); return; }   // review #22
        if (!fuzzy.length) { alert('No property matched — pick one from the list.'); return; }
        hit = fuzzy[0];
      }
      this.prLid = String(hit.id);
      this.prData = null;
      this.draw();
      const lid = this.prLid;   // capture — a second pick mid-flight must not clobber
      const j = await fetch(`${API}/work-orders/by-listing/${encodeURIComponent(lid)}`).then(r => r.ok ? r.json() : null).catch(() => null);
      if (this.prLid !== lid) return;
      this.prData = j ? { ...j, listing_name: label(hit) } : { error: true };
      this.draw();
    });
    const prRefetch = async () => {
      const lid = this.prLid;
      if (!lid) return;
      const j = await fetch(`${API}/work-orders/by-listing/${encodeURIComponent(lid)}`).then(r => r.ok ? r.json() : null).catch(() => null);
      if (this.prLid !== lid) return;
      if (j) this.prData = { ...j, listing_name: this.prData?.listing_name };
      this.draw();
    };
    all('[data-mn-pr-noteinp]').forEach(el => el.addEventListener('input', () => {
      this.prDrafts.set((el as HTMLInputElement).dataset.mnPrNoteinp!, (el as HTMLInputElement).value);
    }));
    all('[data-mn-pr-note]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnPrNote!;
      const text = (this.prDrafts.get(id) || '').trim();
      if (!text) { (this.container.querySelector(`[data-mn-pr-noteinp="${CSS.escape(id)}"]`) as HTMLInputElement | null)?.focus(); return; }
      this.prBusy = id; this.draw();
      const r = await fetch(`${API}/work-orders/${encodeURIComponent(id)}/review-note`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, by: MaintenancePage.actorName() }),
      }).catch(() => null);
      this.prBusy = '';
      if (!r || !r.ok) { alert('Note didn\'t save — the text is still in the box, try again.'); this.draw(); return; }   // review #26: the draft survives a failed save
      this.prDrafts.delete(id);
      await prRefetch();
    }));
    all('[data-mn-pr-reviewed]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnPrReviewed!;
      const w = (this.prData?.rows || []).find((x: any) => x.id === id);
      this.prBusy = id; this.draw();
      const r = await fetch(`${API}/work-orders/${encodeURIComponent(id)}/reviewed`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ on: !(w && w.ground_review), by: MaintenancePage.actorName() }),
      }).catch(() => null);
      this.prBusy = '';
      if (!r || !r.ok) { alert('Didn\'t save — try again.'); this.draw(); return; }
      await prRefetch();
    }));
    all('[data-mn-newwo]').forEach(el => el.addEventListener('click', () => {
      const v = el.dataset.mnNewwo || '';
      const [id, name] = v.split('|');
      this.showNewWoModal(id || (this.board?.rows?.[0]?.listing_id || ''), name || '');
    }));
    all('[data-mn-startpm]').forEach(el => el.addEventListener('click', async () => {
      const [id, name] = (el.dataset.mnStartpm || '').split('|');
      const tpls = this.templates || [];
      if (!tpls.length) { alert('No PM template exists yet — create one under Recurring maintenance → Templates.'); this.tab = 'recurring'; this.recSub = 'templates'; this.draw(); return; }
      // If a single standard template, create straight away; otherwise open the modal pre-set to PM.
      if (tpls.length === 1) {
        let j = await this.post('/work-orders', { type: 'pm', template_id: tpls[0].id, listing_id: id, listing_name: name, by: MaintenancePage.actorName() });
        // R70 review: the single-template shortcut needs the same owner-handled
        // override the modal path has, or it dead-ends for the office.
        if (!j && this._lastErrCode === 'owner_handled' && ['admin', 'director', 'manager', 'remote_ops'].includes(MaintenancePage.fieldRole())) {
          if (confirm('The owner handles maintenance at this property. Create this PM anyway (owner asked us to step in)?')) {
            j = await this.post('/work-orders', { type: 'pm', template_id: tpls[0].id, listing_id: id, listing_name: name, owner_handled_override: true });
          }
        }
        if (j) {
          if (j.work_order?.id) this.expandedWo.add(j.work_order.id);
          if (this.tab === 'board') { this.projectLid = id; this.tab = 'project'; }
          else if (this.tab !== 'project') {   // R102 #4 / R103: started from the Project → stay there; from the board → its Project
            this.tab = 'work_orders';
            this.woListingFilter = '';
            this.woStatusFilter = '';
            this.clearWoSearchForNew();
          }
          await Promise.all([this.reloadWos(), this.reloadBoard()]);
          this.draw();
        }
      } else {
        this.showNewWoModal(id, name);
      }
    }));
    // ── owner care report (Board + Assets) — open the print-to-PDF doc in a new tab ──
    all('[data-mn-care-report]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = el.dataset.mnCareReport!;
      void openApiDoc(`${API}/maintenance/care-report/${encodeURIComponent(id)}?days=180`, 'Home care report');
    }));

    // ── incidents: filters ──
    all('[data-mn-incfilter]').forEach(el => el.addEventListener('change', async () => {
      const sel = el as unknown as HTMLSelectElement;
      if (el.dataset.mnIncfilter === 'listing') { this.incListingFilter = sel.value; this.draw(); return; }   // client-side — no refetch
      if (el.dataset.mnIncfilter === 'severity') this.incSeverityFilter = sel.value;
      else this.incStatusFilter = sel.value;
      await this.reloadIncidents();
      this.draw();
    }));
    // ── incidents: log form toggle ──
    all('[data-mn-inc-log]').forEach(el => el.addEventListener('click', () => {
      this.showLogIncident = !this.showLogIncident;
      this.incRequestId = this.showLogIncident ? MaintenancePage.newRequestId() : '';   // AI-unblock (review #18)
      this.draw();
    }));
    all('[data-mn-inc-cancel]').forEach(el => el.addEventListener('click', () => {
      this.showLogIncident = false;
      this.incRequestId = '';
      this.draw();
    }));
    // ── incidents: create ──
    all('[data-mn-inc-create]').forEach(el => el.addEventListener('click', async () => {
      const btn = el as HTMLButtonElement;
      if (this.incCreating || btn.disabled) return;   // AI-unblock: one tap = one incident
      const form = this.container.querySelector('[data-mn-inc-formfor="new"]');
      if (!form) return;
      const val = (k: string) => (form.querySelector(`[data-mn-if="${k}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value.trim() || '';
      const listing_id = val('listing_id');
      const title = val('title');
      if (!listing_id) { alert('Pick a property.'); return; }
      if (!title) { alert('Give the incident a title.'); return; }
      const body: any = { listing_id, title, description: val('description'), by: MaintenancePage.actorName() };   // R109 #g: who reported it shows on the card
      const severity = val('severity'); if (severity) body.severity = severity;
      const category = val('category'); if (category) body.category = category;
      // AI-unblock (review #18): the same id on every retry of this form (see incRequestId).
      if (!this.incRequestId) this.incRequestId = MaintenancePage.newRequestId();
      body.client_request_id = this.incRequestId;
      this.incCreating = true; btn.disabled = true; btn.textContent = 'Logging…';
      let j: any = null;
      try { j = await this.post('/incidents', body); }
      catch (_) { alert('Couldn\'t reach the server — check your signal and tap Log incident again. If the first tap already went through, it won\'t be logged (or paged) twice.'); }
      finally { this.incCreating = false; }
      if (j && j.incident) {
        this.showLogIncident = false;
        this.incRequestId = '';
        this.expandedInc = j.incident.id;
        this.incDetail[j.incident.id] = { incident: j.incident, on_call_vendors: [] };
        await this.reloadIncidents();
        await this.reloadIncidentDetail(j.incident.id);
        this.draw();
        if (j.incident.triage && j.incident.triage.status === 'pending') this.watchIncidentTriage(j.incident.id);
      } else if (btn.isConnected) { btn.disabled = false; btn.textContent = 'Log incident'; }
      else this.draw();
    }));
    // ── incidents: expand → command view (lazy-load detail for on-call vendors) ──
    all('[data-mn-inc-toggle]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnIncToggle!;
      if (this.expandedInc === id) { this.expandedInc = null; this.draw(); return; }
      this.expandedInc = id;
      this.draw();
      if (!this.incDetail[id]) { await this.reloadIncidentDetail(id); this.draw(); }
      if (this.incDetail[id]?.incident?.triage?.status === 'pending') this.watchIncidentTriage(id);   // AI-unblock: logged elsewhere, still triaging
    }));
    // ── incidents: deflection toggles (sent / self_resolved) ──
    all('[data-mn-inc-deflect]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [id, field] = el.dataset.mnIncDeflect!.split('|');
      const cur = this.incDetail[id]?.incident || (this.incidents || []).find(i => i.id === id);
      const next = !(cur?.deflection?.[field]);
      const j = await this.post(`/incidents/${id}`, { deflection: { [field]: next } });
      if (j && j.incident) {
        if (this.incDetail[id]) this.incDetail[id].incident = j.incident;
        await this.reloadIncidents();
        if (this.tab === 'board' || this.tab === 'project') await this.reloadBoard();   // review R103 #22: the row chip + KPIs must follow
        this.draw();
      }
    }));
    // ── incidents: dispatch (creates the repair WO) ──
    all('[data-mn-inc-dispatch]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnIncDispatch!;
      const vendor_id = (this.container.querySelector(`[data-mn-inc-vendor="${id}"]`) as HTMLSelectElement | null)?.value || null;
      const j = await this.post(`/incidents/${id}/dispatch`, vendor_id ? { vendor_id } : {});
      if (j && j.incident) {
        if (this.incDetail[id]) this.incDetail[id].incident = j.incident;
        // a new WO now exists — invalidate the WO/vendor/cost views.
        this.vendorWos = {};
        this.costs = null;
        await Promise.all([this.reloadIncidents(), this.reloadWos(), this.reloadVendors(), this.reloadBoard()]);
        this.draw();
      }
    }));
    // ── incidents: comp save ──
    all('[data-mn-inc-comp-save]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnIncCompSave!;
      const get = (f: string) => (this.container.querySelector(`[data-mn-inc-comp="${id}|${f}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
      const decidedRaw = get('decided').trim();
      const comp: any = {
        decided: decidedRaw === '' ? null : Number(decidedRaw),
        payer: get('payer') || null,
        note: get('note'),
      };
      const j = await this.post(`/incidents/${id}`, { comp });
      if (j && j.incident) {
        if (this.incDetail[id]) this.incDetail[id].incident = j.incident;
        await this.reloadIncidents();
        if (this.tab === 'board' || this.tab === 'project') await this.reloadBoard();   // review R103 #22: the row chip + KPIs must follow
        this.draw();
      }
    }));
    // ── incidents: status buttons (acknowledge / resolve) ──
    all('[data-mn-inc-status]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      if ((el as HTMLButtonElement).disabled) return;
      const [id, status] = el.dataset.mnIncStatus!.split('|');
      const j = await this.post(`/incidents/${id}`, { status });
      if (j && j.incident) {
        if (this.incDetail[id]) this.incDetail[id].incident = j.incident;
        await this.reloadIncidents();
        if (this.tab === 'board' || this.tab === 'project') await this.reloadBoard();   // review R103 #22: the row chip + KPIs must follow
        this.draw();
      }
    }));
    // ── incidents: review-risk toggle ──
    all('[data-mn-inc-risk]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnIncRisk!;
      const cur = this.incDetail[id]?.incident || (this.incidents || []).find(i => i.id === id);
      const j = await this.post(`/incidents/${id}`, { review_risk: !cur?.review_risk });
      if (j && j.incident) {
        if (this.incDetail[id]) this.incDetail[id].incident = j.incident;
        await this.reloadIncidents();
        if (this.tab === 'board' || this.tab === 'project') await this.reloadBoard();   // review R103 #22: the row chip + KPIs must follow
        this.draw();
      }
    }));
    // ── incidents: re-triage (AI) ──
    all('[data-mn-inc-retriage]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnIncRetriage!;
      const j = await this.post(`/incidents/${id}/triage`, {});
      if (j) {
        if (j.configured === false) alert('AI not configured — set ANTHROPIC_API_KEY on the server to enable triage. Set severity/category by hand for now.');
        if (j.incident && this.incDetail[id]) this.incDetail[id].incident = j.incident;
        await this.reloadIncidents();
        if (this.tab === 'board' || this.tab === 'project') await this.reloadBoard();   // review R103 #22: the row chip + KPIs must follow
        this.draw();
      }
    }));

    // ── work orders: filters ──
    // R70: dependent controls enable/disable live (save still commits).
    all('[data-mn-wogp]').forEach(el => el.addEventListener('change', (e) => {
      const id = (el as HTMLElement).dataset.mnWogp!;
      const sel = this.container.querySelector(`[data-mn-wogpmode="${id}"]`) as HTMLSelectElement | null;
      if (sel) sel.disabled = !(e.currentTarget as HTMLInputElement).checked;
    }));
    all('[data-mn-woduesrc]').forEach(el => el.addEventListener('change', (e) => {
      const id = (el as HTMLElement).dataset.mnWoduesrc!;
      const due = this.container.querySelector(`[data-mn-wodue="${id}"]`) as HTMLInputElement | null;
      if (due) due.disabled = (e.currentTarget as HTMLInputElement).checked;
    }));

    // R70: day plan toggle + date.
    all('[data-mn-dayplan-toggle]').forEach(el => el.addEventListener('click', () => {
      this.dayPlanOpen = !this.dayPlanOpen;
      this.draw();
      if (this.dayPlanOpen && !this.dayPlan) void this.loadDayPlan();
    }));
    all('[data-mn-dayplan-date]').forEach(el => el.addEventListener('change', (e) => {
      // R50 lesson: Chrome fires change per date SEGMENT — debounce so a
      // half-typed year doesn't fetch + redraw mid-edit.
      const inp = e.currentTarget as HTMLInputElement;
      if (this.dayPlanDateTimer) window.clearTimeout(this.dayPlanDateTimer);
      this.dayPlanDateTimer = window.setTimeout(() => {
        this.dayPlanDateTimer = null;
        if (!inp.value || !inp.checkValidity()) return;
        this.dayPlanDate = inp.value;
        void this.loadDayPlan();
      }, 600);
    }));

    // R72: day-plan interactions — must-do toggle, tap-to-edit, save, send.
    all('[data-mn-dayplan-mustdo]').forEach(el => el.addEventListener('change', (e) => {
      this.dayPlanMustDo = (e.currentTarget as HTMLInputElement).checked;
      this.dayPlanEditWo = null;
      void this.loadDayPlan();
    }));
    all('[data-mn-dp-wo]').forEach(el => el.addEventListener('click', () => {
      // Selecting text to copy must not toggle the editor (review).
      if (window.getSelection()?.toString()) return;
      const id = (el as HTMLElement).dataset.mnDpWo!;
      if (this.dayPlanEditWo && this.dayPlanEditWo !== id) {
        // Closing a DIFFERENT open editor — warn if its instructions changed.
        const ed = this.container.querySelector(`[data-mn-dp-editor="${CSS.escape(this.dayPlanEditWo)}"]`) as HTMLElement | null;
        const ta = ed?.querySelector('[data-dpe-desc]') as HTMLTextAreaElement | null;
        const wRow = (this.dayPlan?.groups || []).flatMap((g: any) => g.wos).find((w: any) => w.id === this.dayPlanEditWo);
        if (ta && wRow && ta.value !== (wRow.description || '') && !confirm('Discard unsaved instruction edits on the open work order?')) return;
      }
      this.dayPlanEditWo = this.dayPlanEditWo === id ? null : id;
      this.draw();
    }));
    all('[data-mn-dp-editor]').forEach(el => el.addEventListener('click', (e) => e.stopPropagation()));
    all('[data-dpe-save]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = (el as HTMLElement).dataset.dpeSave!;
      const ed = this.container.querySelector(`[data-mn-dp-editor="${CSS.escape(id)}"]`) as HTMLElement | null;
      if (!ed) return;
      const wRow = (this.dayPlan?.groups || []).flatMap((g: any) => g.wos).find((w: any) => w.id === id) || {};
      const body: any = {};
      const desc = (ed.querySelector('[data-dpe-desc]') as HTMLTextAreaElement)?.value ?? '';
      if (desc !== (wRow.description || '')) body.description = desc;
      const due = (ed.querySelector('[data-dpe-due]') as HTMLInputElement | null);
      if (due && !due.disabled && (due.value || null) !== (wRow.due_date || null)) body.due_date = due.value || null;
      const asg = (ed.querySelector('[data-dpe-assign]') as HTMLInputElement)?.value.trim() ?? '';
      if (asg !== (wRow.assigned_to || '')) body.assigned_to = asg;
      const prio = (ed.querySelector('[data-dpe-prio]') as HTMLSelectElement)?.value;
      if (prio && prio !== (wRow.priority || 'routine')) body.priority = prio;
      const skill = (ed.querySelector('[data-dpe-skill]') as HTMLSelectElement)?.value || null;
      if (skill !== (wRow.skill || null)) body.skill = skill;
      const status = (ed.querySelector('[data-dpe-status]') as HTMLSelectElement)?.value;
      if (status && status !== wRow.status) body.status = status;
      if (!Object.keys(body).length) { this.dayPlanEditWo = null; this.draw(); return; }
      (el as HTMLButtonElement).disabled = true;
      const j = await this.post(`/work-orders/${id}`, body);
      if (j) {
        this.dayPlanEditWo = null;
        await Promise.all([this.loadDayPlan(), this.reloadWos()]);   // the plan REFIGURES route + staffing
        this.draw();
      } else { (el as HTMLButtonElement).disabled = false; }
    }));
    all('[data-mn-gmaps]').forEach(el => el.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.dayPlan?.gmaps_url) openExternal(this.dayPlan.gmaps_url);
    }));
    all('[data-mn-dayplan-copy]').forEach(el => el.addEventListener('click', async () => {
      const text = this.dayPlanAsText();
      try { await navigator.clipboard.writeText(text); alert('Day plan copied — paste it into a text or chat.'); }
      catch (_) { window.prompt('Copy the plan:', text); }
    }));
    all('[data-mn-dayplan-print]').forEach(el => el.addEventListener('click', () => {
      const w = window.open('', '_blank');
      const text = this.dayPlanAsText();
      if (w) {
        w.document.write(`<title>Day plan ${this.esc(this.dayPlan?.date || '')}</title><pre style="font-family:-apple-system,monospace; font-size:13px; padding:24px; white-space:pre-wrap;">${this.esc(text)}</pre><script>setTimeout(()=>print(),300)<\/script>`);
        w.document.close();
      } else {
        window.prompt('Popup blocked — copy the plan:', text);
      }
    }));
    all('[data-mn-dayplan-slack]').forEach(el => el.addEventListener('click', async () => {
      const btn = el as HTMLButtonElement;
      btn.disabled = true;   // double-click = double-post (review)
      const j = await this.post('/maintenance/day-plan/send', { text: this.dayPlanAsText() });
      btn.disabled = false;
      if (j) alert(j.truncated ? 'Day plan posted to Slack (long plan — trimmed to fit).' : 'Day plan posted to Slack.');
    }));

    all('[data-mn-wofilter]').forEach(el => el.addEventListener('change', () => {
      const sel = el as unknown as HTMLSelectElement;
      if (el.dataset.mnWofilter === 'status') this.woStatusFilter = sel.value;
      else if (el.dataset.mnWofilter === 'role') this.woRoleFilter = sel.value;
      else if (el.dataset.mnWofilter === 'assignee') { this.woAssigneeFilter = sel.value; this.assigneeAuto = false; }
      else if (el.dataset.mnWofilter === 'billed') this.woBilledFilter = sel.value;
      // R50 — sort dir + created-date range. Review fix: Chrome fires 'change'
      // per date SEGMENT, and an instant draw() destroys the input mid-edit —
      // debounce the date branches (invalid/partial values never apply).
      else if (el.dataset.mnWofilter === 'sortdir') this.woSortDir = sel.value === 'oldest' ? 'oldest' : 'newest';
      else if (el.dataset.mnWofilter === 'datemode') this.woDateMode = sel.value === 'completed' ? 'completed' : 'created';   // R109 #c
      else if (el.dataset.mnWofilter === 'datefrom' || el.dataset.mnWofilter === 'dateto') {
        const which = el.dataset.mnWofilter;
        if (this.woDateTimer) window.clearTimeout(this.woDateTimer);
        this.woDateTimer = window.setTimeout(() => {
          this.woDateTimer = null;
          if (sel.value && !(el as HTMLInputElement).checkValidity()) return;
          if (which === 'datefrom') this.woDateFrom = sel.value; else this.woDateTo = sel.value;
          this.draw();
        }, 600);
        return;
      }
      else this.woListingFilter = sel.value;
      this.draw();
    }));
    // R109 #d: live search — debounced, then a full redraw (bind() can't patch a
    // subtree) with focus + caret put back so typing never stalls.
    const woQIn = $('[data-mn-wo-q]') as HTMLInputElement | null;
    const redrawKeepingSearch = () => {
      const cur = this.container.querySelector<HTMLInputElement>('[data-mn-wo-q]');
      const pos = cur && document.activeElement === cur ? (cur.selectionStart ?? cur.value.length) : null;
      this.draw();
      const again = this.container.querySelector<HTMLInputElement>('[data-mn-wo-q]');
      if (again && pos != null) { again.focus(); try { again.setSelectionRange(pos, pos); } catch (_) { /* type=search may refuse */ } }
    };
    woQIn?.addEventListener('input', (e) => {
      if ((e as InputEvent).isComposing) return;   // IME mid-word
      this.woQ = woQIn.value; this.woQShown = 100;
      if (this.woQTimer) window.clearTimeout(this.woQTimer);
      this.woQTimer = window.setTimeout(() => { this.woQTimer = null; redrawKeepingSearch(); }, 180);
    });
    woQIn?.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.isComposing) return;
      if (ke.key === 'Escape') {
        e.preventDefault();
        if (this.woQTimer) { window.clearTimeout(this.woQTimer); this.woQTimer = null; }
        this.woQ = ''; this.woQShown = 100;
        redrawKeepingSearch();
      } else if (ke.key === 'Enter') {
        // Enter = open the best match right here
        e.preventDefault();
        if (this.woQTimer) { window.clearTimeout(this.woQTimer); this.woQTimer = null; }
        this.woQ = woQIn.value;
        this.draw();
        const first = this.container.querySelector<HTMLElement>('.mn-wo [data-mn-wotoggle]');
        const id = first && first.dataset.mnWotoggle;
        if (!id) { this.container.querySelector<HTMLInputElement>('[data-mn-wo-q]')?.focus(); return; }
        if (!this.expandedWo.has(id)) void this.toggleWo(id);   // expands + redraws synchronously; the field panel loads after
        this.container.querySelector(`[data-mn-wotoggle="${CSS.escape(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    $('[data-mn-wo-qclear]')?.addEventListener('click', () => { this.woQ = ''; this.woQShown = 100; this.draw(); this.container.querySelector<HTMLInputElement>('[data-mn-wo-q]')?.focus(); });
    $('[data-mn-wo-qallf]')?.addEventListener('click', () => { const q = this.woQ; this.resetWoFilters(); this.woQ = q; this.draw(); });
    $('[data-mn-wo-qmore]')?.addEventListener('click', () => { this.woQShown += 100; this.draw(); });
    // R109 #c: the project's "Done" window — chips, or a typed from/to (debounced like the WO dates)
    all('[data-mn-pj-donedays]').forEach(el => el.addEventListener('click', () => {
      this.projDoneDays = Number(el.dataset.mnPjDonedays) || 0; this.projDoneFrom = ''; this.projDoneTo = ''; this.draw();
    }));
    all('[data-mn-pj-donedate]').forEach(el => el.addEventListener('change', () => {
      const inp = el as HTMLInputElement; const which = el.dataset.mnPjDonedate;
      if (this.projDoneTimer) window.clearTimeout(this.projDoneTimer);
      this.projDoneTimer = window.setTimeout(() => {
        this.projDoneTimer = null;
        if (inp.value && !inp.checkValidity()) return;
        if (which === 'from') this.projDoneFrom = inp.value; else this.projDoneTo = inp.value;
        this.draw();
      }, 600);
    }));
    $('[data-mn-pj-doneclear]')?.addEventListener('click', () => { this.projDoneFrom = ''; this.projDoneTo = ''; this.draw(); });
    all('[data-mn-wo-review]').forEach(el => el.addEventListener('click', () => { this.woReviewOnly = !this.woReviewOnly; this.draw(); }));
    all('[data-mn-wo-urgent]').forEach(el => el.addEventListener('click', () => {
      this.woUrgentOnly = !this.woUrgentOnly; this.draw();
    }));
    all('[data-mn-wo-group]').forEach(el => el.addEventListener('click', () => {
      this.woGroupByProperty = !this.woGroupByProperty; this.draw();
    }));
    all('[data-mn-owner-summary]').forEach(el => el.addEventListener('click', () => {
      this.copyOwnerSummary(el.dataset.mnOwnerSummary!);
    }));

    // ── work orders: expand row ──
    all('[data-mn-wotoggle]').forEach(el => el.addEventListener('click', () => void this.toggleWo(el.dataset.mnWotoggle!)));
    // R105 #3: internal WO comments (state-backed draft; ⌘/Ctrl+Enter posts)
    all('[data-mn-cmt-draft]').forEach(el => {
      const ta = el as HTMLTextAreaElement; const id = ta.dataset.mnCmtDraft!;
      ta.addEventListener('input', () => { this.cmtDrafts[id] = ta.value; const b = this.container.querySelector<HTMLButtonElement>(`[data-mn-cmt-post="${CSS.escape(id)}"]`); if (b) b.disabled = !ta.value.trim(); });
      ta.addEventListener('keydown', (e) => { const ke = e as KeyboardEvent; if (ke.key === 'Enter' && (ke.metaKey || ke.ctrlKey)) { e.preventDefault(); this.container.querySelector<HTMLButtonElement>(`[data-mn-cmt-post="${CSS.escape(id)}"]`)?.click(); } });
    });
    all('[data-mn-cmt-post]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnCmtPost!; const text = (this.cmtDrafts[id] || '').trim(); if (!text) return;
      (el as HTMLButtonElement).disabled = true;
      const j = await this.post(`/work-orders/${encodeURIComponent(id)}/comments`, { text, by: this.who() });
      if (!j) { (el as HTMLButtonElement).disabled = false; return; }
      delete this.cmtDrafts[id];
      const wo = (this.workOrders || []).find(w => w.id === id); if (wo) wo.comments = j.comments;
      this.patchWoComments(id);   // never a full draw() — unsaved edits elsewhere on the card must survive
    }));
    this.bindCmtRemove(this.container);

    // ── work orders: inline title rename (✏️ on an open card) ──
    all('[data-mn-wotitle-edit]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.editingWoTitle = el.dataset.mnWotitleEdit!;
      this.draw();
    }));
    all('[data-mn-wotitle-input]').forEach(el => el.addEventListener('click', (e) => e.stopPropagation()));
    all('[data-mn-wotitle-save]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnWotitleSave!;
      const inp = this.container.querySelector(`[data-mn-wotitle-input="${id}"]`) as HTMLInputElement | null;
      const title = (inp?.value || '').trim();
      if (!title) { inp?.focus(); return; }
      const j = await this.post(`/work-orders/${id}`, { title });
      if (!j) return; // post() already alerted
      const wo = (this.workOrders || []).find(w => w.id === id);
      if (wo) wo.title = title;
      this.editingWoTitle = null;
      this.draw();
    }));

    // ── work orders: inline instructions edit (✎ next to the label) ──
    all('[data-mn-wodesc-edit]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.editingWoDesc = el.dataset.mnWodescEdit!;
      this.draw();
    }));
    all('[data-mn-wodesc-input]').forEach(el => el.addEventListener('click', (e) => e.stopPropagation()));
    all('[data-mn-wodesc-save]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnWodescSave!;
      const inp = this.container.querySelector(`[data-mn-wodesc-input="${id}"]`) as HTMLTextAreaElement | null;
      const description = (inp?.value ?? '').trim();
      const j = await this.post(`/work-orders/${id}`, { description });
      if (!j) return; // post() already alerted
      const wo = (this.workOrders || []).find(w => w.id === id);
      if (wo) wo.description = description;
      this.editingWoDesc = null;
      this.draw();
    }));

    // ── work orders: item toggle done ──
    all('[data-mn-toggle]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [id, key] = el.dataset.mnToggle!.split('|');
      const wo = (this.workOrders || []).find(w => w.id === id);
      const item = wo?.items?.find((it: any) => it.key === key);
      const next = !(item?.done);
      if (await this.post(`/work-orders/${id}`, { items: [{ key, done: next }] })) {
        await this.reloadWos(); this.draw();
      }
    }));

    // ── work orders: item note ──
    all('[data-mn-itemnote]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [id, key] = el.dataset.mnItemnote!.split('|');
      const wo = (this.workOrders || []).find(w => w.id === id);
      const item = wo?.items?.find((it: any) => it.key === key);
      const note = prompt('Item note:', item?.note || '');
      if (note === null) return;
      if (await this.post(`/work-orders/${id}`, { items: [{ key, note }] })) {
        await this.reloadWos(); this.draw();
      }
    }));

    // ── work orders: item photo ──
    all('[data-mn-itemphoto]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const [id, key] = el.dataset.mnItemphoto!.split('|');
      this.uploadPhoto(id, key);
    }));

    // ── work orders: WO-level photo ──
    all('[data-mn-wophoto]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.uploadPhoto(el.dataset.mnWophoto!, null);
    }));

    // ── work orders: before/after photo (kind lands in photos_meta) ──
    all('[data-mn-wophoto-kind]').forEach(el => el.addEventListener('click', (e) => {
      e.stopPropagation();
      const [id, kind] = el.dataset.mnWophotoKind!.split('|');
      this.uploadPhoto(id, null, kind);
    }));

    // ── work orders: file the tech report + mark done (one POST) ──
    // The follow-up checkbox reveals its note input and flips the button label
    // — filing WITH follow-up keeps the WO OPEN (badged) instead of closing it.
    all('[data-mn-comp-fu]').forEach(el => el.addEventListener('change', () => {
      const id = (el as HTMLInputElement).dataset.mnCompFu!;
      const on = (el as HTMLInputElement).checked;
      const note = this.container.querySelector(`[data-mn-comp-funote="${id}"]`) as HTMLInputElement | null;
      if (note) note.style.display = on ? 'block' : 'none';
      const btn = this.container.querySelector(`[data-mn-comp-file="${id}"]`) as HTMLButtonElement | null;
      if (btn) btn.textContent = on ? '📋 File report — needs follow-up' : '✅ File report & mark done';
    }));
    all('[data-mn-comp-file]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnCompFile!;
      const ta = document.getElementById(`mn-comp-${id}`) as HTMLTextAreaElement | null;
      const what_done = (ta?.value || '').trim();
      if (!what_done) { ta?.focus(); return; }
      const fu = this.container.querySelector(`[data-mn-comp-fu="${id}"]`) as HTMLInputElement | null;
      const needs_followup = !!fu?.checked;
      const followup_note = ((this.container.querySelector(`[data-mn-comp-funote="${id}"]`) as HTMLInputElement | null)?.value || '').trim();
      const by = (() => { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || p.full_name || '').trim() || 'unknown'; } catch { return 'unknown'; } })();
      (el as HTMLButtonElement).disabled = true;
      // Follow-up flagged → the visit is documented but the WO STAYS OPEN so
      // it can't quietly die; clean finish → done.
      const j = await this.post(`/work-orders/${id}`, {
        completion: { what_done, needs_followup, followup_note },
        status: needs_followup ? 'open' : 'done', by,
      });
      (el as HTMLButtonElement).disabled = false;
      if (!j) return; // post() already alerted
      const wo = (this.workOrders || []).find(w => w.id === id);
      if (wo) {
        if (j.work_order) Object.assign(wo, j.work_order);
        else { wo.completion = { what_done, needs_followup, followup_note, at: new Date().toISOString(), by }; wo.status = needs_followup ? 'open' : 'done'; }
      }
      this.draw();
      // R116 #5: filing never waits for the evidence — say what is still on its way
      const still = pendingItems(`${API}/work-orders/${id}/photo`).length;
      if (still) this.toastNote(`Report filed — ${still} file${still === 1 ? ' is' : 's are'} still uploading and will land on this work order.`);
    }));

    // ── work orders: delete (office roles only — button is hidden for field techs) ──
    all('[data-mn-wodel]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnWodel!;
      if (!confirm('Delete this work order? This cannot be undone.')) return;
      if (await this.del(`/work-orders/${id}`)) {
        this.workOrders = (this.workOrders || []).filter(w => w.id !== id);
        this.expandedWo.delete(id);
        this.draw();
      }
    }));

    // ── work orders: labor timer (start / pause) + manual time entry ──
    all('[data-mn-timer]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const [id, action] = (el.dataset.mnTimer || '').split('|');
      if (await this.post(`/work-orders/${id}/timer`, { action })) {
        await this.reloadWos(); this.draw();
      }
    }));
    all('[data-mn-timer-manual]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnTimerManual!;
      const inp = this.container.querySelector(`[data-mn-tmin="${id}"]`) as HTMLInputElement | null;
      const minutes = Math.round(Number(inp?.value));
      if (!Number.isFinite(minutes) || minutes <= 0) { inp?.focus(); return; }
      if (await this.post(`/work-orders/${id}/timer`, { action: 'manual', minutes })) {
        await this.reloadWos(); this.draw();
      }
    }));

    // ── work orders: internal team note (R56 — never owner-facing) ──
    all('[data-mn-intnote-add]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnIntnoteAdd!;
      const input = this.container.querySelector(`[data-mn-intnote="${id}"]`) as HTMLInputElement | null;
      const text = (input?.value || '').trim();
      if (!text) return;
      (el as HTMLButtonElement).disabled = true;
      const by = (() => { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || p.full_name || '').trim() || 'unknown'; } catch { return 'unknown'; } })();
      const j = await this.post(`/work-orders/${encodeURIComponent(id)}`, { internal_note: { text, by } });
      (el as HTMLButtonElement).disabled = false;
      if (!j) return; // post() already alerted
      const wo = (this.workOrders || []).find(w => w.id === id);
      if (wo && j.work_order) wo.internal_notes = j.work_order.internal_notes;
      else if (wo) { wo.internal_notes = wo.internal_notes || []; wo.internal_notes.push({ at: new Date().toISOString(), by, text }); }
      // Patch ONLY the notes list — a full draw() would silently revert every
      // unsaved edit elsewhere on the card (the owner-facing summary sits
      // right above this section).
      const listEl = this.container.querySelector(`[data-mn-intnotes-list="${id}"]`);
      if (listEl && wo) listEl.innerHTML = this.intNotesListHtml(wo);
      if (input) input.value = '';
    }));

    // ── work orders: save (assignee + status + summary) ──
    // R100 A6: vendor flow
    all('[data-mn-wotext]').forEach(el => el.addEventListener('click', () => {
      const w = (this.workOrders || []).find((x: any) => x.id === (el as HTMLElement).dataset.mnWotext);
      if (w) void this.showTextVendorModal(w);
    }));
    all('[data-mn-wolink]').forEach(el => el.addEventListener('click', async () => {
      const w = (this.workOrders || []).find((x: any) => x.id === (el as HTMLElement).dataset.mnWolink);
      if (!w || !w.vendor_link) return;
      const url = `${location.origin}/v/${w.vendor_link.token}`;
      try { await navigator.clipboard.writeText(url); (el as HTMLElement).textContent = '✓ copied'; } catch { window.prompt('Vendor link:', url); }
    }));
    all('[data-mn-worevoke]').forEach(el => el.addEventListener('click', async () => {
      const id = (el as HTMLElement).dataset.mnWorevoke!;
      if (!confirm('Revoke the vendor link? Their page stops working immediately; texting the WO again mints a new link.')) return;
      const r = await fetch(`/api/assetops/vendor-flow/work-orders/${encodeURIComponent(id)}/link`, { method: 'DELETE', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ by: MaintenancePage.actorName() }) }).catch(() => null);
      const j = r ? await r.json().catch(() => ({} as any)) : {};
      if (!r || !r.ok) { alert(`Couldn't revoke — ${j.error || 'try again'}`); return; }
      await this.loadAll(); this.draw();
    }));
    all('[data-mn-invoice]').forEach(el => el.addEventListener('click', async () => {
      const [woId, iid, action] = ((el as HTMLElement).dataset.mnInvoice || '').split('|');
      const noteTxt = action === 'reject' ? (window.prompt('Why is this invoice rejected? (optional)') || '') : '';
      const j = await this.post(`/vendor-flow/work-orders/${encodeURIComponent(woId)}/invoices/${encodeURIComponent(iid)}`, { action, note: noteTxt, by: MaintenancePage.actorName() }).catch(() => null);
      if (j && j.ok) { await this.loadAll(); this.draw(); }
    }));
    all('[data-mn-quote]').forEach(el => el.addEventListener('click', async () => {
      const [woId, qid, action] = ((el as HTMLElement).dataset.mnQuote || '').split('|');
      const noteTxt = action === 'decline' ? (window.prompt('Reason for declining (optional):') || '') : '';
      const j = await this.post(`/vendor-flow/work-orders/${encodeURIComponent(woId)}/quotes/${encodeURIComponent(qid)}`, { action, note: noteTxt, by: MaintenancePage.actorName() }).catch(() => null);
      if (j && j.ok) { await this.loadAll(); this.draw(); }
    }));
    // R110 #2: the Who pays / Billed yet? explainer re-reads live, so a WCH-pays WO
    // that's been marked billed shows the clash before Save, not after a redraw.
    // R115 maint: the shared WoEditor binds the explainer, plus the access-mode
    // select (enabled the moment 🚪 is ticked — it used to need a Save first)
    // and the due date (disabled while 🔁 follows the next checkout).
    all('[data-mn-wosave]').forEach(el => bindWoEditForm(this.container, el.dataset.mnWosave!));
    all('[data-mn-wosave]').forEach(el => el.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = el.dataset.mnWosave!;
      const wo = (this.workOrders || []).find(w => w.id === id);
      if (!wo) return;
      // R115 maint: ONE diff for the board and the calendar's WO inspector
      // (lib/woEdit.mjs via the shared WoEditor) — only what changed is sent,
      // so an untouched Save no longer writes an "assigned → X; summary
      // updated" log line.
      const body = collectWoEdit(this.container, wo, { money: true });
      if (!body) { const b = el as HTMLButtonElement; const t = b.textContent; b.textContent = 'No changes'; setTimeout(() => { if (b.isConnected) b.textContent = t; }, 1400); return; }
      if (await this.post(`/work-orders/${id}`, body)) {
        // vendor/cost changes alter the Vendors + Costs rollups — invalidate caches.
        this.vendorWos = {};
        this.costs = null;
        this.calendar = null;   // review-2: a card save can move a booking / due date — the windows view + day plan follow
        if (this.dayPlanOpen) void this.loadDayPlan(); else this.dayPlan = null;
        await Promise.all([this.reloadWos(), this.reloadBoard(), this.reloadVendors()]);
        this.draw();
      }
    }));

    // ── PM templates: expand ──
    all('[data-mn-tpltoggle]').forEach(el => el.addEventListener('click', () => {
      const id = el.dataset.mnTpltoggle!;
      if (this.expandedTpl.has(id)) this.expandedTpl.delete(id); else this.expandedTpl.add(id);
      this.draw();
    }));

    // ── PM templates: new ──
    all('[data-mn-newtpl]').forEach(el => el.addEventListener('click', async () => {
      const name = prompt('New template name:', 'New PM Template');
      if (!name) return;
      const j = await this.post('/pm-templates', { name, cadence_days: this.cadenceDefault, items: [] });
      if (j) {
        const id = j.template?.id || j.templates?.find?.((t: any) => t.name === name)?.id;
        if (id) this.expandedTpl.add(id);
        await this.reloadTemplates(); this.draw();
      }
    }));

    // ── PM templates: add item (local DOM, persisted on Save) ──
    all('[data-mn-tpl-additem]').forEach(el => el.addEventListener('click', () => {
      const tid = el.dataset.mnTplAdditem!;
      const wrap = this.container.querySelector(`[data-mn-tpl-items="${tid}"]`);
      if (!wrap) return;
      const idx = wrap.querySelectorAll('[data-mn-tpl-itemrow]').length;
      const row = document.createElement('div');
      row.className = 'mn-tpl-item';
      row.setAttribute('data-mn-tpl-itemrow', `${tid}|${idx}`);
      row.innerHTML = `
        <input type="text" data-mn-tpl-itemtitle value="" placeholder="Item title"/>
        <textarea data-mn-tpl-iteminstr placeholder="Instructions for the technician"></textarea>
        <button class="mn-btn danger sm" data-mn-tpl-itemdel="${tid}|${idx}" title="Remove item">✕</button>`;
      wrap.appendChild(row);
      (row.querySelector('[data-mn-tpl-itemdel]') as HTMLElement)?.addEventListener('click', () => row.remove());
    }));

    // ── PM templates: remove item (local) ──
    all('[data-mn-tpl-itemdel]').forEach(el => el.addEventListener('click', () => {
      el.closest('.mn-tpl-item')?.remove();
    }));

    // ── PM templates: save ──
    all('[data-mn-tpl-save]').forEach(el => el.addEventListener('click', async () => {
      const tid = el.dataset.mnTplSave!;
      const name = (this.container.querySelector(`[data-mn-tpl-name="${tid}"]`) as HTMLInputElement)?.value.trim() || 'PM Template';
      const cadence = Number((this.container.querySelector(`[data-mn-tpl-cadence="${tid}"]`) as HTMLInputElement)?.value) || 90;
      const wrap = this.container.querySelector(`[data-mn-tpl-items="${tid}"]`);
      const existing = (this.templates || []).find(t => t.id === tid);
      const items: any[] = [];
      wrap?.querySelectorAll<HTMLElement>('[data-mn-tpl-itemrow]').forEach((row, i) => {
        const title = (row.querySelector('[data-mn-tpl-itemtitle]') as HTMLInputElement)?.value.trim() || '';
        const instructions = (row.querySelector('[data-mn-tpl-iteminstr]') as HTMLTextAreaElement)?.value.trim() || '';
        if (!title) return;
        // preserve original key where the row maps to an existing item, else synthesize one.
        const origKey = existing?.items?.[i]?.key;
        const key = origKey || title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 32) || `item_${i}`;
        items.push({ key, title, instructions });
      });
      const j = await this.post('/pm-templates', { id: tid, name, cadence_days: cadence, items });
      if (j) {
        await Promise.all([this.reloadTemplates(), this.reloadBoard()]);
        this.draw();
      }
    }));

    // ── PM templates: delete ──
    all('[data-mn-tpl-del]').forEach(el => el.addEventListener('click', async () => {
      const tid = el.dataset.mnTplDel!;
      if (!confirm('Delete this PM template? Work orders already created from it keep their copied items.')) return;
      if (await this.del(`/pm-templates/${tid}`)) {
        this.expandedTpl.delete(tid);
        await Promise.all([this.reloadTemplates(), this.reloadBoard()]);
        this.draw();
      }
    }));

    // ── assets: property picker ──
    all('[data-mn-astfilter]').forEach(el => el.addEventListener('change', () => {
      this.assetListingFilter = (el as unknown as HTMLSelectElement).value;
      this.showAddAsset = false;
      this.editingAsset = null;
      this.draw();
    }));

    // ── assets: seed from PDS ──
    all('[data-mn-ast-seed]').forEach(el => el.addEventListener('click', async () => {
      const lid = el.dataset.mnAstSeed!;
      const j = await this.post(`/assets/seed/${lid}`, {});
      if (j) {
        alert(`Seeded ${j.created} asset(s) from property details${j.skipped ? `, skipped ${j.skipped} already on file` : ''}.`);
        await this.reloadAssets();
        this.draw();
      }
    }));

    // ── assets: toggle add form ──
    all('[data-mn-ast-add]').forEach(el => el.addEventListener('click', () => {
      this.showAddAsset = !this.showAddAsset;
      this.draw();
    }));

    // ── assets: expand row (loads service history on first open) ──
    all('[data-mn-asttoggle]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnAsttoggle!;
      if (this.expandedAsset.has(id)) {
        this.expandedAsset.delete(id);
        this.editingAsset = null;
        this.draw();
      } else {
        this.expandedAsset.add(id);
        this.draw();
        if (!this.assetHistory[id]) {
          const j = await this.get(`/assets/${id}`);
          if (j) { this.assetHistory[id] = { history: j.history || [], open_count: j.open_count || 0 }; this.draw(); }
        }
      }
    }));

    // ── assets: edit / cancel-edit ──
    all('[data-mn-ast-edit]').forEach(el => el.addEventListener('click', () => {
      this.editingAsset = el.dataset.mnAstEdit!;
      this.draw();
    }));
    all('[data-mn-ast-cancel]').forEach(el => el.addEventListener('click', () => {
      const v = el.dataset.mnAstCancel!;
      if (v === 'new') this.showAddAsset = false; else this.editingAsset = null;
      this.draw();
    }));

    // ── assets: save (create or update) ──
    all('[data-mn-ast-save]').forEach(el => el.addEventListener('click', async () => {
      const v = el.dataset.mnAstSave!;
      const form = this.container.querySelector(`[data-mn-ast-formfor="${v}"]`);
      if (!form) return;
      const field = (name: string) => (form.querySelector(`[data-mn-af="${name}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
      const name = field('name').trim();
      if (!name) { alert('Give the asset a name.'); return; }
      const body: any = {
        listing_id: this.assetListingFilter,
        name,
        category: field('category'),
        make: field('make').trim(),
        model: field('model').trim(),
        serial: field('serial').trim(),
        location_note: field('location_note').trim(),
        warranty_note: field('warranty_note').trim(),
        photo_url: field('photo_url').trim(),
      };
      if (v !== 'new') body.id = v;
      const j = await this.post('/assets', body);
      if (j) {
        if (v === 'new') this.showAddAsset = false; else this.editingAsset = null;
        await this.reloadAssets();
        this.draw();
      }
    }));

    // ── assets: delete (soft) ──
    all('[data-mn-ast-del]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnAstDel!;
      if (!confirm('Archive this asset? Work orders linked to it keep their history.')) return;
      if (await this.del(`/assets/${id}`)) {
        this.expandedAsset.delete(id);
        this.editingAsset = null;
        await this.reloadAssets();
        this.draw();
      }
    }));

    // ── calendar: horizon toggle (reloads calendar + pm-due at new horizon) ──
    all('[data-mn-cal-days]').forEach(el => el.addEventListener('click', async () => {
      this.calDays = Number(el.dataset.mnCalDays) || 30;
      this.calendar = null;
      this.pmDue = null;
      await this.reloadCalendar();
      this.draw();
    }));

    // ── PM plans: autogen flag — auto-create Re-check WOs from missed checks ──
    all('[data-mn-autogen-recheck]').forEach(el => el.addEventListener('change', async () => {
      const box = el as HTMLInputElement;
      const on = box.checked;
      box.disabled = true;
      const by = (() => { try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || p.full_name || '').trim() || 'unknown'; } catch { return 'unknown'; } })();
      const j = await this.post('/autogen-flags', { recheck_wos: on, by });
      if (j && j.flags) this.autogenFlags = j.flags;
      else box.checked = !on; // post() already alerted — revert the box
      box.disabled = false;
      this.draw();
    }));

    // ── PM plans: property filter ──
    all('[data-mn-pmfilter]').forEach(el => el.addEventListener('change', () => {
      this.pmListingFilter = (el as unknown as HTMLSelectElement).value;
      this.draw();
    }));

    // ── PM plans: toggle add form ──
    all('[data-mn-pm-add]').forEach(el => el.addEventListener('click', () => {
      this.showAddPm = !this.showAddPm;
      this.editingPm = null;
      this.draw();
    }));

    // ── PM plans: edit / cancel ──
    all('[data-mn-pm-edit]').forEach(el => el.addEventListener('click', () => {
      this.editingPm = el.dataset.mnPmEdit!;
      this.showAddPm = false;
      this.draw();
    }));
    all('[data-mn-pm-cancel]').forEach(el => el.addEventListener('click', () => {
      const v = el.dataset.mnPmCancel!;
      if (v === 'new') this.showAddPm = false; else this.editingPm = null;
      this.draw();
    }));

    // ── PM plans: trigger-kind toggles which trigger fields show (live) ──
    all('[data-mn-pm-formfor]').forEach(form => {
      const kindSel = form.querySelector('[data-mn-pf="kind"]') as HTMLSelectElement | null;
      const listingSel = form.querySelector('[data-mn-pf="listing_id"]') as HTMLSelectElement | null;
      const unitWrap = form.querySelector('[data-mn-pf-unitwrap]') as HTMLElement | null;
      const seasonWrap = form.querySelector('[data-mn-pf-seasonwrap]') as HTMLElement | null;
      const unitSel = form.querySelector('[data-mn-pf="unit"]') as HTMLSelectElement | null;
      const assetSel = form.querySelector('[data-mn-pf="asset_id"]') as HTMLSelectElement | null;
      const syncTrigger = () => {
        const kind = kindSel?.value || 'interval';
        if (unitWrap) unitWrap.style.display = kind === 'season' ? 'none' : '';
        if (seasonWrap) seasonWrap.style.display = kind === 'season' ? '' : 'none';
        // usage forces the unit to nights; interval limits to days/months.
        if (unitSel) {
          if (kind === 'usage') unitSel.value = 'nights';
          else if (kind === 'interval' && unitSel.value === 'nights') unitSel.value = 'days';
        }
      };
      kindSel?.addEventListener('change', syncTrigger);
      syncTrigger();
      // Repopulate the asset list when the property changes (new-plan form only).
      listingSel?.addEventListener('change', () => {
        if (!assetSel) return;
        const mine = (this.assets || []).filter(a => String(a.listing_id) === String(listingSel.value));
        assetSel.innerHTML = `<option value="">— none —</option>` +
          mine.map(a => `<option value="${this.esc(a.id)}">${this.esc(a.name)}</option>`).join('');
      });
    });

    // ── PM plans: save (create or update) ──
    all('[data-mn-pm-save]').forEach(el => el.addEventListener('click', async () => {
      const v = el.dataset.mnPmSave!;
      const form = this.container.querySelector(`[data-mn-pm-formfor="${v}"]`);
      if (!form) return;
      const field = (name: string) => (form.querySelector(`[data-mn-pf="${name}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
      const name = field('name').trim();
      if (!name) { alert('Give the plan a name.'); return; }
      const listing_id = v === 'new' ? field('listing_id') : (this.pmPlans || []).find(p => p.id === v)?.listing_id;
      if (!listing_id) { alert('Pick a property.'); return; }
      const kind = field('kind') || 'interval';
      const trigger: any = { kind, every: Number(field('every')) || 1 };
      if (kind === 'season') trigger.season_month = Number(field('season_month')) || 1;
      else trigger.unit = kind === 'usage' ? 'nights' : (field('unit') || 'days');
      const body: any = {
        listing_id,
        name,
        asset_id: field('asset_id') || null,
        trigger,
        default_vendor_id: field('default_vendor_id').trim() || null,
      };
      if (v !== 'new') body.id = v;
      const j = await this.post('/pm-plans', body);
      if (j) {
        if (v === 'new') this.showAddPm = false; else this.editingPm = null;
        await Promise.all([this.reloadPmPlans(), this.reloadPmDue()]);
        this.draw();
      }
    }));

    // ── PM plans: delete (soft) ──
    all('[data-mn-pm-del]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnPmDel!;
      if (!confirm('Delete this PM plan? Work orders already generated from it keep their history.')) return;
      if (await this.del(`/pm-plans/${id}`)) {
        this.editingPm = null;
        await Promise.all([this.reloadPmPlans(), this.reloadPmDue()]);
        this.draw();
      }
    }));

    // ── PM plans: generate a WO into the next vacancy window ──
    all('[data-mn-pm-gen]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnPmGen!;
      const j = await this.post(`/pm/${id}/generate`, {});
      if (j) {
        const sw = j.scheduled_window || {};
        const win = sw.start ? `${this.fmtDate(sw.start)}${sw.end ? ` → ${this.fmtDate(sw.end)}` : ' (open-ended)'}` : 'an open window';
        alert(`${j.existing ? 'A work order already exists for this plan' : 'Work order created'} — scheduled into ${win}. Find it on the Work orders tab.`);
        // Refresh WOs, calendar (so the scheduled WO/at-risk flag shows) and due list.
        this.calendar = null;
        await Promise.all([this.reloadWos(), this.reloadPmDue()]);
        this.draw();
      }
    }));

    // ── vendors: filters ──
    all('[data-mn-venfilter]').forEach(el => el.addEventListener('change', () => {
      const which = el.dataset.mnVenfilter;
      if (which === 'trade') this.vendorTradeFilter = (el as unknown as HTMLSelectElement).value;
      else this.vendorOnCallOnly = (el as unknown as HTMLInputElement).checked;
      this.draw();
    }));

    // ── vendors: seed from PDS ──
    all('[data-mn-ven-seed]').forEach(el => el.addEventListener('click', async () => {
      if (!confirm('Pull pest/lawn/pool/trash (and any other) vendor contacts from all properties\' details into the roster? Existing vendors (by name) are left untouched.')) return;
      const j = await this.post('/vendors/seed', {});
      if (j) {
        alert(`Seeded ${j.created} vendor(s) from property details${j.skipped ? `, skipped ${j.skipped} already on the roster` : ''}.`);
        await this.reloadVendors();
        this.draw();
      }
    }));

    // ── vendors: toggle add form ──
    all('[data-mn-ven-add]').forEach(el => el.addEventListener('click', () => {
      this.showAddVendor = !this.showAddVendor;
      this.editingVendor = null;
      this.draw();
    }));

    // ── vendors: expand row (loads its work orders on first open) ──
    all('[data-mn-ventoggle]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnVentoggle!;
      if (this.expandedVendor.has(id)) {
        this.expandedVendor.delete(id);
        this.editingVendor = null;
        this.draw();
      } else {
        this.expandedVendor.add(id);
        this.draw();
        if (!this.vendorWos[id]) {
          const j = await this.get(`/vendors/${id}`);
          if (j) { this.vendorWos[id] = j.work_orders || []; this.draw(); }
        }
      }
    }));

    // ── vendors: edit / cancel-edit ──
    all('[data-mn-ven-edit]').forEach(el => el.addEventListener('click', () => {
      this.editingVendor = el.dataset.mnVenEdit!;
      this.draw();
    }));
    all('[data-mn-ven-cancel]').forEach(el => el.addEventListener('click', () => {
      const v = el.dataset.mnVenCancel!;
      if (v === 'new') this.showAddVendor = false; else this.editingVendor = null;
      this.draw();
    }));

    // ── vendors: save (create or update) ──
    all('[data-mn-ven-save]').forEach(el => el.addEventListener('click', async () => {
      const v = el.dataset.mnVenSave!;
      const form = this.container.querySelector(`[data-mn-ven-formfor="${v}"]`);
      if (!form) return;
      const field = (name: string) => (form.querySelector(`[data-mn-vf="${name}"]`) as HTMLInputElement | null)?.value ?? '';
      const name = field('name').trim();
      if (!name) { alert('Give the vendor a name.'); return; }
      const onCall = (form.querySelector('[data-mn-vf="on_call"]') as HTMLInputElement | null)?.checked || false;
      const body: any = {
        name,
        trades: field('trades'),
        regions: field('regions'),
        phone: field('phone').trim(),
        email: field('email').trim(),
        on_call: onCall,
        rate_note: field('rate_note').trim(),
        notes: field('notes').trim(),
      };
      const rateRaw = field('hourly_rate').trim();
      body.hourly_rate = rateRaw === '' ? null : Number(rateRaw);
      if (v !== 'new') body.id = v;
      const j = await this.post('/vendors', body);
      if (j) {
        if (v === 'new') this.showAddVendor = false; else this.editingVendor = null;
        this.vendorWos = {};
        await this.reloadVendors();
        this.draw();
      }
    }));

    // ── vendors: delete (soft) ──
    all('[data-mn-ven-del]').forEach(el => el.addEventListener('click', async () => {
      const id = el.dataset.mnVenDel!;
      if (!confirm('Archive this vendor? Work orders linked to it keep their vendor_id and cost history.')) return;
      if (await this.del(`/vendors/${id}`)) {
        this.expandedVendor.delete(id);
        this.editingVendor = null;
        delete this.vendorWos[id];
        await this.reloadVendors();
        this.draw();
      }
    }));

    // ── costs: window toggle ──
    all('[data-mn-cost-days]').forEach(el => el.addEventListener('click', async () => {
      this.costDays = Number(el.dataset.mnCostDays) || 365;
      this.costs = null;
      this.draw();
      await this.reloadCosts();
      this.draw();
    }));

    // ── costs: property filter ──
    all('[data-mn-costfilter]').forEach(el => el.addEventListener('change', async () => {
      this.costListingFilter = (el as unknown as HTMLSelectElement).value;
      this.costs = null;
      this.draw();
      await this.reloadCosts();
      this.draw();
    }));
  }
}
